import type { Plural } from '@hk/core'
import type { AppLocale } from '@hk/ui'
import type { BoardFilterKey } from '../screens/boardFilters'
import type { RoomVisualTone } from '@hk/ui'

/* Screen strings. Small enough to live in one file per screen family today; the shape
   is what matters — every visible string has an Arabic sibling declared beside it, so
   an untranslated string is a missing property and not a silent English leak. */

type Dict = {
  boardTitle: string
  boardSubtitle: string
  legendTitle: string
  refresh: string
  searchLabel: string
  searchPlaceholder: string
  allFloors: string
  building: string
  teamsTitle: string
  teamsSubtitle: string
  teamsPairs: string
  teamsFloors: string
  teamsUnpaired: string
  teamsNoPairs: string
  teamsNoPairsBody: string
  teamsMakePair: string
  teamsPairThem: string
  teamsDisband: string
  teamsCover: string
  teamsCoverFor: string
  teamsCovering: string
  teamsClearCover: string
  teamsNobody: string
  teamsGiveFloor: string
  teamsRoomsMoved: string
  teamsHolding: string
  teamsOffToday: string
  teamsOnShift: string
  teamsNoFloor: string
  teamsChoose: string
  allBuildings: string
  floor: string
  emptyTitle: string
  emptyBody: string
  noMatchTitle: string
  noMatchBody: string
  retry: string
  updatedAt: string
  rooms: string
  filters: Record<BoardFilterKey, string>
  tones: Record<RoomVisualTone, string>
  legendOccupancy: string
  legendNotSellable: string

  /* ── SPRINT 35: THE OVERRIDE (T8) ─────────────────────────────────────────── */
  ovrTitle: string
  ovrIntro: string
  ovrCurrent: string
  ovrReasonLabel: string
  ovrReasonHint: string
  ovrConfirm: string
  ovrDone: string
  ovrOnlyDirty: string

  /* ── CQ-138: OFF SALE, AND BACK ────────────────────────────────────────────
     Its own words and not the override's. The two acts share a dialog and nothing else:
     one says a room was cleaned that nobody cleaned, the other says the hotel may not
     sell a room that is perfectly clean. */
  svcOffTitle: string
  svcOnTitle: string
  svcOffIntro: string
  svcOnIntro: string
  /* **DEFECT #152 — TWO BOXES LABELLED "Why" IN ONE DIALOG IS "Why" NOWHERE.** The first
     draft borrowed the override's word, and the dialog then held two fields with the same
     accessible name: a screen reader announced "Why" twice and a sighted supervisor had
     the block headings to tell them apart while nobody else did. Caught by a test that
     could no longer find the field it meant. Each label now names its own act. */
  svcOffReasonLabel: string
  svcOnReasonLabel: string
  svcOffReasonHint: string
  svcOnReasonHint: string
  svcTakeOff: string
  svcPutBack: string
  svcOffNow: string
  svcOutOfOrderNote: string

  /* ── SPRINT 37: WHAT HAPPENED TO THIS ROOM ────────────────────────────────── */
  histTitle: string
  histEmpty: string
  histLoading: string
  histBy: string
  histByNobody: string
  histTruncated: string
  histCapturedEarlier: string
  /** One per transition, and the dictionary guard asks for both languages. */
  roomEvents: Record<
    'STARTED' | 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'SOILED'
    | 'SOILED_BEFORE_INSPECTION' | 'CANCELLED' | 'OVERRIDDEN' | 'TOUCH_UP', string>
  issuesTitle: string

  /* ── W-61 / M-15, needs you (Sprint 47) ───────────────────────────────────── */
  escalationsTitle: string
  /** DEFECT #153. W-61 had no subtitle because it had no header branch at all. */
  escalationsSubtitle: string
  needsYou: string
  nothingNeedsYou: string
  nothingNeedsYouBody: string
  thingsNeedYou: Record<Plural, string>
  minutesOver: string
  minutesAgo: string
  now: string
  today: string
  roomAndArrival: string
  roomItemAndPerson: string
  ofPar: string
  neverAccepted: Record<Plural, string>
  failuresThisWeek: Record<Plural, string>
  daysOfCover: Record<Plural, string>

  /* ── W-75 · WHAT CAN BE WRONG WITH A ROOM (Sprint 63) ──────────────────────
     Sprint 56's card with two columns missing. A fault is triaged rather than promised,
     and reporting one takes nothing off a shelf. */
  fcTitle: string
  fcIntro: string
  fcColRaised: string
  fcNeverRaised: string
  fcRaised: Record<Plural, string>
  /** Retiring does not close what is open — the row says so. */
  fcStillOpen: Record<Plural, string>
  fcKeepHint: string
  fcAdd: string
  fcAddTitle: string
  fcAddIntro: string
  fcAddSave: string

  /* ── W-22 · THE ROOM LIST (Sprint 63) ──────────────────────────────────────
     A second VIEW of W-20 rather than a second screen — Change Note 08. */
  rlView: string
  rlAsGrid: string
  rlAsList: string
  rlSortBy: string
  rlRoom: string
  rlType: string
  rlFloor: string
  rlCondition: string
  rlOccupancy: string
  rlService: string
  rlAssigned: string
  rlDeparture: string
  rlOcc: Record<'VACANT' | 'OCCUPIED' | 'ARRIVING' | 'DEPARTING', string>
  rlSvc: Record<'IN_SERVICE' | 'OUT_OF_SERVICE' | 'OUT_OF_ORDER', string>

  /* ── W-21 · THE ROOM (Sprint 62) ───────────────────────────────────────────
     The desk's room card. The same data as M-03 and a different screen: the handset is
     read standing in a corridor before a door is opened, and this is read because
     somebody asked about a room. Ordered by the question, not by what to carry. */
  rdBack: string
  rdFloor: string
  rdVip: string
  rdGuest: string
  rdGuestInResidence: string
  rdGuestLeaving: string
  rdNoArrivalToday: string
  /** **NOT "no arrival today".** The property system has not said, which is different. */
  rdArrivalUnknown: string
  rdArrivingAt: string
  rdAdults: Record<Plural, string>
  rdPeopleIn: Record<Plural, string>
  rdSleeps: string
  rdOverCapacity: string
  rdLeavingAt: string
  rdWork: string
  rdNothingDue: string
  rdNothingDueBody: string
  rdCredits: string
  rdEstimated: string
  rdStartedAt: string
  rdStates: Record<
    'CREATED' | 'ASSIGNED' | 'STARTED' | 'SUBMITTED' | 'REWORK' | 'COMPLETED'
    | 'CANCELLED', string>
  /** Two different promises — see the screen. */
  rdWillFollow: string
  rdWouldFollow: string
  rdNeeds: string
  rdAboutRoom: string
  rdLastTime: string
  rdCleanedOn: string
  rdByPerson: string
  rdPassedFirstTime: string
  rdWasRejected: string
  /** **Null is a real answer**, and it is not the same as failing. */
  rdNotInspected: string
  rdActs: string

  /* ── W-71 · ONE FAULT, IN FULL (Sprint 64) ────────────────────────────────
     Reached from a card on the service board. The card answers "what is wrong"; this
     answers "why is the room still off sale, and who has touched it". */
  fdBack: string
  fdTitle: string
  fdReportedBy: string
  fdCapturedEarlier: string
  fdOpenFor: string
  fdWasOpenFor: string
  fdAcknowledgedBy: string
  fdClosedBy: string
  fdNobodyYet: string
  /** The issue's own lifecycle words. The board shows severity; this shows state. */
  fdStates: Record<'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED' | 'CANCELLED', string>
  fdPlace: string
  fdRoomOnFloor: string
  fdPublicArea: string
  /** **HISTORY**: this fault is the one that shut the place. */
  fdTookPlaceOut: string
  /** **THE PRESENT**, deliberately a different sentence from the one above. */
  fdPlaceStillShut: string
  fdPlaceBackInUse: string
  fdShutBySomethingElse: string
  fdOtherFaults: Record<Plural, string>
  fdOtherBlocking: Record<Plural, string>
  fdNothingElseWrong: string
  fdWhatWasSaid: string
  fdNoDescription: string
  fdHistory: string
  fdNoHistory: string
  fdRaised: string
  fdByNobody: string
  fdActs: string
  fdAcknowledge: string
  fdCancel: string
  fdCancelReasonBody: string
  fdAlreadyClosed: string

  /* ── W-50 + W-51 · INSPECTION AT THE DESK (Sprint 65) ──────────────────────
     The handset has a button and the desk needs a list. `iq` covers both: the queue and
     the room, because they are one route and share half their vocabulary. */
  iqTitle: string
  /** The sidebar's shorter form — the full title truncates in the rail. */
  iqNav: string
  iqSubtitle: string
  iqBack: string
  iqEmpty: string
  iqAllClear: string
  iqHeadline: Record<Plural, string>
  iqBlockedForYou: Record<Plural, string>
  iqOnFloor: string
  iqExceptions: Record<Plural, string>
  /** Why this caller cannot inspect a row. The server's three words. */
  iqCannot: Record<'NOT_HANDED_IN' | 'NOT_ASSIGNED' | 'OWN_WORK', string>
  iqHandedInBy: string
  iqFigures: string
  iqWaiting: string
  iqTaken: string
  /** **Null is not zero** — a room recovered from an offline handset. */
  iqNotRecorded: string
  iqAgainst: string
  iqTheList: string
  iqAnsweredOf: string
  iqNoChecklist: string
  iqHeDidIt: string
  iqHeCouldNot: string
  iqNotAnswered: string
  iqFail: string
  /** The sentence the whole screen is for. */
  iqPriorFailures: Record<Plural, string>
  iqVerdict: string
  iqMarked: Record<Plural, string>
  iqWhyBack: string
  iqSendBack: string
  iqPass: string
  iqPassed: string
  iqSentBack: string

  /* ── W-108 · WHAT TO SEND UP THIS MORNING (Sprint 67) ──────────────────────
     A picking list, not a balances table — and it proposes rather than acts. */
  rpTitle: string
  rpSubtitle: string
  rpNothingShort: string
  rpOnFloor: string
  rpHas: string
  rpInStore: string
  rpSendCol: string
  rpSend: string
  rpStoreShort: string
  rpNeedsOtherRole: string
  rpLines: Record<Plural, string>
  rpSent: Record<Plural, string>
  /** The purchase order, above the picking list. A different problem for a different person. */
  rpShortTitle: string
  rpShortBody: string
  rpShortLine: string

  /* ── W-103 · WHAT IS AT THE LAUNDRY (Sprint 68) ────────────────────────────
     The balance is not the question: sixty towels that went down this morning and sixty
     that went down on Saturday are the same number and opposite situations. */
  lnTitle: string
  lnSubtitle: string
  lnNoLaundry: string
  lnNothingThere: string
  lnThere: string
  lnOut: string
  lnSent: string
  lnBack: string
  lnHours: string
  lnLate: string
  lnAllOnTime: string
  lnOverdue: Record<Plural, string>
  lnNote: string

  /* ── W-109 · THE PLACES A HOTEL KEEPS ITS LINEN (Sprint 69) ────────────────
     Every screen in this area was built on places only the demonstration seed could
     create. Retired once the ledger has touched them, never removed. */
  plTitle: string
  plSubtitle: string
  plAdd: string
  plName: string
  plNameAr: string
  plKind: string
  plKinds: Record<'STORE' | 'PANTRY' | 'LAUNDRY' | 'TROLLEY', string>
  plHolding: string
  plPars: string
  plOnFloor: string
  plRetired: string
  plRetire: string
  plRestore: string
  plRemove: string
  plInLedger: string
  plCodeWillBe: string
  plNote: string

  /* W-96 — the things a hotel counts. Sprint 80. */
  itTitle: string
  itSubtitle: string
  itAdd: string
  itName: string
  itNameAr: string
  itKind: string
  itKinds: Record<'CIRCULATING' | 'CONSUMABLE', string>
  itKindSettled: string
  itKindWarning: string
  itUnit: string
  itUnits: Record<'piece' | 'pair' | 'set', string>
  itOwned: string
  itInStandards: string
  itRetired: string
  itRetire: string
  itRestore: string
  itRemove: string
  itInLedger: string
  itCodeWillBe: string
  itNote: string

  /* W-101 — how it got to four. Sprint 80. */
  lgTitle: string
  lgSubtitle: string
  lgItem: string
  lgAllItems: string
  lgPlace: string
  lgAnywhere: string
  lgEitherDirection: string
  lgWhen: string
  lgWhat: string
  lgHowMany: string
  lgWhereFrom: string
  lgWhereTo: string
  lgWho: string
  lgToRoom: string
  lgFromACount: string
  lgKinds: Record<'RECEIPT' | 'TRANSFER' | 'ISSUE' | 'RETURN' | 'LOSS' | 'ADJUSTMENT', string>
  lgShowing: string
  lgNewer: string
  lgOlder: string
  lgNothing: string
  lgNothingBody: string
  lgNote: string

  /* W-129's last card — when this hotel works. Sprint 80. */
  shTitle: string
  shIntro: string
  shName: string
  shNameAr: string
  shStarts: string
  shEnds: string
  shBudget: string
  shWas: string
  shAdd: string
  shRetire: string
  shRestore: string
  shRetired: string
  shRostered: string
  shClockShape: string

  /* W-31 — one piece of work, and everything that happened to it. Sprint 80. */
  close: string
  rdOpenTask: string
  tdTitle: string
  tdWhat: string
  tdKind: string
  tdState: string
  tdStates: Record<string, string>
  tdWorth: string
  tdCredits: string
  tdEstimate: string
  tdRaisedBy: string
  tdBySweep: string
  tdByAPerson: string
  tdWhen: string
  tdStarted: string
  tdSubmitted: string
  tdCompleted: string
  tdNotYet: string
  tdFirstStartNote: string
  tdWhoHeld: string
  tdNobodyHeld: string
  tdStillHolding: string
  tdGivenBy: string
  tdChecklist: string
  tdAnswers: Record<string, string>
  tdUnanswered: string
  tdLinen: string
  tdItem: string
  tdStandard: string
  tdUsed: string

  /* ── W-131 + W-133 · WHAT THIS SERVER CAN DO, AND WHAT THE LOG IS ABOUT TO FORGET
        (Sprint 70) ───────────────────────────────────────────────────────────────
     **THREE STATES AND NOT TWO.** Working, not working, and *working but not in a way
     anybody should go live on*. A preview identity provider and an unbacked-up disk are
     both the third thing, and a two-state screen would have to draw them green.

     `sysNotes` is held against `app/contracts/system-health.json` — the server sends a
     CODE and this holds the sentence, because a sentence sent as data is a sentence the
     Arabic dictionary guard cannot see (#74, #82, #83, #102). */
  sysTitle: string
  sysSubtitle: string
  sysAllWell: string
  sysNotReady: string
  sysChecksTitle: string
  sysChecks: Record<string, string>
  sysStates: Record<'OK' | 'PREVIEW' | 'MISSING', string>
  sysNotes: Record<string, string>
  sysBlockedTitle: string
  sysBlockedBody: string
  sysRetentionTitle: string
  sysRetentionBody: string
  sysOldest: string
  sysLogEmpty: string
  sysHeld: string
  sysExpiring: string
  sysRetentionNote: string
  sysAboutTitle: string
  sysEnvironment: string
  sysVersion: string
  sysServerTime: string
  sysHotelZone: string
  sysHotelDate: string

  /* ── W-134 · WHAT IS SIGNED IN, AND FROM WHAT KIND OF THING (Sprint 71) ──────
     **THE SCREEN IS CALLED "DEVICES" AND THERE IS NO SUCH THING IN THIS PRODUCT.** The
     limitation is printed on its face, in both languages, because the decision somebody
     makes in front of a register is one this evidence cannot support. */
  dvTitle: string
  dvSubtitle: string
  dvWhatThisIs: string
  dvNoDeviceIdentity: string
  dvWhatItIsFor: string
  /* **COUNTED SENTENCES, ALL FIVE FORMS.** Every one of these draws at one and drew
     "1 have not been used" and "1 sign-ins" in the first render — English needs two forms
     and Arabic needs five, and a screen whose whole job is to be believed cannot afford a
     sentence that disagrees with its own number. */
  dvHeadlineSessions: Record<Plural, string>
  dvHeadlineAcross: Record<Plural, string>
  dvStale: Record<Plural, string>
  dvGroupings: Record<Plural, string>
  dvSignedIn: string
  dvManySessions: Record<Plural, string>
  dvSince: string
  /** **DAYS, NOT HOURS.** "216h 27m ago" is true and useless — the flag on this screen is
   *  "not used for over a day", and nobody converts 216 hours in their head. */
  dvDaysAgo: Record<Plural, string>
  dvUnknownNote: Record<Plural, string>
  dvEnd: string
  dvEndAll: string
  dvNobody: string
  dvNobodyBody: string

  /* ── W-42 · THE SYSTEM PROPOSES AND THE SUPERVISOR APPROVES (Sprint 73) ──────
     The client's answer, in their own words. Every sentence here is written so that a
     supervisor reading it knows NOTHING HAS MOVED — which is the one thing a plan on a
     board that usually shows reality has to establish before anything else. */
  propButton: string
  propHeadline: string
  propNothing: string
  propNothingMoved: string
  propRow: string
  propOffFloor: string
  propUnplaced: string
  propUnplacedWhy: Record<
    'NOT_ASSIGNABLE' | 'NOBODY_FREE' | 'NOBODY_FREE_ON_FLOOR' | 'NOBODY_ON_SHIFT', string>
  propIdle: string
  propApprove: string
  propApplying: string
  propDiscard: string

  /* ── W-77 · WHAT HAPPENED TO YOUR WORK WHILE YOU WERE DOING IT (Sprint 75) ───
     Every sentence names THE PERSON WHO DID IT. "Your room was taken off you" is an
     event; "Haider took 412 off you" is something a man can act on, or at least stop
     wondering about. A notification that reports an act without naming the actor is what
     makes people believe the system does things to them. */
  ntfTitle: string
  ntfSubtitle: string
  ntfSentence: Record<
    'ROOM_CAME_BACK' | 'WORK_TAKEN_OFF_YOU' | 'WORK_GIVEN_TO_YOU' | 'YOUR_FAULT_WAS_CLOSED'
    /* CQ-42's two, Sprint 82. `{room}` carries the item and the figure on these — see
       `Notification.roomNumber` in the core, where the honest cost of that is written
       down rather than hidden behind a second field nulled on every other row. */
    | 'ADJUSTMENT_NEEDS_YOU' | 'YOUR_ADJUSTMENT_WAS_DECIDED',
    string>
  ntfSomebody: string
  ntfARoom: string
  ntfUnread: string
  ntfAllSeen: string
  ntfMarkSeen: string
  ntfNothingTitle: string
  ntfNothingBody: string
  ntfScope: string

  /* ── W-10 · THE OPERATIONS DASHBOARD (Sprint 61) ──────────────────────────
     Pack 1: *"its job is not to show everything — it is to answer one question in the
     first five seconds: can we cover today?"* — and *"the headline is a gap, not a
     total."* So the headline here is a SENTENCE with four versions, not a tile. */
  opsTitle: string
  opsSubtitle: string
  opsQuestion: string
  opsArrivals: Record<Plural, string>
  opsReady: Record<Plural, string>
  /** The four standings, each with its own next action. */
  opsNoArrivals: string
  opsNoArrivalsBody: string
  opsCovered: string
  opsCoveredBody: string
  opsShort: Record<Plural, string>
  opsShortBy: string
  opsShortNoTime: string
  opsCannot: Record<Plural, string>
  opsCannotBody: string
  opsRecoverable: Record<Plural, string>
  /** The tiles. `opsWaiting` is NOT "overdue" — see the change note. */
  opsRooms: string
  opsDepartures: string
  opsDeparturesNote: string
  opsOpenTasks: string
  opsRunning: string
  /** **NOT the same words as `opsReason.AWAITING_INSPECTION`** — see #163. */
  opsToInspect: string
  opsWaitingOldest: string
  opsWaitingNone: string
  opsFaults: string
  opsBlocking: string
  opsNotReady: string
  opsAllReady: string
  opsReason: Record<
    'AWAITING_CLEAN' | 'BEING_CLEANED' | 'AWAITING_INSPECTION'
    | 'OUT_OF_SERVICE' | 'OUT_OF_ORDER' | 'STILL_OCCUPIED', string>
  opsFloors: string
  opsNobodyHolding: string
  opsNobodyHoldingBody: string
  opsLaneDone: string
  opsLaneIn: string
  opsLaneRework: string
  opsLaneLoad: string
  opsLaneNoBudget: string
  opsDecisions: string
  opsAsAt: string
  /* ── W-129 · THE HOTEL'S OWN WORDS (Sprint 55, CQ-130) ────────────────────
     The screen a hotel opens once, in the week before go-live, to correct every word this
     product invented for it. */
  cfgTitle: string
  cfgSubtitle: string
  cfgIntro: string
  cfgHotel: string
  cfgName: string
  cfgNameAr: string
  cfgNameArHint: string
  cfgCode: string
  cfgCodeHint: string
  cfgTimeZone: string
  cfgTimeZoneHint: string
  cfgLanguage: string
  cfgLanguageHint: string
  /* ── THE INSPECTION POLICY (Sprint 77) ────────────────────────────────────
     Enforced since Sprint 1 by the readiness rule and by task submission, and settable
     from nowhere until now — UAT prerequisite CF-04, ours to meet, which we could not
     have met at a readiness review. */
  cfgInspection: string
  cfgInspectionHint: string
  cfgInspectionYes: string
  cfgInspectionNo: string
  cfgInspectionOffWarning: string
  cfgLanguageEn: string
  cfgLanguageAr: string
  cfgSaveHotel: string
  cfgHotelSaved: string
  cfgRoles: string
  cfgRolesIntro: string
  cfgColRole: string
  cfgColName: string
  cfgColNameAr: string
  cfgColHeld: string
  cfgHeld: Record<Plural, string>
  cfgNobodyHolds: string
  cfgRoleSaved: string
  cfgSave: string
  cfgSaving: string

  /* ── W-129, SECOND CARD · WHAT A GUEST MAY ASK FOR (Sprint 56) ──────────────
     The hotel's own list of request kinds. These words are read by whoever sets the
     product up, once — and by a housekeeping manager on the morning they decide there is
     no turndown during Ramadan. */
  rcTitle: string
  rcIntro: string
  rcColCode: string
  rcColName: string
  rcColNameAr: string
  rcColPromise: string
  rcColCountable: string
  rcColStock: string
  rcColAsked: string
  rcMinutes: Record<Plural, string>
  rcNeverAsked: string
  rcAsked: Record<Plural, string>
  rcNoStock: string
  rcCountableYes: string
  rcCountableNo: string
  rcCountableLabel: string
  rcRetired: string
  rcRetire: string
  rcRestore: string
  rcDelete: string
  rcKeepHint: string
  rcAdd: string
  rcAddTitle: string
  rcAddIntro: string
  rcCode: string
  rcCodeHint: string
  rcCodeSuggested: string
  rcCodeBad: string
  rcPromise: string
  rcPromiseHint: string
  rcAddSave: string
  rcCancel: string
  rcConfirmDelete: string

  /* ── W-128 · EVERY WORD THIS PRODUCT INVENTED (Sprint 59) ───────────────────
     A screen opened for one week, once, by whoever is proofreading the Arabic before
     go-live. Its words carry more weight per reading than most, because nobody builds
     familiarity with it. */
  trTitle: string
  trSubtitle: string
  trIntro: string
  trRemaining: Record<Plural, string>
  trAllDone: string
  trNothingToDo: string
  trColCode: string
  trColName: string
  trColNameAr: string
  trGroupEmpty: string
  trSave: string
  trSaving: string
  trElsewhere: string
  trKind: Record<string, string>
  /** **A NOUN PER KIND**, because "40" alone under "Room for four" reads as a price and
   *  "40 things" is not a sentence anybody writes. Only three kinds have anything to count:
   *  a building has floors, a zone has areas, a room type has rooms. */
  trInUseFloors: Record<Plural, string>
  trInUseAreas: Record<Plural, string>
  trInUseRooms: Record<Plural, string>

  /* ── W-135 · GO-LIVE DATA (Sprint 53) ──────────────────────────────────────
     A screen opened for one week and then never again. Its words carry more weight per
     reading than any other screen's, because nobody builds familiarity with it. */
  glTitle: string
  glSubtitle: string
  glIntro: string
  glProgress: string
  glColWhat: string
  glColSource: string
  glColExpected: string
  glColLoaded: string
  glColDepends: string
  glColState: string
  /** The eleven rows, by wire name. */
  glStep: {
    PROPERTIES_AND_FLOORS: string
    ROOM_TYPES: string
    ROOMS: string
    PUBLIC_AREAS: string
    CHECKLIST_TEMPLATES: string
    CLEANING_RULES_PER_ROOM: string
    STOCK_ITEMS: string
    STOCK_LOCATIONS: string
    OPENING_STOCK_COUNT: string
    PEOPLE_AND_ROLES: string
    FIRST_ROSTER: string
  }
  glSource: {
    BY_HAND: string
    SPREADSHEET: string
    IN_BULK: string
    PHYSICAL_COUNT: string
  }
  glState: {
    DONE: string
    SHORT: string
    LOADED: string
    NOT_STARTED: string
    WAITING: string
    BLOCKED: string
  }
  /** The count under a Short row: how many are still missing. */
  glMissing: Record<Plural, string>
  /** Said once, under the table, where a row reads BLOCKED. */
  glSignInNote: string
  /** Said once, under the table, where any row has no target. */
  glNoTargetNote: string
  glWorkedOut: string
  glTargetLabel: string
  glNothingYet: string
  glDays: Record<Plural, string>

  /** CQ-142. Said only when it is true, and only under the days-of-cover line. */
  laundryTooSlow: string
  alert: {
    soldRoomOutOfOrder: string
    requestsLate: Record<Plural, string>
    repeatFailure: string
    stockBelowPar: string
  }
  issuesEmpty: string
  requestsTitle: string
  requestsEmpty: string
  severities: Record<'MINOR' | 'MAJOR' | 'BLOCKING', string>
  issuePhotos: string
  issuePhotoCount: string
  issuePhotoTaken: string
  issuePhotoBy: string
  issuePhotoFailed: string
  issuePhotoClose: string
  /* **REMOVED IN SPRINT 63 (#166).** The fault categories were a dictionary here and a
     DIFFERENT dictionary on the handset — same codes, different words — with nothing on
     the server that knew them. They come from the hotel's own list now, W-75. */
  requestCategories: Record<string, string>
  issueTookRoomOut: string
  issueNeedsOtherRole: string
  resolve: string
  fulfil: string
  late: string
  reasonTitle: string
  reasonBody: string
  save: string
  stockOwnedTitle: string
  stockOwnedSubtitle: string
  stockWhereTitle: string
  stockItem: string
  stockLocation: string
  stockQuantity: string
  stockPar: string
  stockOwned: string
  stockInLocations: string
  stockInRooms: string
  stockCirculating: string
  stockConsumable: string
  stockAllLocations: string
  stockBelowPar: string
  stockEmpty: string
  stockTitle: string

  /* Serviceable areas — W-30. */
  rosterTitle: string
  rosterSubtitle: string
  rosterEmpty: string
  rosterToday: string
  rosterThisWeek: string
  rosterPrevWeek: string
  rosterNextWeek: string
  rosterPerson: string
  rosterUnset: string
  rosterUnsetHint: string
  rosterHolding: string
  rosterHoldingWarning: string
  rosterPickShift: string
  rosterCoverage: string
  rosterCoveragePlaceholder: string
  rosterSave: string
  rosterCancel: string
  rosterSaved: string
  rosterUnwritten: string
  rosterStates: Record<'ON_SHIFT' | 'REST_DAY' | 'LEAVE' | 'ABSENT', string>
  reportsTitle: string
  reportsSubtitle: string
  reportsPick: string
  reportsNone: string
  reportsNoneBody: string
  reportsFrom: string
  reportsTo: string
  reportsLanguage: string
  reportsRun: string
  reportsDownload: string
  reportsPreview: string
  loadTitle: string
  loadSubtitle: string
  loadIntro: string
  loadColumns: string
  loadChoose: string
  loadChooseAnother: string
  loadReading: string
  loadWouldHappen: string
  loadNewRooms: string
  loadMovedRooms: string
  loadUnchangedRooms: string
  loadNotInFile: string
  loadNotInFileNote: string
  loadFloorsAndTypes: string
  loadBuildingsNew: string
  loadLeftOutOfUse: string
  loadConverted: string
  loadCommit: string
  loadLoading: string
  loadFixFirst: string
  loadDone: string
  loadProblems: string
  loadLine: string
  loadRooms: string
  loadColRoom: string
  loadColBuilding: string
  loadTypes: string
  loadColTypeName: string
  loadColSleeps: string
  loadColMinutes: string
  loadColRooms: string
  loadColFloor: string
  loadColType: string
  loadColChange: string
  loadChanges: Record<'NEW' | 'CHANGED' | 'UNCHANGED' | 'LEFT_OUT_OF_USE', string>

  /* W-127's other half — one room at a time. */
  setupTitle: string
  setupSubtitle: string
  editTab: string
  loadTab: string
  editTitle: string
  editListTitle: string
  editCardHint: string
  editIntro: string
  editSearch: string
  editAdd: string
  editShowRemoved: string
  editNoMatch: string
  editEmpty: string
  editColStatus: string
  /* ── W-127's other half: how a room is cleaned (Sprint 29) ─────────────── */
  cleanColDeparture: string
  cleanColStayover: string
  /** How long a departure clean takes in this room. THE ONE NUMBER THE HOTEL STATES. */
  cleanColMinutes: string
  cleanMinutesHint: string
  /* ── WHAT ONE CREDIT IS WORTH HERE (Sprint 32, CQ-133) ──────────────────
     The property's own divisor. Read constantly, changed almost never, so it is a
     button showing the value rather than a field. */
  divisorCurrent: string
  divisorTitle: string
  divisorIntro: string
  divisorLabel: string
  divisorHint: string
  divisorPreview: string
  divisorSaved: string
  /** What that costs a lane — derived, and drawn greyed beside the minutes. */
  cleanColCredits: string
  cleanColDeepClean: string
  cleanNotSet: string
  cleanNeverPublished: string
  cleanUnknownTemplate: string
  cleanEdit: string
  cleanUnconfigured: Record<Plural, string>
  cleanFixThem: string
  cleanTitleOne: string
  cleanTitleMany: string
  cleanIntro: string
  cleanIntroMany: string
  cleanApplyDefaults: string
  cleanNoDefaults: string
  cleanChoose: string
  cleanLeaveAlone: string
  cleanNotes: string
  cleanSaved: string
  cleanNowAssignable: string
  deepClean: Record<'NONE' | 'MONTHLY' | 'QUARTERLY' | 'TWICE_YEARLY' | 'YEARLY', string>
  editColCard: string
  editInHotel: string
  editOutOfHotel: string
  editHasCard: string
  editEdit: string
  editRemove: string
  editRestore: string
  editSave: string
  editSaving: string
  editCancel: string
  editNewRoom: string
  editRoomNumber: string
  editRoomNumberHint: string
  editFloor: string
  editType: string
  editAddFloor: string
  editFloorCode: string
  editAddBuilding: string
  /* ── the kinds of room this hotel has (Sprint 49) ──────────────────────────── */
  typesTitle: string
  typesName: string
  typesSleeps: string
  typesSleepsNote: string
  typesMinutes: string
  typesMinutesNote: string
  typesRooms: string
  typesEditTitle: string

  /* ── W-107 · what each kind of room needs (CQ-136, Sprint 51) ─────────────── */
  linenTitle: string
  linenSubtitle: string
  linenIntro: string
  linenOurs: string
  linenForWork: string
  linenGrid: string
  linenItem: string
  linenSleeps: string
  linenNone: string
  linenNote: string
  editBuildingCode: string
  editBuildingName: string
  editReasonRemove: string
  editReasonRestore: string
  editReasonHint: string
  editRemovedBecause: string
  editNothingChanged: string
  editCreated: string
  editRenumbered: string
  editMoved: string
  editRemoved: string
  editRestored: string
  editReprint: string
  editConverted: string
  editRestoreWarning: string

  /* W-136 — the cards in the rooms. */
  qrTitle: string
  qrSubtitle: string
  qrIntro: string
  qrRooms: string
  qrLive: string
  qrNever: string
  qrWhereTheyStand: string
  qrRevoked: string
  qrRequests: string
  qrIssueMissing: string
  qrIssuing: string
  qrIssuedCount: string
  qrNothingMissing: string
  qrPrint: string
  qrPrinting: string
  qrShowRevoked: string
  qrSearch: string
  qrFloor: string
  qrColRoom: string
  qrColIssued: string
  qrColRequests: string
  qrColState: string
  qrStateLive: string
  qrStateNever: string
  qrStateRevoked: string
  qrIssue: string
  qrRevoke: string
  qrReprint: string
  qrRevokeReason: string
  qrRevokeHint: string
  qrRevokeConfirm: string
  qrRevoked1: string
  qrIssued1: string
  qrSuperseded: string
  qrNoMatch: string
  qrEmpty: string
  qrPrintHint: string

  /* W-125/126 — what a clean consists of. */
  clTitle: string
  clHowVersionsWork: string
  clListTitle: string
  clSubtitle: string
  clIntro: string
  clLive: string
  clDrafts: string
  clRetired: string
  clNew: string
  clColTemplate: string
  clColAppliesTo: string
  clColVersion: string
  clColItems: string
  /* THERE WAS A `clColCredits` HERE. The column it headed came from `TaskType.Credits`,
     which priced a kind of work flatly while the rooms said a twin and a suite take
     different lengths of time (#111). `clColTime` below is the honest heading for the
     number that remains. */
  clColTime: string
  clColInUse: string
  clColState: string
  clStateLive: string
  clStateDraft: string
  clStateNever: string
  clEdit: string
  clEditDraft: string
  clNeverPublished: string
  clEmpty: string
  clMinutes: string
  clBack: string
  clDraftOf: string
  clDraftFirst: string
  clItemsTitle: string
  clItemsHint: string
  clAddItem: string
  clItemCode: string
  clItemEn: string
  clItemAr: string
  clRequired: string
  clOptional: string
  clPhoto: string
  clRemove: string
  clUp: string
  clDown: string
  clChangesTitle: string
  clChangesNone: string
  clChanges: Record<string, string>
  clPhotoCost: string
  clPhotoSaving: string
  clPhotoUnknown: string
  clNotReEvaluated: string
  clPublish: string
  clPublishing: string
  clPublished: string
  clSave: string
  clSaving: string
  clSaved: string
  clNameEn: string
  clNameAr: string
  clSaveName: string
  clNameNoVersion: string
  clNameSaved: string
  clDiscard: string
  clDiscarded: string
  clNewTemplate: string
  clTaskType: string
  clCreate: string

  areasTitle: string
  areasSubtitle: string
  areasNeedAttention: string
  areasAllOnSchedule: string
  areasEmpty: string
  areasEvery: string
  areasLateBy: string
  areasDueIn: string
  areasNeverCleaned: string
  areasLastDone: string
  areasMarkClean: string

  /* ── Closing a public area by hand (Sprint 57) ──────────────────────────────
     `area.setOutOfService` was granted from the first seed with nothing behind it. These
     are the words on the control that finally performs it. */
  areaSvcClose: string
  areaSvcReopen: string
  areaSvcCloseTitle: string
  areaSvcReopenTitle: string
  areaSvcCloseIntro: string
  areaSvcReopenIntro: string
  areaSvcReasonLabel: string
  areaSvcCloseReasonHint: string
  areaSvcReopenReasonHint: string
  areaSvcClosedNow: string
  areaSvcOutOfOrderNote: string
  areaSvcCancel: string
  areaSvcClosed: string
  areaSvcReopened: string
  areasMarked: string
  areasUngrouped: string
  areaStandings: Record<
    'ON_SCHEDULE' | 'DUE_SOON' | 'OVERDUE' | 'MISSED' | 'BEING_CLEANED' | 'CLOSED' | 'RESTING',
    string>

  assignTitle: string
  assignSubtitle: string
  unassigned: string
  /* ── ONE ARABIC WORD FOR A CREDIT, AND IT IS `وحدة عمل` — DEFECT #115 ────
     The product shipped two. The assignment board has said `وحدة عمل` since Sprint 6;
     W-127 and Sprint 32's divisor said `نقطة`. A supervisor balancing a round on one
     screen and setting the divisor on the other read two different words for the number
     they were balancing, and no guard catches it: both are Arabic, both are plausible,
     and the English side of each pair says "credits".

     Worse, Sprint 32's own comment asserted that `نقطة` was what "every other string in
     the product" used — written while fixing a related wording problem, and false. *A
     claim about the rest of the codebase, made without looking at the rest of the
     codebase.*

     `وحدة عمل` won on incumbency and on clarity: twenty-three sprints older, on the
     screen the client approved, and it says what the number IS rather than scoring it.
     **Anything new that means "credit" uses it.** */
  credits: string
  /** `وحدة`, not `و` — a one-letter abbreviation that is also the commonest word in the
   *  language. "٦٫٥٠ / ٨٫٠٠ و" reads as an unfinished sentence. */
  creditsShort: string
  workload: string
  assignHere: string
  assignTo: string
  cancelSelection: string
  selectedCard: string
  moving: string
  noWorkTitle: string
  noWorkBody: string
  /* ── SPRINT 33: WHERE THE DAY'S WORK COMES FROM ──────────────────────────
     The button that sweeps the hotel, and what it reports back. It is safe to press
     twice, which is why the summary reports what it FOUND rather than saying "done". */
  genButton: string
  genWorking: string
  genRaised: string
  genNothingNew: string
  genBreakdown: string
  genSwitchedOff: string
  genUnassignable: string
  genDismiss: string
  /* ── SPRINT 34: THE SWEEP THAT RUNS ITSELF (CQ-135) ──────────────────────
     Read far more often than changed, so the toolbar states the time and puts the
     change one press away — the same shape as W-127's divisor. */
  schedRunsAt: string
  schedManual: string
  schedChange: string
  schedTitle: string
  schedIntro: string
  schedLabel: string
  schedHint: string
  schedOff: string
  schedSavedOn: string
  schedSavedOff: string
  schedOverdue: string
  /** #116 — a card that cannot be handed to anybody has to say so. */
  cardNotConfigured: string
  cardNotConfiguredWhy: string
  laneEmpty: string
  dropHere: string
  notWorking: string
  overBudget: string
  hasCapacity: string
  roster: Record<'ON_SHIFT' | 'REST_DAY' | 'LEAVE' | 'ABSENT', string>

  checklistTitle: string
  room: string
  doneOf: string
  optional: string
  mandatory: string
  cannotDo: string
  whatStoppedYou: string
  saveException: string
  /* ── CQ-42 · a correction waiting for a second person (Sprint 82) ────────
     Your answer: *"all adjustment must be approved by supervisor, and supervisor should
     receive a notification for it."* Every sentence here is written so that the person
     raising one knows NOTHING HAS MOVED, and the person deciding one can see what the
     figure would be taken from. */
  /* ── CQ-26 · the five things that raise work (Sprint 83) ─────────────────
     **EVERY ROW SAYS WHAT STOPS HAPPENING.** A switch labelled only with a name is a
     switch nobody dares touch, and a hotel that quietly stops raising stayover cleans
     finds out from a guest. */
  trgTitle: string
  trgSubtitle: string
  trgNames: Record<
    'GUEST_CHECKOUT' | 'STAYOVER_SERVICE' | 'SCHEDULED_DEEP_CLEAN'
    | 'VACANT_ROOM_REFRESH' | 'ON_REQUEST', string>
  trgDoes: Record<
    'GUEST_CHECKOUT' | 'STAYOVER_SERVICE' | 'SCHEDULED_DEEP_CLEAN'
    | 'VACANT_ROOM_REFRESH' | 'ON_REQUEST', string>
  trgEvery: string
  trgDays: string
  trgCountsFrom: string

  adjTitle: string
  adjSubtitle: string
  adjRaise: string
  adjNothingMoves: string
  adjWhat: string
  adjPickShelf: string
  adjBy: string
  adjByHint: string
  adjWhy: string
  adjWhyHint: string
  adjSend: string
  adjNoneWaiting: string
  adjMovesBy: string
  adjRaisedBy: string
  adjApprove: string
  adjDecline: string
  adjWhyNot: string
  adjWhyNotHint: string
  adjYoursToWaitFor: string
  adjRecent: string
  adjDecidedLine: string
  adjStates: Record<'PROPOSED' | 'APPROVED' | 'DECLINED', string>

  cancel: string
  exceptionRecorded: string
  submitRoom: string
  submitting: string
  submittedTitle: string
  submittedBody: string
  stillNeeded: string
  waitingToSend: string
  submitAfterSending: string
  staleSince: string
  workingOffline: string
  loadFailed: string
  handsetNotice: string

  /* W-105/106 — the physical count and its variance. */
  cntTitle: string
  cntSubtitle: string
  cntIndexTitle: string
  cntIndexIntro: string
  cntStartAt: string
  cntStarting: string
  cntNoLocations: string
  cntRecent: string
  cntNoCounts: string
  cntColReference: string
  cntColLocation: string
  cntColStarted: string
  cntColProgress: string
  cntColState: string
  cntOpen: string
  cntSubmitted: string
  cntApproved: string
  cntAbandoned: string
  cntOpenIt: string
  cntBack: string
  cntSheetTitle: string
  cntBlindTitle: string
  cntBlindWhy: string
  cntProgress: string
  cntColItem: string
  cntColCounted: string
  cntColCountedBy: string
  cntColTime: string
  cntColExpected: string
  cntHidden: string
  cntUnreadable: string
  cntNotCounted: string
  cntSave: string
  cntSaving: string
  cntSaved: string
  cntHandOver: string
  cntHandingOver: string
  cntHeldNotice: string
  cntAbandon: string
  cntAbandonReason: string
  cntAbandonConfirm: string
  cntCancel: string
  cntVarianceTitle: string
  cntVarianceIntro: string
  cntFigItems: string
  cntFigMatched: string
  cntFigVariances: string
  cntFigNet: string
  cntFigNotCounted: string
  cntShortOver: string
  cntNoAdjustment: string
  cntPercentOfHolding: string
  cntColDifference: string
  cntColReason: string
  cntColDecision: string
  cntReasonMatched: string
  cntReasonReceipt: string
  cntReasonLaundry: string
  cntReasonCirculation: string
  cntReasonIssued: string
  cntReasonLoss: string
  cntAdjNone: string
  cntAdjUp: string
  cntAdjWriteOff: string
  cntAdjRecount: string
  cntWorkingLaundry: string
  cntWorkingCirculation: string
  cntApprove: string
  cntApproving: string
  cntSendBack: string
  cntSendBackReason: string
  cntSendingBack: string
  cntYouCounted: string
  cntSubmittedBy: string
  cntApprovedBy: string
  cntAbandonedBy: string
  cntReturnedNote: string
  cntRound: string

  /* W-120/121 — everyone who can sign in. */
  usrTitle: string
  usrSubtitle: string
  usrWhoCanSignIn: string
  usrIntro: string
  usrNoPms: string
  usrActive: string
  usrNoRole: string
  usrDisabled: string
  usrAdd: string
  usrAdding: string
  usrAddTitle: string
  usrEmployeeNo: string
  usrName: string
  usrNameAr: string
  usrLanguage: string
  usrEnglish: string
  usrArabic: string
  usrCreate: string
  usrCancel: string
  usrColPerson: string
  usrColEmployeeNo: string
  usrColRole: string
  usrColProperty: string
  usrColLastSeen: string
  usrColSource: string
  usrColState: string
  usrEmail: string
  usrSourceSynced: string
  usrSourceEnteredHere: string
  usrNeverSignedIn: string
  usrNoneHere: string
  usrOpen: string
  usrBack: string
  usrFromPms: string
  usrFromPmsWhy: string
  usrOurs: string
  usrOursWhy: string
  usrRole: string
  usrProperty: string
  usrSave: string
  usrSaving: string
  usrSaved: string
  usrRemoveRole: string
  usrSessions: string
  usrSessionsWhy: string
  usrNoSessions: string
  usrColDevice: string
  usrColSignedIn: string
  usrColExpires: string
  usrColActivity: string
  usrUnknownDevice: string
  usrDeviceHandsetApp: string
  usrDeviceAndroid: string
  usrDeviceIphone: string
  usrDeviceIpad: string
  usrDeviceEdge: string
  usrDeviceFirefox: string
  usrDeviceChrome: string
  usrDeviceSafari: string
  usrThisDevice: string
  usrEnd: string
  usrEndAll: string
  usrEnded: string
  usrEndedByAdmin: string
  usrEndedSignedOut: string
  usrEndedDisabled: string
  usrEndedAll: string
  usrActivity: string
  usrActivityWindow: string
  usrTasksCompleted: string
  usrInspectionsFailed: string
  usrRequestsFulfilled: string
  usrNoRate: string
  usrDisable: string
  usrEnable: string
  usrDisableReason: string
  usrDisableConfirm: string
  usrDisableWarning: string
  usrDisabledOn: string
  usrCannotSignIn: string
  /* ── W-122 · roles and permissions ─────────────────────────────────────── */
  rolTitle: string
  rolSubtitle: string
  rolWhy: string
  rolHalfMeans: string
  rolAll: string
  rolChangedOnly: string
  rolNothingChanged: string
  rolColArea: string
  rolColCapability: string
  rolSave: string
  rolSaving: string
  rolSaved: string
  rolDiscard: string
  rolUnsaved: string
  rolChangedFromDefault: string
  rolDefaultIs: string
  rolDenied: string
  rolAllowed: string
  rolAllowedWithReason: string
  rolEnforcedBy: string
  rolNotEnforcedYet: string
  rolAreaRooms: string
  rolAreaWork: string
  rolAreaRequests: string
  rolAreaFaults: string
  rolAreaAreas: string
  rolAreaInventory: string
  rolAreaReports: string
  rolAreaFiles: string
  rolAreaAdministration: string
  rolCapRoomViewReadiness: string
  rolCapRoomSetOutOfService: string
  rolCapRoomOverrideToClean: string
  rolCapRoomSyncFromPms: string
  rolCapTaskStart: string
  rolCapTaskSubmit: string
  rolCapTaskAssign: string
  rolCapTaskRaise: string
  rolCapTaskInspect: string
  rolCapTaskCancel: string
  rolCapRosterEdit: string
  rolCapRequestRaise: string
  rolCapRequestFulfil: string
  rolCapIssueRaise: string
  rolCapFaultDowngradeBlocking: string
  rolCapAreaClean: string
  rolCapAreaSetOutOfService: string
  rolCapStockMove: string
  rolCapStockAdjust: string
  rolCapAdminStockPars: string
  /** CQ-138 — correcting another man's linen figure after the room was handed in. */
  rolCapLinenAdjust: string
  /** W-135 — reading the go-live checklist, and typing the hotel's own figures into it. */
  rolCapAdminGoLive: string
  /** W-129 — the hotel's own name, its zone, and what the roles are called. */
  rolCapAdminConfiguration: string
  rolCapAdminRequestCategories: string
  rolCapAdminFaultCategories: string
  rolCapReportAttendantProductivity: string
  rolCapReportRoomsPerDay: string
  rolCapReportFaultsDowntime: string
  rolCapReportLoadThroughDay: string
  rolCapReportConsumables: string
  rolCapReportLinenLoss: string
  rolCapReportRequestLateness: string
  rolCapReportInspectionFailures: string
  rolCapFileRemove: string
  rolCapAdminRooms: string
  rolCapAdminLinenStandards: string
  rolCapAdminQrCodes: string
  rolCapAdminChecklists: string
  rolCapAdminUsers: string
  rolCapAdminRoles: string
  rolCapAdminTranslations: string
  rolCapAdminActivity: string

  /* ── W-124, THE ACTIVITY LOG (Sprint 31) ──────────────────────────────────
     Pack 5: "Who did what, kept for two years, filtered by the three things anyone ever
     looks for." Three groups of strings: the screen's own chrome, the VERBS (what
     happened), and the VALUES the before/after columns hold.

     The last two are the interesting ones, and they are here rather than derived because
     they arrive from the server as tokens — which is the only way the one screen an
     Arabic-speaking administrator opens after an incident can be in Arabic at all. Both
     groups are held against `app/contracts/activity-values.json` by a test, so a token the
     server starts writing cannot reach a screen with no sentence for it. */
  actTitle: string
  actSubtitle: string
  actTabAll: string
  actTabPermissions: string
  actTabOverrides: string
  actTabSignIns: string
  actColWhen: string
  actColWho: string
  actColWhat: string
  actColSubject: string
  actColBefore: string
  actColAfter: string
  actColWhere: string
  actExport: string
  actExporting: string
  actFrom: string
  actTo: string
  actRangeAll: string
  actRange7: string
  actRange30: string
  actRange90: string
  actOlder: string
  actLoading: string
  actEmptyTitle: string
  actEmptyBody: string
  actEmptyFilteredBody: string
  actErrorTitle: string
  actNoPermissionTitle: string
  actNoPermissionBody: string
  actRetentionNote: string
  actEntries: {
    one: string
    two: string
    few: string
    many: string
    other: string
  }
  actNothing: string
  actReasonLabel: string

  /** Who, when nobody did it. */
  actActor: {
    SYSTEM: string
    UNKNOWN: string
  }

  /** What happened. One per `ActivityVerb`. */
  actVerb: {
    PERMISSION_CHANGED: string
    ROLE_ASSIGNED: string
    ROLE_REMOVED: string
    SIGN_IN_REJECTED: string
    SIGN_IN_NO_ACCOUNT: string
    ROOM_OVERRIDDEN: string
    TASK_CANCELLED: string
    FAULT_DOWNGRADED: string
    STOCK_ADJUSTED: string
    PHOTOGRAPH_REMOVED: string
    USER_DISABLED: string
    USER_ENABLED: string
    PERSON_ADDED: string
    SESSION_ENDED: string
    ROOM_REMOVED_FROM_HOTEL: string
    ROOM_RESTORED: string
    CLEANING_RULES_SET: string
    CHECKLIST_PUBLISHED: string
    /* **#151 — THIS ONE WAS MISSING FOR A WHOLE SPRINT.** Sprint 51 added the verb on the
       server and the guard here counted eighteen rather than naming them, so the Arabic
       audit log drew LINEN_STANDARD_SET in Latin capitals and nothing failed. */
    LINEN_STANDARD_SET: string
    /* CQ-138, Sprint 52. */
    ROOM_TAKEN_OUT_OF_SERVICE: string
    LINEN_FIGURE_CORRECTED: string
    /* W-129, Sprint 55 — CQ-130. */
    HOTEL_DETAILS_SET: string
    INSPECTION_POLICY_SET: string
    ROLE_RENAMED: string
    /* W-129, second card, Sprint 56. */
    FAULT_CATEGORY_ADDED: string
    FAULT_CATEGORY_CHANGED: string
    FAULT_CATEGORY_RETIRED: string
    FAULT_CATEGORY_RESTORED: string
    FAULT_CATEGORY_REMOVED: string
    STOCK_PLACE_ADDED: string
    STOCK_PLACE_CHANGED: string
    STOCK_PLACE_RETIRED: string
    STOCK_PLACE_RESTORED: string
    STOCK_PLACE_REMOVED: string
    STOCK_ITEM_ADDED: string
    STOCK_ITEM_CHANGED: string
    STOCK_ITEM_RETIRED: string
    STOCK_ITEM_RESTORED: string
    STOCK_ITEM_REMOVED: string
    SHIFT_ADDED: string
    SHIFT_CHANGED: string
    SHIFT_RETIRED: string
    SHIFT_RESTORED: string
    /* CQ-42, Sprint 82. Three rows for one correction: somebody asserted the shelf was
       wrong, and a second person accepted or refused it. */
    /* CQ-26, Sprint 83 — a hotel switched one of the five things that raise work. */
    WORK_TRIGGER_CHANGED: string
    STOCK_ADJUSTMENT_PROPOSED: string
    STOCK_ADJUSTMENT_APPROVED: string
    STOCK_ADJUSTMENT_DECLINED: string
    REQUEST_CATEGORY_ADDED: string
    REQUEST_CATEGORY_CHANGED: string
    REQUEST_CATEGORY_RETIRED: string
    REQUEST_CATEGORY_RESTORED: string
    REQUEST_CATEGORY_REMOVED: string
    /* Sprint 57 — the catalogue's third "granted, not built" row. */
    AREA_TAKEN_OUT_OF_SERVICE: string
    /* W-128, Sprint 59. */
    TRANSLATION_CHANGED: string
  }

  /** What kind of thing it was done to. One per entry in
      `ActivityValues.SubjectTypes`. */
  actSubject: {
    ROOM: string
    USER: string
    ROLE: string
    SESSION: string
    SIGN_IN: string
    ISSUE: string
    STOCK_ITEM: string
    TEMPLATE: string
    TASK: string
    CHECKLIST_ANSWER: string
    FAULT_CATEGORY: string
    STOCK_PLACE: string
    REQUEST_CATEGORY: string
    AREA: string
    /* W-128's nine kinds, Sprint 59 — the subject of a corrected Arabic name. */
    ROOM_TYPE: string
    STOCK_LOCATION: string
    TASK_TYPE: string
    SHIFT: string
    /* CQ-26 — one of the five things that raise work. Sprint 83. */
    WORK_TRIGGER: string
    ZONE: string
    BUILDING: string
    PROPERTY: string
  }

  /** Where it came from. */
  actSource: {
    WEB: string
    MOBILE: string
    RULE: string
  }

  /** The before and after columns. One per entry in `ActivityValues.All` — room states,
      permission grants, issue states, movement kinds, role codes, and the handful of
      tokens this screen introduced. */
  actValue: {
    ACTIVE: string
    /* W-129, second card, Sprint 56 — a kind of request switched off. Its opposite is
       ACTIVE above, shared deliberately: one word, one sentence, one translation. */
    RETIRED: string
    DISABLED: string
    LIVE: string
    ENDED_BY_ADMIN: string
    ALL_ENDED: string
    USER_DISABLED: string
    IN_HOTEL: string
    REMOVED: string
    /* CQ-138 — the two columns of the row that says somebody took a room off sale.
       OUT_OF_ORDER cannot be reached through that act, but it is a value the column can
       hold, and a token the log can print is a token the log must be able to say. */
    IN_SERVICE: string
    OUT_OF_SERVICE: string
    OUT_OF_ORDER: string
    PRESENT: string
    REJECTED: string
    NO_ACCOUNT: string
    EMPTY: string
    CLEARED_TEMPLATES: string
    UNKNOWN_DEVICE: string
    ALL_DEVICES: string
    HANDSET_APP: string
    ANDROID: string
    IPHONE: string
    IPAD: string
    EDGE: string
    FIREFOX: string
    CHROME: string
    SAFARI: string
    DENIED: string
    ALLOWED: string
    ALLOWED_WITH_REASON: string
    DIRTY: string
    IN_PROGRESS: string
    CLEAN: string
    INSPECTED: string
    INSPECTION_REQUIRED: string
    INSPECTION_NOT_REQUIRED: string
    OPEN: string
    ACKNOWLEDGED: string
    RESOLVED: string
    CANCELLED: string
    RECEIPT: string
    TRANSFER: string
    ISSUE: string
    RETURN: string
    LOSS: string
    ADJUSTMENT: string
    /* CQ-42's three states, Sprint 82 — the two columns of a decision row. */
    PROPOSED: string
    APPROVED: string
    DECLINED: string
    /* CQ-26's switch, Sprint 83. "ON 14" carries a number and is read as a value. */
    ON: string
    OFF: string
    ROOM_ATTENDANT: string
    FLOOR_SUPERVISOR: string
    HOUSEKEEPING_MANAGER: string
    STORE_KEEPER: string
    FRONT_DESK: string
    ADMINISTRATOR: string
  }
}

const en: Dict = {
  boardTitle: 'Room board',
  boardSubtitle: 'Live room condition',
  legendTitle: 'Key',
  refresh: 'Refresh',
  searchLabel: 'Room number',
  searchPlaceholder: 'Search a room number',
  allFloors: 'All floors',
  building: 'Building',
  teamsTitle: 'Teams',
  teamsSubtitle: 'Who works with whom, and on which floor',
  teamsPairs: 'Pairs',
  teamsFloors: 'Floors today',
  teamsUnpaired: 'On no team',
  teamsNoPairs: 'Nobody is paired yet',
  teamsNoPairsBody: 'Choose two men below and put them together. A pair is given a floor, and either of them may work any room on it.',
  teamsMakePair: 'Make a pair',
  teamsPairThem: 'Pair them',
  teamsDisband: 'Break up',
  teamsCover: 'Cover',
  teamsCoverFor: 'Who is not coming in?',
  teamsCovering: 'covering for',
  teamsClearCover: 'He came in',
  teamsNobody: 'Nobody',
  teamsGiveFloor: 'Give this floor to',
  teamsRoomsMoved: '{count} rooms moved to this pair',
  teamsHolding: 'holding {count}',
  teamsOffToday: 'not in today',
  teamsOnShift: 'on shift',
  teamsNoFloor: 'no floor yet',
  teamsChoose: 'Choose',
  allBuildings: 'All buildings',
  floor: 'Floor',
  emptyTitle: 'No rooms on this board yet',
  emptyBody: 'Rooms appear here once the property has been set up.',
  noMatchTitle: 'No rooms match',
  noMatchBody: 'Try a different filter, or clear the search.',
  retry: 'Try again',
  updatedAt: 'Updated',
  rooms: 'rooms',
  filters: {
    ALL: 'All',
    READY: 'Ready',
    DIRTY: 'Dirty',
    IN_PROGRESS: 'In progress',
    AWAITING_INSPECTION: 'Awaiting inspection',
    NOT_IN_SERVICE: 'Not in service',
    OCCUPIED: 'Occupied',
    DEPARTING: 'Departing today',
  },
  tones: {
    dirty: 'Dirty',
    prog: 'In progress',
    clean: 'Clean',
    ready: 'Inspected',
    oos: 'Out of service',
    ooo: 'Out of order',
  },
  legendOccupancy: 'corner chip = occupancy',
  legendNotSellable: 'dashed = not sellable',

  /* ── THE OVERRIDE (T8) ────────────────────────────────────────────────────
     THE ONLY TRANSITION ON THIS SCREEN, and the words have to carry why. Every other
     move a room makes belongs to a task and happens on the handset or the assignment
     board; this one is a person with authority saying a room is fine when the system has
     no record of it being cleaned.

     THERE IS NO "you may not do this" STRING, deliberately. A role without
     `room.overrideToClean` gets tiles that do not open at all — a control offered and then
     refused is worse than one never offered, and a sentence explaining a dialog nobody can
     reach would be a dead string. Two of these were written and deleted unused. */
  ovrTitle: 'Mark room {room} clean',
  /* WHAT IT COSTS, NOT WHAT IT DOES. Somebody reaching this dialog already knows what
     "mark clean" means; what they need before pressing is that it is recorded with their
     name against it, and that nobody cleaned the room as far as the record goes. */
  ovrIntro:
    'This records room {room} as clean without any cleaning work against it. It is kept '
    + 'in the activity log with your name and your reason, and a manager can see it.',
  ovrCurrent: 'The room is {state} now.',
  ovrReasonLabel: 'Why',
  ovrReasonHint: 'Whoever reads this in six weeks was not standing where you are.',
  ovrConfirm: 'Mark it clean',
  ovrDone: 'Room {room} is marked clean.',
  /* THE ONLY STATE IT APPLIES FROM, said on the tile rather than as a refusal after the
     press. A room already in progress has an attendant on it, and the way to end that is
     to cancel the work — which is a different act, on a different screen, with its own
     record. */
  ovrOnlyDirty:
    'Only a dirty room can be marked clean this way. Work already under way is cancelled '
    + 'from the assignment board.',

  /* ── CQ-138 ────────────────────────────────────────────────────────────────
     **THE TITLES NAME THE DIRECTION**, because the same control does both and a heading
     that read "Room 110, availability" would leave somebody working out which way the
     button goes from the button. */
  svcOffTitle: 'Take room {room} off sale',
  svcOnTitle: 'Put room {room} back on sale',
  /* WHAT IT COSTS, like the override above it: what the front desk sees, and that the
     room does not come back on its own. */
  svcOffIntro:
    'The front desk cannot sell room {room} until somebody puts it back. Cleaning carries '
    + 'on as normal — this is about selling the room, not about its condition.',
  svcOnIntro:
    'Room {room} can be sold again as soon as this is done, subject to its condition and '
    + 'whoever is in it.',
  svcOffReasonLabel: 'Why it is coming off sale',
  svcOnReasonLabel: 'Why it is going back on sale',
  svcOffReasonHint: 'A room off sale with no reason against it is one nobody dares put back.',
  svcOnReasonHint: 'Say what finished. The room goes back on sale straight away.',
  svcTakeOff: 'Take it off sale',
  svcPutBack: 'Put it back on sale',
  svcOffNow: 'This room is off sale.',
  /* A ROOM A FAULT TOOK OUT OF ORDER IS NOT THIS DIALOG'S. Said here rather than left to
     a refusal, so the supervisor learns where to go rather than only that they may not. */
  svcOutOfOrderNote:
    'This room is out of order because a fault was raised on it. Resolve the fault to put '
    + 'it back on sale.',

  /* ── WHAT HAPPENED TO THIS ROOM ───────────────────────────────────────────
     SENTENCES, NOT STATE PAIRS. "DIRTY to IN_PROGRESS" is what the database holds; what a
     supervisor is reading the panel to find out is that somebody started cleaning. The
     words come from the server (`Wire.Of(TransitionCode)`) and the sentences from here,
     so the two languages are held together by the dictionary guard. */
  histTitle: 'What happened',
  histEmpty: 'Nothing has happened to this room in the last {days} days.',
  histLoading: 'Reading the room\u2019s history\u2026',
  histBy: 'by {name}',
  /* NOT "by system". The property system is a thing the hotel recognises; "system" is a
     word that makes somebody ask whose. */
  histByNobody: 'from the property system',
  histTruncated: 'Only the most recent {count} are shown.',
  /* THE GAP IS SAID ONLY WHEN THERE IS ONE. An attendant working with no signal records
     the time on their handset and the server applies it when the queue drains; on a
     morning where that gap is two hours, a panel showing only the server's time makes it
     look as though nothing happened until eleven. */
  histCapturedEarlier: 'done at {time}, recorded at {recorded}',
  roomEvents: {
    STARTED: 'Cleaning started',
    SUBMITTED: 'Submitted for inspection',
    ACCEPTED: 'Inspection passed',
    REJECTED: 'Inspection rejected',
    SOILED: 'Back to dirty',
    SOILED_BEFORE_INSPECTION: 'Back to dirty before inspection',
    CANCELLED: 'Work cancelled',
    /* THE ONE THAT IS NOT ORDINARY WORK, and it says so in the words rather than relying
       on the reason beside it. */
    OVERRIDDEN: 'Marked clean without a clean',
    TOUCH_UP: 'Touch-up started',
  },
  issuesTitle: 'Reported faults',

  escalationsTitle: 'Needs you',
  escalationsSubtitle: 'What the product noticed and cannot fix by itself',
  needsYou: 'Needs you',
  nothingNeedsYou: 'Nothing needs you',
  nothingNeedsYouBody: 'No room is blocked, no promise is broken and the shelves are '
    + 'holding. This screen fills itself when something changes.',
  thingsNeedYou: {
    one: '{count} thing · nothing else can move it',
    two: '{count} things · nothing else can move them',
    few: '{count} things · nothing else can move them',
    many: '{count} things · nothing else can move them',
    other: '{count} things · nothing else can move them',
  },
  minutesOver: '{minutes} over',
  minutesAgo: '{minutes} ago',
  now: 'Now',
  today: 'Today',
  roomAndArrival: '{room} · arrival {time}',
  roomItemAndPerson: '{room} {item} · {name}',
  ofPar: '{quantity} of {par} par',
  neverAccepted: {
    one: '{count} has never been accepted',
    two: '{count} have never been accepted',
    few: '{count} have never been accepted',
    many: '{count} have never been accepted',
    other: '{count} have never been accepted',
  },
  failuresThisWeek: {
    one: '{count} failure this week',
    two: '{count} failures this week',
    few: '{count} failures this week',
    many: '{count} failures this week',
    other: '{count} failures this week',
  },
  daysOfCover: {
    one: 'About {count} day left at the recent rate',
    two: 'About {count} days left at the recent rate',
    few: 'About {count} days left at the recent rate',
    many: 'About {count} days left at the recent rate',
    other: 'About {count} days left at the recent rate',
  },

  /* ── W-75 · WHAT CAN BE WRONG WITH A ROOM (Sprint 63) ────────────────────── */
  fcTitle: 'What can be wrong with a room',
  /* **IT SAYS WHOSE LIST IT WAS.** Every one of these six words was written by us, and a
     screen that did not admit it would be quoting the hotel back to itself. */
  fcIntro:
    'These are the choices an attendant sees when they report something broken, and the '
    + 'groups every fault report counts by. We wrote them; correct them, add what this '
    + 'hotel has and switch off what it does not.',
  fcColRaised: 'Reported',
  fcNeverRaised: 'Never',
  fcRaised: {
    one: '{count} fault',
    two: '{count} faults',
    few: '{count} faults',
    many: '{count} faults',
    other: '{count} faults',
  },
  fcStillOpen: {
    one: '{count} still open',
    two: '{count} still open',
    few: '{count} still open',
    many: '{count} still open',
    other: '{count} still open',
  },
  fcKeepHint:
    'A category something has been reported under is kept, because those reports are part '
    + 'of this hotel\u2019s history — switching it off stops it being offered and leaves them '
    + 'alone. Only a row nothing has ever been reported under can be removed.',
  fcAdd: 'Add a kind of fault',
  fcAddTitle: 'A new kind of fault',
  fcAddIntro:
    'The code is permanent: it is what every fault report counts by, and there is no way '
    + 'to change it afterwards that leaves those reports consistent. The names are not — '
    + 'correct them whenever you like.',
  fcAddSave: 'Add it',

  /* ── W-22 · THE ROOM LIST (Sprint 63) ──────────────────────────────────── */
  rlView: 'How to show the rooms',
  rlAsGrid: 'Board',
  rlAsList: 'List',
  rlSortBy: 'sort by this',
  rlRoom: 'Room',
  rlType: 'Type',
  rlFloor: 'Floor',
  rlCondition: 'Condition',
  rlOccupancy: 'Guest',
  rlService: 'On sale',
  rlAssigned: 'With',
  rlDeparture: 'Leaves',
  rlOcc: {
    VACANT: 'Empty',
    OCCUPIED: 'Guest in',
    ARRIVING: 'Arriving',
    DEPARTING: 'Leaving today',
  },
  rlSvc: {
    IN_SERVICE: 'On sale',
    OUT_OF_SERVICE: 'Off sale',
    OUT_OF_ORDER: 'Out of order',
  },

  /* ── W-21 · THE ROOM (Sprint 62) ───────────────────────────────────────── */
  rdBack: 'Back to the board',
  rdFloor: 'Floor {code}',
  rdVip: 'VIP',
  rdGuest: 'The guest',
  rdGuestInResidence: 'Guest in residence',
  rdGuestLeaving: 'Guest leaving today',
  rdNoArrivalToday: 'No arrival today',
  /* **NOT "no arrival today", WHICH IS THE WHOLE POINT OF THE SENTENCE.** A clerk told
     there is no arrival promises the room for the afternoon, and the guest walks in at
     eleven. */
  rdArrivalUnknown: 'Somebody is arriving — the property system has not said when',
  rdArrivingAt: 'Arriving at {time}',
  rdAdults: {
    one: '{count} adult',
    two: '{count} adults',
    few: '{count} adults',
    many: '{count} adults',
    other: '{count} adults',
  },
  rdPeopleIn: {
    one: '{count} person in the room',
    two: '{count} people in the room',
    few: '{count} people in the room',
    many: '{count} people in the room',
    other: '{count} people in the room',
  },
  rdSleeps: 'the room sleeps {count}',
  rdOverCapacity: '{count} people in a room with fewer beds than that.',
  rdLeavingAt: 'Leaving at {time}',
  rdWork: 'Outstanding work',
  rdNothingDue: 'Nothing is due on this room',
  rdNothingDueBody: 'No cleaning task is open against it today.',
  rdCredits: '{credits} cr',
  rdEstimated: 'about {minutes}',
  rdStartedAt: 'Started at {time}',
  rdStates: {
    CREATED: 'Raised, nobody assigned',
    ASSIGNED: 'Assigned',
    STARTED: 'Being cleaned',
    SUBMITTED: 'Handed in, waiting for inspection',
    REWORK: 'Sent back',
    COMPLETED: 'Finished',
    CANCELLED: 'Cancelled',
  },
  /* **"WILL" AND "WOULD" ARE TWO DIFFERENT PROMISES.** A task already bound to a version
     will be cleaned against that one whatever is published tomorrow. An unstarted task
     merely would follow the newest. Saying "will" for both is how somebody edits a
     checklist, expects the room in progress to change, and is quietly wrong. */
  rdWillFollow: 'Will be cleaned against {name}, version {version} — {items} items',
  rdWouldFollow: 'Would follow {name}, version {version} — {items} items',
  rdNeeds: 'What this room needs',
  rdAboutRoom: 'About this room',
  rdLastTime: 'Last time',
  rdCleanedOn: 'Cleaned {date}',
  rdByPerson: 'by {name}',
  rdPassedFirstTime: 'Passed inspection first time.',
  rdWasRejected: 'Was sent back at least once.',
  /* NOT "not inspected yet" — there may be no inspection coming. This hotel may not
     inspect at all, and a sentence implying one is owed would invent a promise. */
  rdNotInspected: 'Not inspected.',
  rdActs: 'What you can do',

  fdBack: 'Back to the board',
  fdTitle: 'Fault',
  fdReportedBy: 'Reported by {name}, {when}',
  fdCapturedEarlier: 'Recorded on the phone at {time} and reached us at {recorded}.',
  fdOpenFor: 'Open for {duration}',
  fdWasOpenFor: 'Was open for {duration}',
  fdAcknowledgedBy: 'Taken by {name}, {when}',
  fdClosedBy: 'Closed by {name}, {when}',
  fdNobodyYet: 'Nobody has taken it yet.',
  fdStates: {
    OPEN: 'Open', ACKNOWLEDGED: 'Taken', RESOLVED: 'Resolved', CANCELLED: 'Cancelled',
  },
  fdPlace: 'Where',
  fdRoomOnFloor: 'Room {number} · floor {floor}',
  fdPublicArea: 'Public area',
  fdTookPlaceOut: 'This is the fault that took it out of order.',
  fdPlaceStillShut: 'It is still out of order.',
  fdPlaceBackInUse: 'It is back in use.',
  fdShutBySomethingElse: 'It is out of order for another reason.',
  fdOtherFaults: {
    one: '{count} other fault is open here.',
    two: '{count} other faults are open here.',
    few: '{count} other faults are open here.',
    many: '{count} other faults are open here.',
    other: '{count} other faults are open here.',
  },
  fdOtherBlocking: {
    one: 'One of them is blocking, so clearing this will not put it back.',
    two: '{count} of them are blocking, so clearing this will not put it back.',
    few: '{count} of them are blocking, so clearing this will not put it back.',
    many: '{count} of them are blocking, so clearing this will not put it back.',
    other: '{count} of them are blocking, so clearing this will not put it back.',
  },
  fdNothingElseWrong: 'Nothing else is reported here.',
  fdWhatWasSaid: 'What was reported',
  fdNoDescription: 'No description was typed.',
  fdHistory: 'Every step',
  fdNoHistory: 'Nothing has been recorded against this fault yet.',
  fdRaised: 'Raised',
  fdByNobody: 'no name recorded',
  fdActs: 'What you can do',
  fdAcknowledge: 'Take it',
  fdCancel: 'Cancel the report',
  fdCancelReasonBody: 'Cancelling says this was not a real fault, so it is recorded with '
    + 'your name.',
  fdAlreadyClosed: 'This fault is closed. Nothing more can be done to it here.',

  iqTitle: 'Waiting for inspection',
  iqNav: 'To inspect',
  iqSubtitle: 'Oldest first. Open one to pass it or send it back.',
  iqBack: 'Back to the queue',
  iqEmpty: 'Nothing is waiting.',
  iqAllClear: 'Nothing is waiting for inspection.',
  iqHeadline: {
    one: '{count} room is waiting, for {oldest}.',
    two: '{count} rooms are waiting. The oldest has waited {oldest}.',
    few: '{count} rooms are waiting. The oldest has waited {oldest}.',
    many: '{count} rooms are waiting. The oldest has waited {oldest}.',
    other: '{count} rooms are waiting. The oldest has waited {oldest}.',
  },
  iqBlockedForYou: {
    one: 'One of them is yours to clean, so somebody else must sign for it.',
    two: '{count} of them are yours to clean, so somebody else must sign for them.',
    few: '{count} of them are yours to clean, so somebody else must sign for them.',
    many: '{count} of them are yours to clean, so somebody else must sign for them.',
    other: '{count} of them are yours to clean, so somebody else must sign for them.',
  },
  iqOnFloor: 'floor {floor}',
  iqExceptions: {
    one: 'One thing he could not do.',
    two: '{count} things he could not do.',
    few: '{count} things he could not do.',
    many: '{count} things he could not do.',
    other: '{count} things he could not do.',
  },
  iqCannot: {
    NOT_HANDED_IN: 'This room has not been handed in yet.',
    NOT_ASSIGNED: 'Nobody was assigned to this room, so there is no work to sign for.',
    OWN_WORK: 'This is your own work. Somebody else must inspect it.',
  },
  iqHandedInBy: 'Handed in by {name} at {time}',
  iqFigures: 'The numbers',
  iqWaiting: 'Waiting',
  iqTaken: 'Took',
  iqNotRecorded: 'not recorded',
  iqAgainst: 'against {minutes}',
  iqTheList: 'What he was asked to do',
  iqAnsweredOf: '{answered} of {total}',
  iqNoChecklist: 'This room had no checklist.',
  iqHeDidIt: 'He marked it done.',
  iqHeCouldNot: 'He could not do it.',
  iqNotAnswered: 'He did not answer this.',
  iqFail: 'Fail',
  iqPriorFailures: {
    one: 'He has failed this once already this week.',
    two: 'He has failed this {count} times already this week.',
    few: 'He has failed this {count} times already this week.',
    many: 'He has failed this {count} times already this week.',
    other: 'He has failed this {count} times already this week.',
  },
  iqVerdict: 'Your verdict',
  iqMarked: {
    one: 'One line marked.',
    two: '{count} lines marked.',
    few: '{count} lines marked.',
    many: '{count} lines marked.',
    other: '{count} lines marked.',
  },
  iqWhyBack: 'Why is it going back?',
  iqSendBack: 'Send it back',
  iqPass: 'Pass the room',
  iqPassed: 'Passed. The room is ready.',
  iqSentBack: 'Sent back. It is on his list again.',

  rpTitle: 'To send up',
  rpSubtitle: 'What each pantry is short of, and whether the store can cover it.',
  rpNothingShort: 'Every pantry is at par.',
  rpOnFloor: 'floor {floor}',
  rpHas: 'Has',
  rpInStore: 'In store',
  rpSendCol: 'Send',
  rpSend: 'Send it up',
  rpStoreShort: 'the store cannot cover this',
  rpNeedsOtherRole: 'Moving stock needs a different role.',
  rpLines: {
    one: 'One line to send.',
    two: '{count} lines to send.',
    few: '{count} lines to send.',
    many: '{count} lines to send.',
    other: '{count} lines to send.',
  },
  rpSent: {
    one: 'One line sent.',
    two: '{count} lines sent.',
    few: '{count} lines sent.',
    many: '{count} lines sent.',
    other: '{count} lines sent.',
  },
  rpShortTitle: 'The store cannot fill the pantries',
  rpShortBody: 'This is an order to place, not a trolley to load.',
  rpShortLine: '{needed} needed across the pantries, {held} in the store',

  lnTitle: 'At the laundry',
  lnSubtitle: 'What is down there, and how long it has been.',
  lnNoLaundry: 'No laundry has been set up for this hotel.',
  lnNothingThere: 'Nothing is at the laundry.',
  lnThere: 'There',
  lnOut: 'Oldest out',
  lnSent: 'Sent this week',
  lnBack: 'Back this week',
  lnHours: '{hours}h',
  lnLate: 'late',
  lnAllOnTime: 'Nothing has been at the laundry longer than {hours} hours.',
  lnOverdue: {
    one: 'One item has been at the laundry longer than {hours} hours.',
    two: '{count} items have been at the laundry longer than {hours} hours.',
    few: '{count} items have been at the laundry longer than {hours} hours.',
    many: '{count} items have been at the laundry longer than {hours} hours.',
    other: '{count} items have been at the laundry longer than {hours} hours.',
  },
  lnNote: 'What went down first is taken to have come back first — linen is not marked, '
    + 'so the age is the age of the balance rather than of any one piece. Sent and back '
    + 'cover the last seven days.',

  plTitle: 'Places',
  plSubtitle: 'Where linen is kept, and what each one is called.',
  plAdd: 'Add a place',
  plName: 'Name',
  plNameAr: 'Arabic name',
  plKind: 'Kind',
  plKinds: {
    STORE: 'Store', PANTRY: 'Floor pantry', LAUNDRY: 'Laundry', TROLLEY: 'Trolley',
  },
  plHolding: 'Items held',
  plPars: 'Pars set',
  plOnFloor: 'floor {floor}',
  plRetired: 'retired',
  plRetire: 'Retire',
  plRestore: 'Restore',
  plRemove: 'Remove',
  plInLedger: 'in {count} movements',
  plCodeWillBe: 'Its code will be {code}. It is typed once and never changes.',
  plNote: 'A place the ledger has ever mentioned can be retired but not removed — every '
    + 'movement ever recorded points at it. Trolleys are listed here but belong to a pair '
    + 'and are created with the team that holds them.',

  itTitle: 'Items',
  itSubtitle: 'The things this hotel counts, and what each one is called.',
  itAdd: 'Add an item',
  itName: 'Name',
  itNameAr: 'Arabic name',
  itKind: 'Kind',
  itKinds: {
    CIRCULATING: 'Comes back', CONSUMABLE: 'Goes with the guest',
  },
  itKindSettled: 'settled by the ledger',
  itKindWarning: 'Whether it comes back can be chosen now and not after anything has '
    + 'moved. It decides whether the hotel still owns one that has gone to a room.',
  itUnit: 'Counted in',
  itUnits: { piece: 'Pieces', pair: 'Pairs', set: 'Sets' },
  itOwned: 'Owned',
  itInStandards: 'in {count} standards',
  itRetired: 'retired',
  itRetire: 'Retire',
  itRestore: 'Restore',
  itRemove: 'Remove',
  itInLedger: 'in {count} movements',
  itCodeWillBe: 'Its code will be {code}. It is typed once and never changes.',
  itNote: 'An item the ledger has ever mentioned can be retired but not removed — every '
    + 'movement ever recorded points at it. Whether it comes back is settled by the first '
    + 'movement, because changing it afterwards would restate what every earlier movement '
    + 'meant.',

  lgTitle: 'Movements',
  lgSubtitle: 'How every figure in this hotel got to be what it is.',
  lgItem: 'Item',
  lgAllItems: 'Every item',
  lgPlace: 'Place',
  lgAnywhere: 'Anywhere',
  lgEitherDirection: 'Everything that touched this place, in either direction.',
  lgWhen: 'When',
  lgWhat: 'What happened',
  lgHowMany: 'How many',
  lgWhereFrom: 'From',
  lgWhereTo: 'To',
  lgWho: 'Who',
  lgToRoom: 'room {room}',
  lgFromACount: 'part of a count',
  lgKinds: {
    RECEIPT: 'Received', TRANSFER: 'Moved', ISSUE: 'Issued to a room',
    RETURN: 'Came back', LOSS: 'Written off', ADJUSTMENT: 'Corrected',
  },
  lgShowing: 'Showing {from} to {to} of {total}',
  lgNewer: 'Newer',
  lgOlder: 'Older',
  lgNothing: 'Nothing has moved',
  lgNothingBody: 'No movement matches what you have chosen. Every balance in this hotel '
    + 'is worked out from this list, so an empty one means the figures are all zero.',
  lgNote: 'Nothing here can be edited or deleted, and that is what makes it worth '
    + 'reading. A mistake is corrected by recording a movement the other way, so the '
    + 'correction is visible beside what it corrected.',

  shTitle: 'When this hotel works',
  shIntro: 'These hours are not a label. A shift\u2019s length is the credit budget of '
    + 'every lane on the assignment board, so shortening one takes work off everybody '
    + 'rostered to it — from the moment you save.',
  shName: 'Shift',
  shNameAr: 'Name in Arabic',
  shStarts: 'Starts',
  shEnds: 'Ends',
  shBudget: 'Credits a lane',
  shWas: 'was {credits}',
  shAdd: 'Add a shift',
  shRetire: 'Switch off',
  shRestore: 'Switch back on',
  shRetired: 'switched off',
  shRostered: 'worked on {count} days',
  shClockShape: 'Write the time as 08:00.',

  close: 'Close',
  rdOpenTask: 'Everything that happened to this work',
  tdTitle: 'The work on {where}',
  tdWhat: 'What it is',
  tdKind: 'Kind of clean',
  tdState: 'Where it has got to',
  tdStates: {
    CREATED: 'Raised, nobody assigned',
    ASSIGNED: 'Assigned',
    STARTED: 'Being cleaned',
    SUBMITTED: 'Handed in, waiting for inspection',
    REWORK: 'Sent back',
    COMPLETED: 'Finished',
    CANCELLED: 'Cancelled',
  },
  tdWorth: 'What it was worth',
  tdCredits: '{credits} cr',
  tdEstimate: 'Expected to take',
  tdRaisedBy: 'Raised by',
  tdBySweep: 'The morning sweep',
  tdByAPerson: 'Somebody asked for it',
  tdWhen: 'When',
  tdStarted: 'First started',
  tdSubmitted: 'Handed in',
  tdCompleted: 'Finished',
  tdNotYet: 'not yet',
  tdFirstStartNote: 'The first start is the FIRST one, and a room that was sent back and '
    + 'cleaned again keeps it. The gap between these two is not how long anybody spent in '
    + 'the room.',
  tdWhoHeld: 'Who has held it',
  tdNobodyHeld: 'Nobody has been given this yet.',
  tdStillHolding: 'still theirs',
  tdGivenBy: 'given by {who}',
  tdChecklist: 'The checklist, as it was answered',
  tdAnswers: { YES: 'Done', NO: 'Not done' },
  tdUnanswered: 'not answered',
  tdLinen: 'Linen',
  tdItem: 'Item',
  tdStandard: 'Standard',
  tdUsed: 'Used',

  sysTitle: 'System',
  sysSubtitle: 'What this server can do, and what the log is about to forget',
  /* **A SENTENCE, NOT A ROW OF TICKS.** A list of green marks is read as "fine" without
     being read at all; a sentence naming the number that is not green gets read. */
  sysAllWell: 'Everything this server depends on is working.',
  sysNotReady: '{count} of the things this server depends on are not ready for live use. '
    + 'They are listed below with what each one is running on.',
  sysChecksTitle: 'What this server is running on',
  sysChecks: {
    identityProvider: 'Who verifies a sign-in',
    fileStorage: 'Where photographs are kept',
    pdfRenderer: 'Printing a report to PDF',
    database: 'The database',
  },
  /* **"NOT READY FOR LIVE USE" AND NOT "WARNING".** A warning is something somebody
     decides whether to act on. This is a statement about whether the hotel can go live,
     which is not a judgement call. */
  sysStates: {
    OK: 'Working',
    PREVIEW: 'Not ready for live use',
    MISSING: 'Not available',
  },
  sysNotes: {
    configuredProvider: 'Verified against the hotel’s own provider.',
    previewProvider: 'A stand-in that issues sign-ins without verifying them — anybody who '
      + 'can reach this address can sign in as anybody. Not to be used with real staff.',
    storageCovered: 'A location the hotel has confirmed survives the server being replaced.',
    /* **THE SILENT ONE.** The row still resolves and the file is simply gone — nobody gets
       an error, weeks later, in an inspection dispute. ERD 10.4. */
    storageLocalDisk: 'This server’s own disk. Nobody has confirmed it is backed up, and a '
      + 'photograph on it goes with the machine — without any error when it does.',
    rendererAvailable: 'Available.',
    rendererMissing: 'There is no browser on this server, so reports can be read on screen '
      + 'but not printed to PDF. The print button says so rather than failing.',
    databaseAnswered: 'It answered a real query, not just the request getting this far.',
    databaseUnreachable: 'It did not answer. What it said was:',
  },
  sysBlockedTitle: 'Waiting on the hotel',
  /* **NOT DEFECTS AND NOT OURS TO CLOSE.** A health screen showing only the parts we
     control would read as "everything is fine" on a server that cannot verify a token. */
  sysBlockedBody: 'These are not faults in the product. They are decisions and details '
    + 'still owed from the hotel’s side before this server can be used for real work.',
  sysRetentionTitle: 'How long the log is kept',
  sysRetentionBody: 'Every override, every permission change and every correction is kept '
    + 'for {months} months, then removed.',
  sysOldest: 'Oldest entry still held',
  /* **"NOTHING HAS BEEN WRITTEN" IS NOT A DATE.** Somebody looking for last March needs to
     tell "it was removed" from "it never happened". */
  sysLogEmpty: 'Nothing has been written yet',
  sysHeld: 'Entries held',
  sysExpiring: 'Entries removed in the next 30 days',
  sysRetentionNote: 'This period is fixed. It is not changeable from a screen on purpose — '
    + 'lowering it is a way to remove months of evidence with one number, and the people '
    + 'who would have wanted those entries are the people who would not be told.',
  sysAboutTitle: 'About this server',
  sysEnvironment: 'Running as',
  sysVersion: 'Version',
  sysServerTime: 'Server clock',
  sysHotelZone: 'Hotel time zone',
  /* **BOTH CLOCKS.** They are three hours apart for a quarter of every day, and every
     screen in this product asks for the second one — #170 was exactly this gap. */
  sysHotelDate: 'Hotel date now',

  dvTitle: 'Devices',
  dvSubtitle: 'What is signed in, and from what kind of thing',
  dvWhatThisIs: 'Read this before acting on it',
  /* **THE SENTENCE THE SCREEN EXISTS TO SAY.** Without it, a plausible list of devices is
     read as a true one, and somebody ends the wrong person's shift. */
  dvNoDeviceIdentity:
    'This is not a list of devices. Nothing here gives a phone or a computer an identity — '
    + 'all that is known is what each sign-in said about itself, which can be wrong and is '
    + 'the same for two identical handsets. A line below is one person and one kind of '
    + 'thing, not one device.',
  dvWhatItIsFor:
    'What it is good for: finding something still signed in that should not be — after '
    + 'somebody leaves, after a handset goes missing, at the end of a season.',
  dvHeadlineSessions: {
    one: '{count} sign-in is open.',
    two: '{count} sign-ins are open.',
    few: '{count} sign-ins are open.',
    many: '{count} sign-ins are open.',
    other: '{count} sign-ins are open.',
  },
  dvHeadlineAcross: {
    one: 'It belongs to {count} person.',
    two: 'They belong to {count} people.',
    few: 'They belong to {count} people.',
    many: 'They belong to {count} people.',
    other: 'They belong to {count} people.',
  },
  dvStale: {
    one: '{count} has not been used for over a day.',
    two: '{count} have not been used for over a day.',
    few: '{count} have not been used for over a day.',
    many: '{count} have not been used for over a day.',
    other: '{count} have not been used for over a day.',
  },
  dvGroupings: {
    one: '{count} of the lines below holds more than one sign-in. It could be one thing '
      + 'signed in more than once or several things — there is no way here to tell them apart.',
    two: '{count} of the lines below hold more than one sign-in. Each could be one thing '
      + 'signed in more than once or several things — there is no way here to tell them apart.',
    few: '{count} of the lines below hold more than one sign-in. Each could be one thing '
      + 'signed in more than once or several things — there is no way here to tell them apart.',
    many: '{count} of the lines below hold more than one sign-in. Each could be one thing '
      + 'signed in more than once or several things — there is no way here to tell them apart.',
    other: '{count} of the lines below hold more than one sign-in. Each could be one thing '
      + 'signed in more than once or several things — there is no way here to tell them apart.',
  },
  dvSignedIn: 'Open sign-ins',
  dvManySessions: {
    one: '{count} sign-in on this line',
    two: '{count} sign-ins on this line',
    few: '{count} sign-ins on this line',
    many: '{count} sign-ins on this line',
    other: '{count} sign-ins on this line',
  },
  dvSince: 'first signed in {when}',
  dvDaysAgo: {
    one: '{count} day ago',
    two: '{count} days ago',
    few: '{count} days ago',
    many: '{count} days ago',
    other: '{count} days ago',
  },
  dvUnknownNote: {
    one: '{count} sign-in did not say anything recognisable about itself. It is listed as '
      + 'unknown rather than guessed at.',
    two: '{count} sign-ins did not say anything recognisable about themselves. They are '
      + 'listed as unknown rather than guessed at.',
    few: '{count} sign-ins did not say anything recognisable about themselves. They are '
      + 'listed as unknown rather than guessed at.',
    many: '{count} sign-ins did not say anything recognisable about themselves. They are '
      + 'listed as unknown rather than guessed at.',
    other: '{count} sign-ins did not say anything recognisable about themselves. They are '
      + 'listed as unknown rather than guessed at.',
  },
  dvEnd: 'End',
  dvEndAll: 'End all',
  dvNobody: 'Nobody is signed in',
  dvNobodyBody: 'No sign-in is open anywhere in this hotel right now.',

  propButton: 'Suggest who takes what',
  propHeadline: 'Suggested: {count} rooms.',
  propNothing: 'Nothing could be suggested.',
  /* **THE SENTENCE THAT MAKES IT A PROPOSAL.** Without it this is a list on a board that
     otherwise shows what is true, and the difference is who is accountable in the morning. */
  propNothingMoved: 'Nothing has moved yet — this is a suggestion.',
  propRow: '{room} → {name}',
  propOffFloor: '(not on their floor)',
  propUnplaced: '{count} could not be given to anybody: {rooms}.',
  propUnplacedWhy: {
    NOT_ASSIGNABLE: 'Nobody has said how these rooms are cleaned yet.',
    NOBODY_FREE: 'Everybody on shift is already at their hours.',
    /* **CQ-145's OWN SENTENCE, AND IT NAMES THE FIX.** Somebody on this board has hours
       left — he is just not working that floor, and rooms are not offered across floors.
       "Everybody is at their hours" would be a plain untruth in front of a half-empty
       lane, and a supervisor who catches the screen in one stops believing the rest. */
    NOBODY_FREE_ON_FLOOR: 'The people working those floors are at their hours. Somebody '
      + 'else has time, but rooms are only suggested to whoever is working the floor — '
      + 'put a hand on the floor, or move a card across yourself.',
    NOBODY_ON_SHIFT: 'Nobody is on shift to take them.',
  },
  propIdle: 'This would leave {names} with nothing to do. Rooms are only suggested to '
    + 'whoever is already working that floor, so anybody not on a floor yet is offered '
    + 'nothing. Put them on a floor, or move a card yourself.',
  propApprove: 'Assign these',
  propApplying: 'Assigning {done} of {total}',
  propDiscard: 'Discard',

  ntfTitle: 'What happened',
  ntfSubtitle: 'What other people did to your work',
  ntfSentence: {
    ROOM_CAME_BACK: '{who} sent {room} back to you.',
    WORK_TAKEN_OFF_YOU: '{who} took {room} off you.',
    WORK_GIVEN_TO_YOU: '{who} gave you {room}.',
    YOUR_FAULT_WAS_CLOSED: '{who} closed the fault you reported in {room}.',
    ADJUSTMENT_NEEDS_YOU: '{who} is waiting for you to decide a correction: {room}.',
    YOUR_ADJUSTMENT_WAS_DECIDED: '{who} decided your correction: {room}.',
  },
  /* **NOT "THE SYSTEM".** Every one of these acts had a person behind it, and the one case
     where the name is missing is a person whose account has since been removed — not a
     machine, and saying "the system" would teach a reader that some of them are. */
  ntfSomebody: 'Somebody',
  ntfARoom: 'a room',
  ntfUnread: '{count} since you last looked.',
  ntfAllSeen: 'Nothing new since you last looked.',
  ntfMarkSeen: 'Mark as seen',
  /* "Nothing has happened" and "you are up to date" are the same place for a man who has
     never opened this, and the sentence that fits both is about the hotel, not about him. */
  ntfNothingTitle: 'Nothing has happened to your work',
  ntfNothingBody: 'Nobody has moved your rooms, sent one back or closed a fault you '
    + 'reported in the last fortnight.',
  /* **CORRECTED IN SPRINT 82, BECAUSE THE ROW ABOVE IT MADE IT UNTRUE.** CQ-42 put a
     correction waiting for YOUR decision on this screen, which is not something done to
     your work — and a footer that denied the line above it would be the screen arguing
     with itself in front of the reader. */
  ntfScope: 'This covers the last fortnight: what other people did to your work, and '
    + 'anything waiting on a decision from you. What is outstanding right now is on the '
    + 'boards, not here.',

  /* ── W-10 · THE OPERATIONS DASHBOARD (Sprint 61) ──────────────────────────
     **THE HEADLINE IS A SENTENCE AND THERE ARE FOUR OF THEM**, because there are four
     genuinely different mornings and each has a different next action. A single figure
     with a colour would have invented the threshold between them — and the fourth,
     "cannot be covered", is the one a manager has to act on outside this product. */
  opsTitle: 'Today',
  /* **NOT THE QUESTION AGAIN.** The card below asks it in the reader's eyeline; a
     header repeating the same five words is the screen saying one thing twice before
     it has said anything. This says what the screen IS. */
  opsSubtitle: 'Where the hotel stands right now',
  opsQuestion: 'Can we cover today?',
  opsArrivals: {
    one: '{count} arrival',
    two: '{count} arrivals',
    few: '{count} arrivals',
    many: '{count} arrivals',
    other: '{count} arrivals',
  },
  opsReady: {
    one: '{count} room ready',
    two: '{count} rooms ready',
    few: '{count} rooms ready',
    many: '{count} rooms ready',
    other: '{count} rooms ready',
  },
  opsNoArrivals: 'Nobody is arriving today',
  opsNoArrivalsBody:
    'Today is departures and stayovers. The rooms below still have to be turned round for '
    + 'tomorrow.',
  opsCovered: 'Today is covered',
  opsCoveredBody: 'Every arrival has a room ready for it. Keep the floors moving.',
  opsShort: {
    one: '{count} more room has to become ready',
    two: '{count} more rooms have to become ready',
    few: '{count} more rooms have to become ready',
    many: '{count} more rooms have to become ready',
    other: '{count} more rooms have to become ready',
  },
  /* **THE DEADLINE IS AN ARRIVAL, NEVER A CHECK-IN HOUR.** Nothing in this product holds
     one, and a standard check-in time printed on a manager's screen is the sort of figure
     that gets believed. */
  opsShortBy: 'The first arrival with no room waiting is at {time}.',
  opsShortNoTime: 'No arrival today carries a time, so this is how many rather than by when.',
  opsCannot: {
    one: '{count} room short, and it cannot be found on the floors',
    two: '{count} rooms short, and they cannot be found on the floors',
    few: '{count} rooms short, and they cannot be found on the floors',
    many: '{count} rooms short, and they cannot be found on the floors',
    other: '{count} rooms short, and they cannot be found on the floors',
  },
  opsCannotBody:
    'Cleaning everything that can be cleaned still leaves the hotel short. This one is '
    + 'settled with the front desk, not on this screen.',
  opsRecoverable: {
    one: '{count} could still become ready today',
    two: '{count} could still become ready today',
    few: '{count} could still become ready today',
    many: '{count} could still become ready today',
    other: '{count} could still become ready today',
  },
  opsRooms: 'Rooms',
  opsDepartures: 'Departures',
  opsDeparturesNote: 'of {expected} expected',
  opsOpenTasks: 'Open work',
  opsRunning: 'Being cleaned',
  /* **NOT "OVERDUE", AND NOT "WAITING FOR INSPECTION" EITHER — #163.**

     Not "overdue", because a task in this product has no due time and nothing can be late
     against one. What this counts is a queue caused by a person: rooms a man has handed in
     that nobody has inspected yet. That much was right in the first draft.

     What was wrong was the words. The panel below draws the room board's own
     "Waiting for inspection" — every room that is clean in a hotel that inspects — and the
     first render of this screen put "Waiting for inspection 1" three inches above
     "Waiting for inspection 4". They are two true counts of two different sets, and one
     label. **The handset calls its identical tile "Inspect"**, so this says the same. */
  opsToInspect: 'To inspect',
  opsWaitingOldest: 'Oldest {minutes}',
  opsWaitingNone: 'Nothing waiting',
  opsFaults: 'Open faults',
  opsBlocking: 'Blocking',
  opsNotReady: 'Not ready, and why',
  opsAllReady: 'Every room in the hotel is ready.',
  /* The room board's own words for the same six states, so the two screens cannot name
     one thing differently. */
  opsReason: {
    AWAITING_CLEAN: 'Waiting to be cleaned',
    BEING_CLEANED: 'Being cleaned',
    AWAITING_INSPECTION: 'Waiting for inspection',
    OUT_OF_SERVICE: 'Out of service',
    OUT_OF_ORDER: 'Out of order',
    STILL_OCCUPIED: 'Guest still in',
  },
  opsFloors: 'Who is on the floors',
  opsNobodyHolding: 'Nobody is holding rooms',
  opsNobodyHoldingBody: "Today's work has not been given out yet.",
  opsLaneDone: 'Done',
  opsLaneIn: 'In {room}',
  opsLaneRework: '{count} back',
  opsLaneLoad: '{credits} of {budget}',
  opsLaneNoBudget: 'Not on today\u2019s roster',
  opsDecisions: 'Needs a decision',
  opsAsAt: 'As at {time}',
  /* ── W-129 ─────────────────────────────────────────────────────────────────
     **THE INTRO SAYS WHOSE WORDS THESE ARE.** Every Arabic name a hotel finds in this
     product on its first morning was written by us, and a settings screen that did not
     admit that would be quoting the client back to itself. */
  cfgTitle: 'Settings',
  cfgSubtitle: 'The words this hotel uses, and what a guest may ask for',
  cfgIntro:
    'The Arabic on every screen was written by us and is meant to be corrected. Change '
    + 'anything here and it changes everywhere — the sidebar, the reports, the handsets.',
  cfgHotel: 'This hotel',
  cfgName: 'Name',
  cfgNameAr: 'Name in Arabic',
  cfgNameArHint: 'Leave it empty if the English name is the name in both languages.',
  cfgCode: 'Short code',
  /* SHOWN AND NOT OFFERED. It is what a person says out loud to tell two hotels apart,
     and renaming it has consequences this screen cannot explain. */
  cfgCodeHint: 'Set when the hotel was added. Ring us if it needs to change.',
  cfgTimeZone: 'Time zone',
  cfgTimeZoneHint:
    'Everything the hotel calls “today” comes from this — the board, the roster and every '
    + 'report.',
  cfgLanguage: 'Screens open in',
  cfgLanguageHint: 'What somebody sees before they choose for themselves.',
  cfgInspection: 'Does a cleaned room wait to be inspected?',
  cfgInspectionHint: 'This decides when a room can be sold, not just who checks it.',
  cfgInspectionYes: 'Yes — a supervisor looks at it first',
  cfgInspectionNo: 'No — it is ready when the attendant says so',
  /* **WHAT TURNING IT OFF ACTUALLY DOES**, said where the choice is made rather than
     discovered a fortnight later by somebody wondering why nothing is ever inspected. */
  cfgInspectionOffWarning:
    'With this off, a room goes on sale on one person\u2019s word and the work is recorded as '
    + 'finished the moment they hand it in. Nobody checks it, and the inspection screens '
    + 'and reports will be empty because there will be nothing to put in them.',
  cfgLanguageEn: 'English',
  cfgLanguageAr: 'Arabic',
  cfgSaveHotel: 'Save the hotel’s details',
  cfgHotelSaved: 'Saved.',
  cfgRoles: 'What the roles are called',
  cfgRolesIntro:
    'The names only. What each role may do is on the roles and permissions screen, and '
    + 'nothing here changes it.',
  cfgColRole: 'Role',
  cfgColName: 'Name',
  cfgColNameAr: 'Name in Arabic',
  cfgColHeld: 'Held by',
  cfgHeld: {
    one: '{count} person',
    two: '{count} people',
    few: '{count} people',
    many: '{count} people',
    other: '{count} people',
  },
  cfgNobodyHolds: 'Nobody here',
  cfgRoleSaved: 'Saved.',
  cfgSave: 'Save',
  cfgSaving: 'Saving…',

  /* ── W-129, SECOND CARD (Sprint 56) ────────────────────────────────────────
     **THE INTRO SAYS WHERE THIS LIST CAME FROM**, because the answer changes what somebody
     does with it. "These are the kinds we started you with" invites correction; a list with
     no provenance reads as a decision somebody already made. */
  rcTitle: 'What a guest may ask for',
  rcIntro:
    'These are the kinds of request the front desk and the QR code in each room can offer, '
    + 'and how long each one is promised in. We started you with six; they are a guess, and '
    + 'this is where you make them yours.',
  rcColCode: 'Short code',
  rcColName: 'Name',
  rcColNameAr: 'Name in Arabic',
  rcColPromise: 'Promised in',
  rcColCountable: 'How many?',
  rcColStock: 'Takes from stock',
  rcColAsked: 'Asked for',
  rcMinutes: {
    one: '{count} minute',
    two: '{count} minutes',
    few: '{count} minutes',
    many: '{count} minutes',
    other: '{count} minutes',
  },
  rcNeverAsked: 'Never',
  rcAsked: {
    one: '{count} time',
    two: '{count} times',
    few: '{count} times',
    many: '{count} times',
    other: '{count} times',
  },
  rcNoStock: 'Nothing',
  rcCountableYes: 'Yes — a number is asked for',
  rcCountableNo: 'No',
  rcCountableLabel: 'Ask how many',
  rcRetired: 'Switched off',
  rcRetire: 'Switch off',
  rcRestore: 'Switch back on',
  rcDelete: 'Remove',
  /* **THE HINT IS WHY, NOT WHAT.** A disabled button with no explanation is a screen that
     looks broken; this one says the thing the person needs to know. */
  rcKeepHint:
    'A kind of request guests have already asked for stays in your reports and cannot be '
    + 'removed — switch it off instead, and it stops being offered from then on. One that '
    + 'has never been asked for can be removed entirely.',
  rcAdd: 'Add a kind of request',
  rcAddTitle: 'A new kind of request',
  rcAddIntro:
    'The short code is what every report counts by, and it can never be changed afterwards. '
    + 'Everything else can.',
  rcCode: 'Short code',
  rcCodeHint: 'Capital letters, digits and underscores — PRAYER_MAT. Permanent.',
  rcCodeSuggested: 'Suggested from the name. Change it now if you want something else.',
  rcCodeBad: 'Capital letters, digits and underscores, starting with a letter.',
  rcPromise: 'Promised in',
  rcPromiseHint: 'Minutes. Your other kinds are between 30 and 60.',
  rcAddSave: 'Add it',
  rcCancel: 'Cancel',
  rcConfirmDelete: 'Remove {name}? Nobody has asked for it, so nothing is lost.',

  /* ── W-128 (Sprint 59) ──────────────────────────────────────────────────────
     **THE INTRO ADMITS WHOSE WORDS THESE ARE**, because that is what tells somebody they
     are allowed to change them. A screen headed "Translations" with no explanation reads
     as a list of settings rather than as a list of our guesses. */
  trTitle: 'Arabic names',
  trSubtitle: 'Everything this system named, and what you call it',
  trIntro:
    'Every Arabic name below was written by us. They are our best guess at what this hotel '
    + 'calls each thing, and none of them is a decision you made. Change any of them and it '
    + 'changes everywhere — the screens, the handsets and the reports. The English is what '
    + 'the system calls it and stays as it is.',
  trRemaining: {
    one: '{count} still to do',
    two: '{count} still to do',
    few: '{count} still to do',
    many: '{count} still to do',
    other: '{count} still to do',
  },
  trAllDone: 'Every name has an Arabic one.',
  trNothingToDo: 'Nothing has been loaded yet, so there is nothing here to name.',
  trColCode: 'Short code',
  trColName: 'What the system calls it',
  trColNameAr: 'What you call it',
  trGroupEmpty: 'None of these yet.',
  trSave: 'Save',
  trSaving: 'Saving…',
  /* **SAID ONCE, AT THE BOTTOM.** Four kinds of word live on the Settings screen instead,
     because there the ENGLISH can be changed too — and somebody who cannot find "Room
     attendant" here should be told where it is rather than left hunting. */
  trElsewhere:
    'The hotel’s own name, what the roles are called and the kinds of guest request are on '
    + 'the Settings screen, because there you can change the English as well. A checklist '
    + 'question is reworded by publishing a new version of its template.',
  trKind: {
    BUILDING: 'Buildings',
    ZONE: 'Zones',
    ROOM_TYPE: 'Room types',
    AREA: 'Public areas',
    STOCK_ITEM: 'Stock items',
    STOCK_LOCATION: 'Stock locations',
    TASK_TYPE: 'Kinds of work',
    SHIFT: 'Shifts',
    TEMPLATE: 'Checklist templates',
  },
  /** **THE NOUN IS IN THE STRING**, because "40" alone under "Room for four" reads as a
   *  price. Arabic needs five forms of it and English two, which is why this is a plural
   *  record rather than a template with a number in it. */
  trInUseFloors: {
    one: '{count} floor', two: '{count} floors', few: '{count} floors',
    many: '{count} floors', other: '{count} floors',
  },
  trInUseAreas: {
    one: '{count} area', two: '{count} areas', few: '{count} areas',
    many: '{count} areas', other: '{count} areas',
  },
  trInUseRooms: {
    one: '{count} room', two: '{count} rooms', few: '{count} rooms',
    many: '{count} rooms', other: '{count} rooms',
  },

  /* ── W-135 ─────────────────────────────────────────────────────────────────
     **THE INTRO SAYS WHAT THE SCREEN IS FOR AND THEN GETS OUT OF THE WAY.** It is read
     once, by somebody who has been handed a job nobody wanted, and what they need to know
     is that the order matters and the counts are live. */
  glTitle: 'Go-live data',
  glSubtitle: 'What has to be loaded before the first shift runs',
  glIntro:
    'In the order it has to be done. The counts are this hotel’s, now — say how many '
    + 'there should be and a row can tell you when it is finished.',
  glProgress: '{done} of {total} finished',
  glColWhat: 'What',
  glColSource: 'How it gets in',
  glColExpected: 'Should be',
  glColLoaded: 'Loaded',
  glColDepends: 'Needs first',
  glColState: 'State',
  glStep: {
    PROPERTIES_AND_FLOORS: 'Buildings and floors',
    ROOM_TYPES: 'Kinds of room',
    ROOMS: 'Rooms',
    PUBLIC_AREAS: 'Public areas',
    CHECKLIST_TEMPLATES: 'Checklists',
    CLEANING_RULES_PER_ROOM: 'Cleaning rules per room',
    STOCK_ITEMS: 'Linen and consumables',
    STOCK_LOCATIONS: 'Stores and pantries',
    OPENING_STOCK_COUNT: 'Opening stock count',
    PEOPLE_AND_ROLES: 'People and roles',
    FIRST_ROSTER: 'First roster',
  },
  glSource: {
    BY_HAND: 'Typed in',
    SPREADSHEET: 'Spreadsheet',
    IN_BULK: 'Applied in bulk',
    /* NAMED FOR THE WORK, not the screen. This is the one row that needs somebody
       walking round the hotel, and the pack says to book it rather than discover it. */
    PHYSICAL_COUNT: 'Counted on foot',
  },
  glState: {
    DONE: 'Done',
    SHORT: 'Short',
    /* **NOT "DONE".** Something is here and nobody has said how much there should be. */
    LOADED: 'Loaded',
    NOT_STARTED: 'Not started',
    WAITING: 'Waiting',
    BLOCKED: 'Blocked',
  },
  glMissing: {
    one: '{count} missing',
    two: '{count} missing',
    few: '{count} missing',
    many: '{count} missing',
    other: '{count} missing',
  },
  glSignInNote:
    'People and roles waits on how sign-in is verified, which is still an open question '
    + 'with us. Everybody on the list can be given a role today; nobody can be verified '
    + 'when they sign in.',
  glNoTargetNote:
    'Rows reading “Loaded” have something in them and no figure to check against. Say how '
    + 'many there should be and they can say when they are finished.',
  glWorkedOut: 'Worked out',
  glTargetLabel: 'How many {what} should there be?',
  glNothingYet: 'None yet',
  glDays: {
    one: '{count} day',
    two: '{count} days',
    few: '{count} days',
    many: '{count} days',
    other: '{count} days',
  },

  /* **CQ-142 — THE SENTENCE THAT MAKES THIS CARD WORTH ITS PLACE.** "Runs out in a day" is
     a fact a supervisor already has by looking at the shelf. "And washing what you have
     will not get it back in time" is a decision: somebody buys, or borrows from the other
     pantry, this morning. It names the 24 hours because a supervisor who is being told to
     act is entitled to the number the product is reasoning from. */
  laundryTooSlow: 'The laundry takes about 24 hours — washing what you have will not cover this.',
  alert: {
    soldRoomOutOfOrder: 'Sold room out of order',
    requestsLate: {
      one: '{count} request is late',
      two: '{count} requests are late',
      few: '{count} requests are late',
      many: '{count} requests are late',
      other: '{count} requests are late',
    },
    repeatFailure: 'The same thing failed twice',
    stockBelowPar: '{item} is below par',
  },
  issuesEmpty: 'Nothing reported.',
  requestsTitle: 'Guest requests',
  requestsEmpty: 'Nothing waiting.',
  severities: { MINOR: 'Minor', MAJOR: 'Major', BLOCKING: 'Blocking' },
  issuePhotos: 'Photos',
  issuePhotoCount: '{n}',
  issuePhotoTaken: 'taken {when}',
  issuePhotoBy: 'by {name}',
  issuePhotoFailed: 'This photo is recorded but the file is missing from storage. '
    + 'Report it with reference {correlationId}.',
  issuePhotoClose: 'Close',
  requestCategories: {
    EXTRA_TOWELS: 'Extra towels', EXTRA_PILLOW: 'Extra pillow',
    TURNDOWN: 'Turndown service', AMENITIES: 'Amenities', OTHER: 'Other',
  },
  issueTookRoomOut: 'This room is out of order because of this.',
  issueNeedsOtherRole: 'Clearing a blocking fault needs a different role.',
  resolve: 'Resolve',
  fulfil: 'Done',
  late: 'Late',
  reasonTitle: 'Say why',
  reasonBody: 'This puts the room back on sale, so it is recorded with your name.',
  save: 'Save',
  stockOwnedTitle: 'What the hotel has',
  stockOwnedSubtitle: 'Linen stays ours when it goes to a room. Consumables do not.',
  stockWhereTitle: 'Where it is',
  stockItem: 'Item',
  stockLocation: 'Place',
  stockQuantity: 'Here',
  stockPar: 'Par',
  stockOwned: 'Owned',
  stockInLocations: 'In store',
  stockInRooms: 'In rooms',
  stockCirculating: 'linen',
  stockConsumable: 'consumable',
  stockAllLocations: 'Everywhere',
  stockBelowPar: '{count} below par.',
  stockEmpty: 'Nothing recorded here yet.',
  stockTitle: 'Linen and stock',

  rosterTitle: 'Roster',
  rosterSubtitle: 'Who is working, and when — Saturday to Friday.',
  rosterEmpty: 'Nobody at this property works a housekeeping shift yet.',
  rosterToday: 'Today',
  rosterThisWeek: 'This week',
  rosterPrevWeek: 'Previous week',
  rosterNextWeek: 'Next week',
  rosterPerson: 'Who',
  rosterUnset: '—',
  rosterUnsetHint: 'Not set',
  rosterHolding: '{count} open',
  rosterHoldingWarning:
    '{name} is holding {count} open tasks — that work stays in their lane and has to be '
    + 'moved by hand.',
  rosterPickShift: 'Which shift?',
  rosterCoverage: 'Covering (optional)',
  rosterCoveragePlaceholder: 'Floors 3–5',
  rosterSave: 'Save',
  rosterCancel: 'Cancel',
  rosterSaved: 'Saved.',
  rosterUnwritten: '{count} of this week is still unwritten.',
  reportsTitle: 'Reports',
  reportsSubtitle: 'Read it on screen, then print the same document.',
  reportsPick: 'Which report?',
  reportsNone: 'No reports are open to you.',
  reportsNoneBody:
    'Reports are granted by role. If you were expecting one here, ask whoever set up '
    + 'your account — nothing is broken.',
  reportsFrom: 'From',
  reportsTo: 'To',
  reportsLanguage: 'Written in',
  reportsRun: 'Run',
  reportsDownload: 'Download PDF',
  reportsPreview: 'The report',
  loadTitle: 'Load rooms',
  loadSubtitle: 'Put the hotel’s own floors, room types and rooms in from a spreadsheet.',
  loadIntro:
    'Upload the hotel’s room list and this screen will show exactly what loading it '
    + 'would do. Nothing is written until you say so.',
  loadColumns:
    'The file needs a row of column names, and three of them: room, floor and type. '
    + 'Add a building column if the hotel has more than one building — without it there '
    + 'is no way to tell one building’s third floor from another’s. Type name, the '
    + 'Arabic name and clean minutes are used if they are there. '
    + 'Send .xlsx if you can — a CSV has to be saved as “CSV UTF-8” to keep Arabic.',
  loadChoose: 'Choose a file',
  loadChooseAnother: 'Choose a different file',
  loadReading: 'Reading it…',
  loadWouldHappen: 'What this would do',
  loadNewRooms: 'Rooms added',
  loadMovedRooms: 'Rooms moved',
  loadUnchangedRooms: 'Rooms unchanged',
  loadNotInFile: 'In the hotel, not in this file',
  loadNotInFileNote: 'Left exactly as they are. Nothing here closes a room.',
  loadFloorsAndTypes: '{floors} new floors · {types} new room types · {changed} renamed',
  loadBuildingsNew: '{count} new buildings',
  loadLeftOutOfUse: '{count} out of use, left that way',
  loadConverted: '{count} rows had Arabic numerals, read as Western digits',
  loadCommit: 'Load them',
  loadLoading: 'Loading…',
  loadFixFirst: 'Fix the lines below and upload the file again — it is all or nothing.',
  loadDone: 'Loaded. {rooms} rooms added, {moved} moved.',
  loadProblems: '{count} to put right',
  loadLine: 'Line {line}',
  loadRooms: '{count} rooms in the file',
  loadColRoom: 'Room',
  loadColBuilding: 'Building',
  loadTypes: '{count} kinds of room in the file',
  loadColTypeName: 'Name',
  loadColSleeps: 'Sleeps',
  loadColMinutes: 'Minutes',
  loadColRooms: 'Rooms',
  loadColFloor: 'Floor',
  loadColType: 'Type',
  loadColChange: '',
  loadChanges: {
    NEW: 'New',
    CHANGED: 'Changed',
    UNCHANGED: 'Unchanged',
    LEFT_OUT_OF_USE: 'Left out of the hotel',
  },

  /* ── one room at a time ─────────────────────────────────────────────────
     THE WORDS HERE ARE THE DESIGN. "Out of the hotel" is not "out of service": the
     second is a room that exists and is shut for repair, lives on the room board and is
     set by a supervisor. Two ways to switch a room off, in a product where nobody can
     tell which one they are looking at, is the whole defect. Every string on this screen
     says "the hotel" for that reason. */
  setupTitle: 'Rooms and floors',
  setupSubtitle: 'The shape of the hotel: which rooms it has, on which floors, of which type.',
  editTab: 'One room at a time',
  loadTab: 'From a spreadsheet',
  editTitle: 'The hotel’s rooms',
  /* NOT "the hotel's rooms" a second time. A card heading that repeats the card above it
     reads as a mistake on screen — and it was one here, exactly as the sidebar's
     "Reports" above "Reports" was in Sprint 19. */
  editListTitle: '{count} rooms',
  /* SAID BEFORE THE ACT, not only after it. The reprint notice after saving is the
     record; this is the warning, in front of somebody who has not typed the new number
     yet and can still decide it is not worth the trip upstairs. */
  editCardHint: 'There is a card in this room. Changing the number means printing a new one.',
  editIntro:
    'The day 305 is renumbered, or a room is knocked through and stops existing. '
    + 'Taking a room out of the hotel is not the same as putting it out of service — '
    + 'a room out of service is still one of the hotel’s rooms and still on the board.',
  editSearch: 'Find a room',
  editAdd: 'Add a room',
  editShowRemoved: 'Show rooms that are out of the hotel',
  editNoMatch: 'No room matches that.',
  editEmpty: 'This hotel has no rooms yet. Load them from a spreadsheet first.',
  editColStatus: '',
  cleanColDeparture: 'Departure clean',
  cleanColStayover: 'Stayover clean',
  cleanColMinutes: 'Minutes',
  /* WHAT THE NUMBER MEANS, not what to type. A supervisor filling this in for a suite
     needs to know it is the DEPARTURE clean they are timing and that the rest follows
     from it — otherwise they will reasonably wonder where to put the stayover. */
  cleanMinutesHint: 'A departure clean. Other work scales from it.',
  /* THE VALUE IS IN THE LABEL. "Credits" alone would make somebody press it to find out
     what it says; "1 credit = 20 minutes" answers the question without the press, which
     is what the control is mostly for. */
  divisorCurrent: '1 credit = {minutes} min',
  divisorTitle: 'What one credit is worth',
  /* THE CONSEQUENCE FIRST. This is the only control on the screen that changes every
     lane in the hotel, and a person about to press it should know that before they read
     what the field is called. */
  divisorIntro:
    'Every lane on the assignment board is counted in credits, and this is how many '
    + 'minutes of work one credit is. Changing it re-prices every room in the hotel at '
    + 'once — no room takes any longer, but every figure a supervisor balances a round '
    + 'against will move.',
  divisorLabel: 'Minutes per credit',
  divisorHint: 'Between 5 and 120.',
  /* A REAL ROOM, BEFORE AND AFTER. "2.25 becomes 3" is a fact somebody can decide
     against; "the divisor is 15" is not. */
  divisorPreview:
    'Room {room} takes {minutes} minutes, so it would go from {was} credits to {now}.',
  divisorSaved: 'One credit is now {minutes} minutes of work.',
  cleanColCredits: 'Credits',
  cleanColDeepClean: 'Deep clean',
  cleanNotSet: 'Not set',
  cleanNeverPublished: 'nothing published yet',
  cleanUnknownTemplate: 'A checklist that is no longer in use',
  cleanEdit: 'How it is cleaned',
  /* THE CONSEQUENCE, NOT THE CONDITION. "3 rooms have no cleaning rules" is a tidiness
     problem; "3 rooms cannot be given to anyone" is why somebody should stop and read.

     AND IT AGREES WITH ITS OWN NUMBER. It read "1 rooms cannot be given to anyone" on
     the first screenshot — the commonest case there is, since a room arrives unconfigured
     one at a time after go-live. The English half repeats itself across two/few/many so
     both dictionaries hold the same keys and the guard comparing them stays useful. */
  cleanUnconfigured: {
    /* NO `{count}` IN THE ONE AND TWO FORMS, IN EITHER LANGUAGE, and the placeholder
       guard is what made that a decision rather than an accident.

       Arabic names one and two IN THE NOUN — غرفة واحدة, غرفتان — so repeating the
       numeral would read as "1 one room". English does not have to follow, but it reads
       better when it does, and matching keeps both dictionaries holding the same
       placeholders, which is the only reason the guard can compare them at all.
       (`two` is never selected for English; it is here so the shapes match.) */
    one: 'One room cannot be given to anyone until somebody says how it is cleaned.',
    two: 'Two rooms cannot be given to anyone until somebody says how they are cleaned.',
    few: '{count} rooms cannot be given to anyone until somebody says how they are cleaned.',
    many: '{count} rooms cannot be given to anyone until somebody says how they are cleaned.',
    other: '{count} rooms cannot be given to anyone until somebody says how they are cleaned.',
  },
  cleanFixThem: 'Set them now',
  cleanTitleOne: 'How room {room} is cleaned',
  cleanTitleMany: 'How {count} rooms are cleaned',
  cleanIntro:
    'The checklist an attendant is given, what the room is worth on a round, and how '
    + 'often it gets a deep clean. A room with no checklist and no credit cannot be '
    + 'assigned at all.',
  cleanIntroMany:
    'Anything you leave blank is left as it is on each room. Filling a box sets it on '
    + 'every room in this list.',
  cleanApplyDefaults: 'Apply the {type} defaults',
  cleanNoDefaults: 'This room type has no defaults set yet.',
  cleanChoose: 'Choose a checklist',
  cleanLeaveAlone: 'Leave as it is',
  cleanNotes: 'Anything unusual about cleaning this room',
  cleanSaved: 'Saved.',
  cleanNowAssignable: '{count} rooms can now be given to somebody.',
  deepClean: {
    NONE: 'Not scheduled',
    MONTHLY: 'Monthly',
    QUARTERLY: 'Quarterly',
    TWICE_YEARLY: 'Twice a year',
    YEARLY: 'Yearly',
  },
  editColCard: 'Card',
  editInHotel: 'In the hotel',
  editOutOfHotel: 'Out of the hotel',
  editHasCard: 'Has a card',
  editEdit: 'Edit',
  editRemove: 'Take out of the hotel',
  editRestore: 'Put back in the hotel',
  editSave: 'Save',
  editSaving: 'Saving…',
  editCancel: 'Cancel',
  editNewRoom: 'A new room',
  editRoomNumber: 'Room number',
  editRoomNumberHint: 'As it is painted on the door.',
  editFloor: 'Floor',
  editType: 'Room type',
  editAddFloor: 'Add a floor',
  editFloorCode: 'Floor',
  editAddBuilding: 'Add a building',
  typesTitle: 'Kinds of room',
  typesName: 'Name',
  typesSleeps: 'Sleeps',
  typesSleepsNote:
    'How many people the room holds. It is what the linen standard for this kind of room '
    + 'is written against, so changing it changes what goes through every one of these '
    + 'doors.',
  typesMinutes: 'Minutes',
  typesMinutesNote:
    'The starting point for a new room of this kind. Rooms that already exist keep the '
    + 'time they were given.',
  typesRooms: 'Rooms',
  typesEditTitle: 'Kind of room',

  linenTitle: 'What a clean needs',
  linenSubtitle: 'What each kind of room needs for each kind of clean.',
  linenIntro:
    'This is the list an attendant is shown before opening a door, and the figure a '
    + 'trolley is counted against at the end of a shift. Choose a kind of clean, then set '
    + 'how much of each item a room of each kind needs.',
  linenOurs:
    'The numbers you started with are ours, not yours — taken from the approved design '
    + 'pack and scaled by how many people a room sleeps. Nothing in the product treats '
    + 'them as authority. Correct them here.',
  linenForWork: 'For this kind of clean',
  linenGrid: 'Item by kind of room',
  linenItem: 'Item',
  linenSleeps: 'sleeps {count}',
  linenNone: 'none',
  linenNote:
    'An empty box means nothing of that item is carried — it is not a zero, and nothing '
    + 'appears on the attendant’s list. Changes apply to the next clean; nothing already '
    + 'finished is restated. Nobody is refused for using something different: the standard '
    + 'is what makes the difference visible.',
  editBuildingCode: 'Short code',
  editBuildingName: 'Name',
  editReasonRemove: 'Why is this room leaving the hotel?',
  editReasonRestore: 'Why is this room coming back?',
  editReasonHint: 'Somebody will ask in six weeks’ time.',
  editRemovedBecause: 'Out of the hotel since {date} — {reason}',
  editNothingChanged: 'Nothing changed.',
  editCreated: 'Room {roomNumber} added.',
  editRenumbered: 'Renumbered to {roomNumber}.',
  editMoved: 'Room {roomNumber} moved.',
  editRemoved: 'Room {roomNumber} is out of the hotel. Its card has been cancelled.',
  editRestored: 'Room {roomNumber} is back in the hotel, and needs cleaning before it is sold.',
  /* THE CONSEQUENCE THAT HAPPENS OFF THIS SCREEN, said once to the person who caused
     it. The code still resolves to the right room — what is wrong is the number printed
     under the square. */
  editReprint:
    'The card in room {roomNumber} still works, but the number printed on it is now '
    + 'wrong. Print a new one from the QR codes screen.',
  editConverted: 'Saved as {roomNumber} — a room number is written in Western digits.',
  editRestoreWarning:
    'It comes back needing to be cleaned, whatever state it was in when it left.',

  /* ── W-136, the cards in the rooms ──────────────────────────────────────
     THE WORDS ARE ABOUT PAPER, because the work is: somebody prints a sheet, cuts it up
     and walks a floor with it. "Reprint" is not "reissue" — the code is unchanged and the
     card is the thing that was torn — and the screen must never make those read alike. */
  qrTitle: 'Room QR codes',
  qrSubtitle: 'Issue, revoke and reprint the code on every door.',
  qrIntro:
    'A guest scans the card beside their bed and asks for towels without an account. '
    + 'The code behind each row is a random value and never the room number — if it were '
    + 'the number, anyone who can count could raise a request against any room in the '
    + 'hotel without ever visiting it.',
  qrWhereTheyStand: 'Where the cards stand',
  qrRooms: 'Rooms',
  qrLive: 'Cards live',
  /* NOT "never issued", which is what the pack drew — because it is a LIE about a room
     whose card was revoked yesterday, and that is the commonest way a room ends up
     without one. The figure counts rooms with no LIVE card, and so does the row badge;
     both now say what they count. Found by rendering the screen against a hotel where a
     card had just been revoked and reading the row. */
  qrNever: 'No card',
  qrRevoked: 'Revoked',
  qrRequests: 'Requests through cards',
  qrIssueMissing: 'Issue the {count} missing',
  qrIssuing: 'Issuing…',
  qrIssuedCount: '{count} codes issued. Print the sheet and put the new cards up.',
  qrNothingMissing: 'Every room has a card.',
  qrPrint: 'Print the cards',
  qrPrinting: 'Building the sheet…',
  qrPrintHint: 'Six to a page, with cut lines. Only live cards are printed.',

  /* ── W-125/126, what a clean consists of ────────────────────────────────
     THE WORDS ARE ABOUT VERSIONS, not about saving. "Publish as v8" is a different act
     from "save", and the screen must never let them read alike: one changes a draft
     nobody has seen, the other changes what every attendant is handed tomorrow. */
  clTitle: 'Checklist templates',
  /* NOT THE SCREEN'S OWN NAME, AND NOT ITS SUBTITLE. Both cards on this screen repeated
     the header above them in the first render — the FOURTH time in this project, after the
     sidebar's "Reports" over "Reports", the room master's, and W-136's. A heading names
     what is UNDER it; the shell owns the screen's name. */
  clHowVersionsWork: 'How versions work',
  clListTitle: '{count} templates',
  clSubtitle: 'What a clean consists of · versioned, never edited in place.',
  clIntro:
    'A live template is never edited — a change makes a new version. Every completed clean '
    + 'records which version it followed, so a room cleaned in July can still be read '
    + 'against July’s rules. Without that, editing a checklist silently rewrites the '
    + 'history of every inspection that used it.',
  clLive: 'Live',
  clDrafts: 'Draft',
  clRetired: 'Retired',
  clNew: 'New template',
  clColTemplate: 'Template',
  clColAppliesTo: 'Applies to',
  clColVersion: 'Version',
  clColItems: 'Items',
  clColTime: 'Standard time',
  clColInUse: 'In use since',
  clColState: '',
  clStateLive: 'Live',
  clStateDraft: 'Draft',
  clStateNever: 'Not in use',
  clEdit: 'Edit',
  clEditDraft: 'Continue the draft',
  clNeverPublished: 'Never published',
  clEmpty: 'This hotel has no checklists yet.',
  clMinutes: '{count} min',
  clBack: 'All templates',
  clDraftOf: 'Draft · based on v{live} · not yet in use',
  clDraftFirst: 'Draft · the first version of this checklist · not yet in use',
  clItemsTitle: 'Items, in order',
  clItemsHint: 'The order is what an attendant sees on the handset.',
  clAddItem: 'Add an item',
  clItemCode: 'Code',
  clItemEn: 'English',
  clItemAr: 'Arabic',
  clRequired: 'Required',
  clOptional: 'Optional',
  clPhoto: 'Photo',
  clRemove: 'Remove',
  clUp: 'Move up',
  clDown: 'Move down',
  /* THE PANEL THAT IS THE POINT OF THE SCREEN. Pack 5 says so, and says why: the trade is
     put in front of the person making the change rather than discovered by the person
     paying for the storage. */
  clChangesTitle: 'What changes from v{live}',
  clChangesNone: 'Nothing yet. This draft is the same as the version in use.',
  clChanges: {
    ADDED: '{code} — added',
    REMOVED: '{code} — removed',
    RELABELLED: '{code} — wording changed',
    BECAME_REQUIRED: '{code} — now required',
    BECAME_OPTIONAL: '{code} — now optional',
    PHOTO_NOW_REQUIRED: '{code} — a photograph is now required',
    PHOTO_NO_LONGER_REQUIRED: '{code} — a photograph is no longer required',
    MOVED: '{code} — moved',
  },
  clPhotoCost:
    'About {count} more photographs a day, at {cleans} cleans a day. That is a storage and '
    + 'upload question rather than a design one — worth deciding now.',
  clPhotoSaving: 'About {count} fewer photographs a day, at {cleans} cleans a day.',
  clPhotoUnknown:
    'This hotel has not recorded enough completed cleans yet to say what the photographs '
    + 'would cost per day.',
  /* ONE {live}, NOT TWO. The English read "keep their v7 record" and the Arabic says it
     once, which is how Arabic says it — and the placeholder guard refused the pair. The
     English is what changed: repeating the version number twice in one sentence was
     English habit, not meaning. */
  clNotReEvaluated:
    'Rooms cleaned under v{live} keep that record. Nothing already completed is '
    + 're-evaluated.',
  clPublish: 'Publish as v{version}',
  clPublishing: 'Publishing…',
  clPublished: 'Published as v{version}. Every clean started from now follows it.',
  clSave: 'Save the draft',
  clSaving: 'Saving…',
  clSaved: 'Draft saved. It is not in use until it is published.',
  clNameEn: 'What this list is called',
  clNameAr: 'What it is called in Arabic',
  clSaveName: 'Save the name',
  /* SAID OUT LOUD. An administrator who has just learned that every other change here
     makes a version will assume this did too, and go looking for a draft to publish. */
  clNameNoVersion: 'Renaming does not make a new version.',
  clNameSaved: 'Name saved.',
  clDiscard: 'Discard the draft',
  clDiscarded: 'Draft discarded. The version in use was never touched.',
  clNewTemplate: 'A new checklist',
  clTaskType: 'For which kind of task?',
  clCreate: 'Create',
  qrShowRevoked: 'Show revoked cards',
  qrSearch: 'Find a room',
  qrFloor: 'Floor {floor}',
  qrColRoom: 'Room',
  qrColIssued: 'Issued',
  qrColRequests: 'Requests',
  qrColState: '',
  qrStateLive: 'Live',
  qrStateNever: 'No card',
  qrStateRevoked: 'Revoked',
  qrIssue: 'Issue',
  qrRevoke: 'Revoke',
  qrReprint: 'Reprint',
  qrRevokeReason: 'Why is this card being revoked?',
  qrRevokeHint:
    'It costs a new card on the door. In six weeks this is the only thing that says '
    + 'whether the code was abused or the floor was renumbered.',
  qrRevokeConfirm: 'Revoke the card',
  qrRevoked1: 'Card revoked. Issue a new one and print it before the room is sold again.',
  qrIssued1: 'A card has been issued for room {roomNumber}. It is not on the door yet.',
  /* A REVOKED ROW SITS ABOVE ITS REPLACEMENT AND SAYS SO. The same room appears twice on
     purpose: the requests that arrived through the damaged card are still attributable to
     the card that was on the door at the time, and a row that just vanished would take
     that history with it. */
  qrSuperseded: 'Superseded — {reason}',
  qrNoMatch: 'No room matches that.',
  qrEmpty: 'This hotel has no rooms yet. Load them under Rooms and areas first.',
  rosterStates: {
    ON_SHIFT: 'On shift',
    REST_DAY: 'Rest day',
    LEAVE: 'Leave',
    ABSENT: 'Absent',
  },

  areasTitle: 'Public areas',
  areasSubtitle: 'Grouped by zone, not by floor',
  areasNeedAttention: '{count} need attention',
  areasAllOnSchedule: 'Everything is on schedule.',
  areasEmpty: 'No areas are set up for this property yet.',
  areasEvery: 'every {interval}',
  areasLateBy: '{duration} late',
  areasDueIn: 'due in {duration}',
  areasNeverCleaned: 'never cleaned',
  areasLastDone: 'last done {when}',
  areasMarkClean: 'Mark cleaned',
  areaSvcClose: 'Close it',
  areaSvcReopen: 'Reopen it',
  areaSvcCloseTitle: 'Close {area}',
  areaSvcReopenTitle: 'Reopen {area}',
  /* **WHAT CLOSING ACTUALLY DOES**, in the two consequences a supervisor cannot see from
     the button: the lateness clock stops, and work already raised is not cancelled. */
  areaSvcCloseIntro:
    'While it is closed it will not appear as due or late, and no clean can be recorded '
    + 'against it. Anything already raised against it stays — nobody is sent home.',
  areaSvcReopenIntro:
    'It goes straight back onto the schedule, and picks up its lateness from when it was '
    + 'last cleaned rather than from now.',
  areaSvcReasonLabel: 'Why',
  areaSvcCloseReasonHint:
    'A sentence. It is the only thing that will explain, in a fortnight, why this area '
    + 'stopped appearing on anybody’s list.',
  areaSvcReopenReasonHint: 'A sentence — what changed.',
  areaSvcClosedNow: 'Closed at the moment.',
  areaSvcOutOfOrderNote:
    'A fault has this area out of order, so it is not yours to open or close here. It '
    + 'comes back when the fault is resolved.',
  areaSvcCancel: 'Cancel',
  areaSvcClosed: '{name} is closed.',
  areaSvcReopened: '{name} is open again.',
  areasMarked: '{name} — recorded.',
  areasUngrouped: 'No zone',
  areaStandings: {
    ON_SCHEDULE: 'On schedule',
    DUE_SOON: 'Due soon',
    OVERDUE: 'Overdue',
    MISSED: 'Missed',
    BEING_CLEANED: 'Being cleaned',
    CLOSED: 'Closed',
    RESTING: 'Outside its hours',
  },

  assignTitle: 'Assignment board',
  assignSubtitle: "Today's work, by attendant",
  unassigned: 'Unassigned',
  credits: 'credits',
  creditsShort: 'cr',
  workload: 'Workload',
  assignHere: 'Assign here',
  assignTo: 'Assign to',
  cancelSelection: 'Cancel',
  selectedCard: 'Selected',
  moving: 'Moving',
  noWorkTitle: 'No work on the board today',
  /* THE WAY OUT, BESIDE THE PROBLEM. Before Sprint 33 this said tasks "appear here as
     rooms are raised", which was true and useless: nothing in the product raised one.
     An empty board at 06:30 is now one press from a full one. */
  noWorkBody: 'Generate today’s cleans from the rooms the hotel has, or raise one by hand.',
  genButton: 'Generate today’s work',
  genWorking: 'Sweeping the hotel…',
  /* WHAT IT DID, NOT "DONE". */
  genRaised: 'Raised {count} for today.',
  /* THE COMMONEST OUTCOME ON A SECOND PRESS, and the one that would otherwise read as a
     failure. "Nothing raised" alone makes a supervisor press again harder. */
  genNothingNew: 'Nothing new — the board is already up to date.',
  genBreakdown:
    '{departures} departures · {stayovers} stayovers · {deep} deep cleans · '
    + '{refresh} refreshes · {inHand} already in hand · {nothing} nothing to clean · '
    + '{shut} not in service · {swept} rooms in all',
  /* **A SWITCH THE HOTEL CHOSE IS NOT A WARNING**, so this is drawn plainly. What it
     answers is the question a thin board raises: is this morning quiet, or broken? */
  genSwitchedOff: '{count} wanted "{what}", which this hotel does not raise',
  /* BY NUMBER, because that is what is painted on the door and what they will type into
     the room screen to fix it. */
  genUnassignable:
    'These rooms have work today that nobody can be given, because nobody has said how '
    + 'they are cleaned: {rooms}. Set them under Rooms and floors.',
  genDismiss: 'Dismiss',
  /* THE VALUE IS IN THE LABEL, so the commonest question is answered without a press. */
  schedRunsAt: 'Runs automatically at {time}',
  schedManual: 'Generated by hand',
  schedChange: 'Change',
  schedTitle: 'When today’s work is generated',
  /* WHAT IT DOES AND WHAT IT DOES NOT DO. Somebody turning this on needs to know the
     button stays — automation that removes the manual path leaves nothing to do when it
     fails, and picking up the nine-o'clock departures is a real daily act. */
  schedIntro:
    'The hotel’s cleans can be generated automatically each morning, at the hotel’s own '
    + 'local time. The button stays either way — press it again whenever the property '
    + 'system reports new departures.',
  schedLabel: 'Generate at',
  schedHint: 'The hotel’s local time. Leave empty to generate by hand.',
  schedOff: 'Turn the schedule off',
  schedSavedOn: 'Today’s work will be generated at {time} from now on.',
  schedSavedOff: 'The schedule is off. Press the button to generate the day.',
  /* THE OBJECTION, ANSWERED ON THE SCREEN. An empty board at half past six has to be
     distinguishable from a quiet morning, or nobody can tell automation from failure. */
  schedOverdue:
    'Today’s work should have been generated automatically at {time} and has not been. '
    + 'Press the button, and tell whoever looks after the server.',
  /* SHORT, because it sits on a card beside a room number and a credit figure. The full
     sentence is the aria-label and the refusal; this is the flag that stops somebody
     dragging it in the first place. */
  cardNotConfigured: 'Not set up',
  cardNotConfiguredWhy:
    'Nobody has said how room {room} is cleaned, so this cannot be given to anyone. '
    + 'Set it under Rooms and floors.',
  laneEmpty: 'Nothing assigned',
  dropHere: 'Drop a task here to assign',
  notWorking: 'Not on shift. Assignments are blocked.',
  overBudget: '{name} is over budget by {by} credits.',
  hasCapacity: '{name} has {free} credits free.',
  roster: {
    ON_SHIFT: 'On shift',
    REST_DAY: 'Rest day',
    LEAVE: 'Leave',
    ABSENT: 'Absent',
  },

  checklistTitle: 'Checklist',
  room: 'Room',
  doneOf: 'done',
  optional: 'Optional',
  mandatory: 'Required',
  cannotDo: "Can't do this",
  whatStoppedYou: 'What stopped you?',
  saveException: 'Save',
  trgTitle: 'What raises work',
  trgSubtitle: 'Five things can put a room on the board. Each is yours to switch off.',
  trgNames: {
    GUEST_CHECKOUT: 'A guest checks out',
    STAYOVER_SERVICE: 'A guest is staying another night',
    SCHEDULED_DEEP_CLEAN: 'A deep clean comes round',
    VACANT_ROOM_REFRESH: 'An empty room has stood too long',
    ON_REQUEST: 'Somebody asks for it',
  },
  trgDoes: {
    GUEST_CHECKOUT: 'The morning sweep raises a departure clean for every room a guest '
      + 'has left, or is leaving today.',
    STAYOVER_SERVICE: 'The sweep raises a stayover clean for every room somebody is '
      + 'sleeping in tonight.',
    SCHEDULED_DEEP_CLEAN: 'The sweep raises a deep clean when a room reaches the '
      + 'frequency set against it — monthly, quarterly, twice a year or yearly. Rooms set '
      + 'to "none" are never scheduled.',
    VACANT_ROOM_REFRESH: 'The sweep raises a touch-up for a room that is empty, made up, '
      + 'and has not been gone into for the number of days you set.',
    ON_REQUEST: 'A supervisor raises a task by hand, for anything the sweep did not. '
      + 'Switch this off and nobody can.',
  },
  trgEvery: 'After',
  trgDays: 'days standing',
  /* **THE DATE THAT TURNS SILENCE INTO A PROMISE.** Switching this on raises nothing on
     the first morning — treating "never deep-cleaned" as "overdue" would empty the hotel
     — and without this line that is indistinguishable from a switch that does not work. */
  trgCountsFrom: 'Rooms with no deep clean recorded yet count from {date}.',

  adjTitle: 'Corrections waiting',
  adjSubtitle: 'A correction to a shelf figure is decided by a second person.',
  adjRaise: 'Correct a figure',
  /* **THE SENTENCE THAT PREVENTS THE COMPLAINT.** Somebody who writes off four torn
     towels and watches the shelf not move will otherwise conclude we lost it. */
  adjNothingMoves: 'Nothing moves yet. Somebody else with stock rights decides this, and '
    + 'the shelf figure changes only when they do.',
  adjWhat: 'Which item, and where',
  adjPickShelf: 'Choose an item and a place',
  adjBy: 'Change the figure by',
  adjByHint: '-4',
  adjWhy: 'Why',
  adjWhyHint: 'Four torn in the laundry',
  adjSend: 'Send for approval',
  adjNoneWaiting: 'Nothing is waiting for a decision.',
  adjMovesBy: '{by} — from {from} to {to}',
  adjRaisedBy: '{who} · {when}',
  adjApprove: 'Approve',
  adjDecline: 'Turn down',
  adjWhyNot: 'Why not',
  adjWhyNotHint: 'The laundry still has them. Ask on Friday.',
  /* Not "you cannot". He has done nothing wrong; the rule is that it takes two people. */
  adjYoursToWaitFor: 'You raised this one, so somebody else decides it.',
  adjRecent: 'Recently decided',
  adjDecidedLine: '{what} {by} — {state} by {who}',
  adjStates: { PROPOSED: 'waiting', APPROVED: 'approved', DECLINED: 'turned down' },

  cancel: 'Cancel',
  exceptionRecorded: 'Could not be done',
  submitRoom: 'Submit room',
  submitting: 'Submitting',
  submittedTitle: 'Room submitted',
  submittedBody: 'Your supervisor will inspect it.',
  stillNeeded: '{count} still needed',
  waitingToSend: '{count} waiting to send',
  submitAfterSending: 'You can submit once your work has sent.',
  staleSince: 'Showing information from {time}. It may have changed.',
  workingOffline: 'Working offline',
  loadFailed: 'The checklist could not be loaded.',
  handsetNotice:
    'A preview, not the handset application. The rules, the offline queue and the '
    + 'wording are the real ones; the mobile stack is not yet confirmed, so this runs '
    + 'in the browser at handset width.',

  /* ── W-105 and W-106 ────────────────────────────────────────────────────────
     Two screens and one job, like W-125/126: counting a shelf, and deciding what to do
     about what the count found. The words keep those two acts apart — nothing on the
     count sheet says "write off", and nothing on the variance says "count". */
  cntTitle: 'Stock counts',
  cntSubtitle: 'Count a store, then decide what the difference means.',
  /* NOT THE SCREEN'S OWN NAME — the fifth time this has had to be said in a comment.
     A heading names what is UNDER it; the shell owns the screen's name. */
  cntIndexTitle: 'Where to count',
  cntIndexIntro:
    'While a count is open, stock cannot be moved into or out of that location — that is '
    + 'what stops the numbers changing under the person walking the shelves. Approving '
    + 'or abandoning the count releases it.',
  cntStartAt: 'Count {location}',
  cntStarting: 'Starting…',
  cntNoLocations: 'No stock locations have been set up yet.',
  cntRecent: 'Counts',
  cntNoCounts: 'Nothing has been counted here yet.',
  cntColReference: 'Count',
  cntColLocation: 'Location',
  cntColStarted: 'Started',
  cntColProgress: 'Counted',
  cntColState: 'State',
  cntOpen: 'Being counted',
  cntSubmitted: 'Awaiting approval',
  cntApproved: 'Approved',
  cntAbandoned: 'Abandoned',
  cntOpenIt: 'Open',
  cntBack: 'All counts',
  cntSheetTitle: 'Count sheet · {reference}',
  cntBlindTitle: 'This is a blind count',
  cntBlindWhy:
    'The expected quantity is not shown, and it is not sent to this screen at all. '
    + 'Showing it turns a count into a confirmation: people see 412, count roughly 412 '
    + 'and move on. The difference appears once the sheet is handed over.',
  cntProgress: '{counted} of {total} items counted',
  cntColItem: 'Item',
  cntColCounted: 'Counted',
  cntColCountedBy: 'Counted by',
  cntColTime: 'Time',
  cntColExpected: 'Expected',
  cntHidden: 'Hidden',
  cntUnreadable:
    'One of the figures cannot be read as a number. Nothing has been saved — check the '
    + 'boxes before saving again, so a mistyped entry is not mistaken for a count.',
  cntNotCounted: 'Not counted',
  cntSave: 'Save and pause',
  cntSaving: 'Saving…',
  cntSaved: 'Saved. You can close this and come back to it.',
  cntHandOver: 'Hand over for approval',
  cntHandingOver: 'Handing over…',
  cntHeldNotice:
    'Movements in and out of {location} are held while this count is open.',
  cntAbandon: 'Abandon this count',
  cntAbandonReason: 'Why is it being abandoned?',
  cntAbandonConfirm: 'Abandon and release the store',
  cntCancel: 'Cancel',
  cntVarianceTitle: 'Variance · {reference}',
  cntVarianceIntro:
    'Approving writes one adjustment into the ledger for each item marked to adjust. It '
    + 'overwrites nothing: what was expected, what was found and the difference are all '
    + 'kept. An item marked to recount writes nothing at all.',
  cntFigItems: 'Items counted',
  cntFigMatched: 'Matched exactly',
  cntFigVariances: 'Variances',
  cntFigNet: 'Net pieces',
  cntFigNotCounted: 'Not counted',
  cntShortOver: '{short} short, {over} over',
  cntNoAdjustment: 'no adjustment needed',
  cntPercentOfHolding: '{percent} of what was counted',
  cntColDifference: 'Difference',
  cntColReason: 'Likely reason',
  cntColDecision: 'Adjustment',
  cntReasonMatched: 'Matches',
  cntReasonReceipt: 'Earlier receipt not recorded',
  cntReasonLaundry: 'Possibly at the laundry',
  cntReasonCirculation: 'Possibly still in circulation',
  cntReasonIssued: 'Issued but not recorded',
  cntReasonLoss: 'Loss in circulation',
  cntAdjNone: 'Nothing to write',
  cntAdjUp: 'Adjust up',
  cntAdjWriteOff: 'Write off',
  cntAdjRecount: 'Recount',
  cntWorkingLaundry: '{quantity} at the laundry',
  cntWorkingCirculation: '{quantity} out on rails',
  cntApprove: 'Approve adjustments',
  cntApproving: 'Approving…',
  cntSendBack: 'Send for recount',
  cntSendBackReason: 'What should be checked again?',
  cntSendingBack: 'Sending back…',
  cntYouCounted:
    'You counted this one, so somebody else approves it. Counting a shortfall and '
    + 'authorising the write-off are two decisions on purpose.',
  cntSubmittedBy: 'Counted by {name}, {time}',
  cntApprovedBy: 'Approved by {name}, {time}',
  cntAbandonedBy: 'Abandoned by {name}, {time}',
  cntReturnedNote: 'Sent back for a recount: {reason}',
  cntRound: 'Round {round}',

  /* ── W-120 and W-121 ────────────────────────────────────────────────────────
     They own identity, we own authorisation — except that in this phase there is no
     "they", so the wording says so plainly rather than leaving an empty table looking
     like a sync that has not run. */
  usrTitle: 'Users',
  usrSubtitle: 'Everyone who can sign in, and the ones who cannot yet.',
  /* NOT THE SCREEN'S OWN NAME. Sixth time this has needed saying in a comment. */
  usrWhoCanSignIn: 'Who can sign in',
  usrIntro:
    'A role is what lets somebody sign in and decides what they can do. Somebody with '
    + 'no role cannot reach anything — which is why they have a tab of their own rather '
    + 'than one amber row in a list of thirty.',
  usrNoPms:
    'There is no property system connected in this phase, so people are added here by '
    + 'hand. When one is connected, people will arrive from it and their names will '
    + 'become read-only.',
  usrActive: 'Active',
  usrNoRole: 'No role',
  usrDisabled: 'Disabled',
  usrAdd: 'Add a person',
  usrAdding: 'Adding…',
  usrAddTitle: 'Add a person',
  usrEmployeeNo: 'Employee number',
  usrName: 'Name',
  usrNameAr: 'Name in Arabic',
  usrLanguage: 'Language',
  usrEnglish: 'English',
  usrArabic: 'Arabic',
  usrCreate: 'Add',
  usrCancel: 'Cancel',
  usrColPerson: 'Person',
  usrColEmployeeNo: 'Employee no.',
  usrColRole: 'Role',
  usrColProperty: 'Property',
  usrColLastSeen: 'Last seen',
  usrColSource: 'Source',
  usrColState: 'State',
  usrEmail: 'Email',
  usrSourceSynced: 'Synced',
  usrSourceEnteredHere: 'Added here',
  /* IN WORDS, NOT A DASH. "Never signed in" is a different fact from "a long time ago",
     and it is the one that tells an administrator the new joiner has not started yet. */
  usrNeverSignedIn: 'Never signed in',
  usrNoneHere: 'Nobody is in this state.',
  usrOpen: 'Open',
  usrBack: 'All users',
  usrFromPms: 'From the property system',
  usrFromPmsWhy:
    'Read-only here — corrections are made at source. There is no property system in '
    + 'this phase, so these were typed here and can be corrected here.',
  usrOurs: 'What they can do here',
  usrOursWhy: 'Ours to set. Nothing about permissions comes from the property system.',
  usrRole: 'Role',
  usrProperty: 'Property',
  usrSave: 'Save',
  usrSaving: 'Saving…',
  usrSaved: 'Saved.',
  usrRemoveRole: 'Remove this role',
  usrSessions: 'Sessions',
  usrSessionsWhy:
    'Ending a session signs that one device out on its very next request — not when the '
    + 'session would have expired.',
  usrNoSessions: 'This person has never signed in.',
  usrColDevice: 'Device',
  usrColSignedIn: 'Signed in',
  usrColExpires: 'Expires',
  usrColActivity: 'Last activity',
  /* NEVER A GUESS. A label against the wrong row ends the wrong person's shift. */
  usrUnknownDevice: 'Unknown device',
  usrDeviceHandsetApp: 'Housekeeping handset',
  usrDeviceAndroid: 'Android handset',
  usrDeviceIphone: 'iPhone',
  usrDeviceIpad: 'iPad',
  usrDeviceEdge: 'Edge on Windows',
  usrDeviceFirefox: 'Firefox',
  usrDeviceChrome: 'Chrome',
  usrDeviceSafari: 'Safari',
  usrThisDevice: 'This device',
  usrEnd: 'End',
  usrEndAll: 'End all sessions',
  usrEnded: 'Ended',
  usrEndedByAdmin: 'Ended by an administrator',
  usrEndedSignedOut: 'Signed out',
  usrEndedDisabled: 'Ended when the account was disabled',
  usrEndedAll: 'Ended with all the others',
  usrActivity: 'Activity',
  usrActivityWindow: 'Last 30 days',
  usrTasksCompleted: 'Rooms completed',
  usrInspectionsFailed: 'Sent back',
  usrRequestsFulfilled: 'Requests fulfilled',
  /* THE PACK DRAWS A "rework rate 3%" HERE AND THIS BUILD DOES NOT. A percentage
     against a named person is a performance judgement, and the client has not agreed
     what its denominator should be — CQ-122 is open on what a credit represents. Two
     counts and the window say the same thing without inventing one. */
  /* AND NO "CQ-122" IN THE SENTENCE. The first draft cited the clarification number in
     both languages — which the Arabic guard caught as Latin letters, and which was wrong
     in English too: a housekeeping administrator in Najaf has never seen our
     clarification register and "(CQ-122)" reads as a fault code. Our internal shorthand
     belongs in the code comment above, not on somebody's screen. */
  usrNoRate:
    'Counts, not rates. What a figure like a rework percentage should be measured '
    + 'against is still a question for the hotel.',
  usrDisable: 'Disable this person',
  usrEnable: 'Enable this person',
  usrDisableReason: 'Why?',
  /* A DIFFERENT WORDING FROM THE BUTTON THAT OPENED THE FORM. The first draft used the
     same string for both, which put two "Disable this person" buttons on screen at once
     — one that opens a form and one that does the thing. Found by a test that could not
     tell them apart, which is exactly the problem a person has. */
  usrDisableConfirm: 'Disable and sign them out',
  usrDisableWarning:
    'They will be signed out of every device on their next tap, and will not be able to '
    + 'sign in again until somebody enables them.',
  usrDisabledOn: 'Disabled {when} — {reason}',
  usrCannotSignIn: 'Cannot sign in until somebody assigns a role.',
  /* ── W-122 · roles and permissions ──────────────────────────────────────────
     THE CAPABILITY SENTENCES ARE TRANSLATED; THE CODES ARE NOT. `stock.adjust` is
     what the server checks and what appears in a log, and translating it would make
     two names for one thing. What an administrator reads is the sentence. */
  rolTitle: 'Roles and permissions',
  rolSubtitle: 'Every cell is a decision',
  rolWhy:
    'Roles are ours, not the property system’s. Nothing about permissions crosses that '
    + 'interface — the property system tells us who a person is, and this table decides what '
    + 'they may do. That separation is what lets an administrator fix an access problem at '
    + 'eight in the morning without anyone else’s help.',
  rolHalfMeans: 'A half-filled box means “allowed, with a reason recorded”.',
  rolAll: 'All capabilities',
  rolChangedOnly: 'Changed from default',
  rolNothingChanged: 'Nothing has been changed from what was set up.',
  rolColArea: 'Area',
  rolColCapability: 'Capability',
  rolSave: 'Save',
  rolSaving: 'Saving…',
  rolSaved: 'Saved.',
  rolDiscard: 'Discard changes',
  rolUnsaved: '{count} not saved yet',
  rolChangedFromDefault: '{count} changed from what was set up',
  rolDefaultIs: 'Set up as: {grant}',
  rolDenied: 'Not allowed',
  rolAllowed: 'Allowed',
  rolAllowedWithReason: 'Allowed, with a reason recorded',
  rolEnforcedBy: 'Checked by',
  /* SAID OUT LOUD ON THE ROW. Three of these are granted and enforced by nothing yet,
     and an administrator setting one is entitled to know it changes nothing today. */
  rolNotEnforcedYet: 'Nothing checks this yet.',
  rolAreaRooms: 'Rooms',
  rolAreaWork: 'Work',
  rolAreaRequests: 'Requests',
  rolAreaFaults: 'Faults',
  rolAreaAreas: 'Public areas',
  rolAreaInventory: 'Inventory',
  rolAreaReports: 'Reports',
  rolAreaFiles: 'Photographs',
  rolAreaAdministration: 'Administration',
  rolCapRoomViewReadiness: 'View the board and room detail',
  rolCapRoomSetOutOfService: 'Take a room off sale for repair',
  rolCapRoomOverrideToClean: 'Declare a room clean without an inspection',
  rolCapRoomSyncFromPms: 'Move a room on a guest checking out',
  rolCapTaskStart: 'Start a clean',
  rolCapTaskSubmit: 'Submit a clean, and photograph it',
  rolCapTaskAssign: 'Assign and reassign work',
  /* NAMES BOTH SCALES, because the grid cell is one permission and a role holder needs
     to know it covers the morning sweep as well as a single touch-up. */
  rolCapTaskRaise: 'Raise work, and generate the day’s cleans',
  rolCapTaskInspect: 'Inspect, and send work back',
  rolCapTaskCancel: 'Cancel work in hand',
  rolCapRosterEdit: 'Edit the roster',
  rolCapRequestRaise: 'Raise a guest request',
  rolCapRequestFulfil: 'Take, deliver or decline a guest request',
  rolCapIssueRaise: 'Report a fault, and photograph it',
  rolCapFaultDowngradeBlocking: 'Downgrade a blocking fault',
  rolCapAreaClean: 'Record a public-area clean',
  rolCapAreaSetOutOfService: 'Close a public area',
  rolCapStockMove: 'Record a stock movement, and count',
  rolCapStockAdjust: 'Adjust stock, and approve a count',
  rolCapAdminStockPars: 'Set what each place should hold',
  rolCapLinenAdjust: 'Correct somebody else’s linen figure',
  rolCapAdminGoLive: 'Read the go-live checklist',
  rolCapAdminConfiguration: 'Change the hotel’s own name and what the roles are called',
  rolCapAdminRequestCategories: 'Change what a guest may ask for, and how long each is promised in',
  rolCapAdminFaultCategories: 'Change the list of things that can be wrong with a room',
  rolCapReportAttendantProductivity: 'Open attendant productivity',
  rolCapReportRoomsPerDay: 'Open rooms cleaned per day',
  rolCapReportFaultsDowntime: 'Open faults and downtime',
  rolCapReportLoadThroughDay: 'Open load through the day',
  rolCapReportConsumables: 'Open the consumables report',
  rolCapReportLinenLoss: 'Open linen used and lost',
  rolCapReportRequestLateness: 'Open guest requests and lateness',
  rolCapReportInspectionFailures: 'Open inspection failures',
  rolCapFileRemove: 'Remove a photograph',
  rolCapAdminRooms: 'Set up rooms and areas',
  rolCapAdminLinenStandards: 'Set what a clean needs',
  rolCapAdminQrCodes: 'Issue and revoke room cards',
  rolCapAdminChecklists: 'Edit checklist templates',
  rolCapAdminUsers: 'Assign roles to people',
  rolCapAdminRoles: 'Change roles and permissions',
  rolCapAdminTranslations: 'Correct the Arabic on everything this system named',
  rolCapAdminActivity: 'Read the activity log',

  // ── W-124, the activity log ─────────────────────────────────────────────
  actTitle: 'Activity log',
  actSubtitle: 'Who did what, when · kept for {months} months',
  actTabAll: 'All activity',
  actTabPermissions: 'Permission changes',
  actTabOverrides: 'Overrides',
  actTabSignIns: 'Sign-in failures',
  actColWhen: 'When',
  actColWho: 'Who',
  actColWhat: 'What',
  actColSubject: 'Subject',
  actColBefore: 'Before',
  actColAfter: 'After',
  actColWhere: 'Where from',
  actExport: 'Export',
  actExporting: 'Preparing the file…',
  actFrom: 'From',
  actTo: 'To',
  actRangeAll: 'Everything kept',
  actRange7: 'Last 7 days',
  actRange30: 'Last 30 days',
  actRange90: 'Last 90 days',
  actOlder: 'Show older',
  actLoading: 'Reading the log…',
  actEmptyTitle: 'Nothing recorded yet',
  actEmptyBody:
    'This log fills up as people override rooms, change permissions and fail to sign in. '
    + 'An empty one on a new installation is expected.',
  actEmptyFilteredBody:
    'Nothing matches this filter and date range. Try another tab, or widen the dates.',
  actErrorTitle: 'The log could not be read',
  actNoPermissionTitle: 'You cannot see the activity log',
  actNoPermissionBody:
    'It records what everybody did, so it is deliberately held by fewer people than the '
    + 'screens it reports on. An administrator can grant it on the roles screen.',
  actRetentionNote:
    'Entries are never edited. They are deleted {months} months after they happen, whole.',
  /* THE CARD'S OWN HEADING, and it exists because the first version repeated the screen
     title — "Activity log" above "Activity log", which is the mistake the sidebar already
     records having made once with "Reports" above "Reports". The shell names the screen;
     the card says what is in front of you. */
  actEntries: {
    one: 'One entry',
    two: 'Two entries',
    few: '{count} entries',
    many: '{count} entries',
    other: '{count} entries',
  },
  actNothing: 'nothing',
  actReasonLabel: 'Reason',

  actActor: {
    SYSTEM: 'System',
    UNKNOWN: 'Unknown',
  },

  actVerb: {
    PERMISSION_CHANGED: 'Permission',
    ROLE_ASSIGNED: 'Role given',
    ROLE_REMOVED: 'Role taken away',
    SIGN_IN_REJECTED: 'Sign-in refused',
    SIGN_IN_NO_ACCOUNT: 'Sign-in — no account here',
    ROOM_OVERRIDDEN: 'Override',
    TASK_CANCELLED: 'Work cancelled',
    FAULT_DOWNGRADED: 'Blocking fault cleared',
    STOCK_ADJUSTED: 'Stock adjusted',
    PHOTOGRAPH_REMOVED: 'Photograph removed',
    USER_DISABLED: 'Account disabled',
    USER_ENABLED: 'Account enabled',
    PERSON_ADDED: 'Person added',
    SESSION_ENDED: 'Session ended',
    ROOM_REMOVED_FROM_HOTEL: 'Room taken out of the hotel',
    ROOM_RESTORED: 'Room put back',
    CLEANING_RULES_SET: 'Cleaning rules',
    CHECKLIST_PUBLISHED: 'Checklist published',
    LINEN_STANDARD_SET: 'Linen standard changed',
    ROOM_TAKEN_OUT_OF_SERVICE: 'Room taken off sale',
    LINEN_FIGURE_CORRECTED: 'Linen figure corrected',
    HOTEL_DETAILS_SET: 'Hotel details changed',
    /* Its own verb since Sprint 77 — the question asked afterwards is "since when
       have we not been inspecting", not "what changed on Tuesday". */
    INSPECTION_POLICY_SET: 'Inspection policy changed',
    ROLE_RENAMED: 'Role renamed',
    FAULT_CATEGORY_ADDED: 'Kind of fault added',
    FAULT_CATEGORY_CHANGED: 'Kind of fault renamed',
    FAULT_CATEGORY_RETIRED: 'Kind of fault switched off',
    FAULT_CATEGORY_RESTORED: 'Kind of fault switched back on',
    FAULT_CATEGORY_REMOVED: 'Kind of fault removed',
    STOCK_PLACE_ADDED: 'Place for linen added',
    STOCK_PLACE_CHANGED: 'Place for linen renamed',
    STOCK_PLACE_RETIRED: 'Place for linen switched off',
    STOCK_PLACE_RESTORED: 'Place for linen switched back on',
    STOCK_PLACE_REMOVED: 'Place for linen removed',
    STOCK_ITEM_ADDED: 'Item added',
    STOCK_ITEM_CHANGED: 'Item changed',
    STOCK_ITEM_RETIRED: 'Item switched off',
    STOCK_ITEM_RESTORED: 'Item switched back on',
    STOCK_ITEM_REMOVED: 'Item removed',
    SHIFT_ADDED: 'Shift added',
    SHIFT_CHANGED: 'Shift hours or name changed',
    SHIFT_RETIRED: 'Shift switched off',
    SHIFT_RESTORED: 'Shift switched back on',
    WORK_TRIGGER_CHANGED: 'What raises work changed',
    STOCK_ADJUSTMENT_PROPOSED: 'Correction sent for approval',
    STOCK_ADJUSTMENT_APPROVED: 'Correction approved',
    STOCK_ADJUSTMENT_DECLINED: 'Correction turned down',
    REQUEST_CATEGORY_ADDED: 'Kind of request added',
    REQUEST_CATEGORY_CHANGED: 'Kind of request changed',
    REQUEST_CATEGORY_RETIRED: 'Kind of request switched off',
    REQUEST_CATEGORY_RESTORED: 'Kind of request switched back on',
    REQUEST_CATEGORY_REMOVED: 'Kind of request removed',
    AREA_TAKEN_OUT_OF_SERVICE: 'Public area closed or reopened',
    TRANSLATION_CHANGED: 'Arabic name corrected',
  },

  actSubject: {
    ROOM: 'Room',
    USER: 'Person',
    ROLE: 'Role',
    SESSION: 'Session',
    SIGN_IN: 'Sign-in',
    ISSUE: 'Fault',
    STOCK_ITEM: 'Stock item',
    TEMPLATE: 'Checklist',
    TASK: 'Task',
    CHECKLIST_ANSWER: 'Checklist item',
    FAULT_CATEGORY: 'Kind of fault',
    STOCK_PLACE: 'Place for linen',
    REQUEST_CATEGORY: 'Kind of request',
    AREA: 'Public area',
    ROOM_TYPE: 'Room type',
    STOCK_LOCATION: 'Stock location',
    TASK_TYPE: 'Kind of work',
    SHIFT: 'Shift',
    WORK_TRIGGER: 'What raises work',
    ZONE: 'Zone',
    BUILDING: 'Building',
    PROPERTY: 'Hotel',
  },

  actSource: {
    WEB: 'Web',
    MOBILE: 'Handset',
    RULE: 'Rule',
  },

  actValue: {
    ACTIVE: 'Active',
    RETIRED: 'Switched off',
    DISABLED: 'Disabled',
    LIVE: 'Live',
    ENDED_BY_ADMIN: 'Ended by an administrator',
    ALL_ENDED: 'All sessions ended',
    USER_DISABLED: 'Ended — account disabled',
    IN_HOTEL: 'In the hotel',
    REMOVED: 'Removed',
    IN_SERVICE: 'On sale',
    OUT_OF_SERVICE: 'Off sale',
    OUT_OF_ORDER: 'Out of order',
    PRESENT: 'Present',
    REJECTED: 'Refused',
    NO_ACCOUNT: 'No account here',
    EMPTY: 'Nothing was typed',
    CLEARED_TEMPLATES: 'Checklists cleared',
    /* THE DEVICE CODES. The same words W-121 uses, deliberately — the sessions panel and
       the log are two views of one act, and a supervisor who cut off "the Android handset"
       there must read the same three words here. */
    UNKNOWN_DEVICE: 'Unknown device',
    ALL_DEVICES: 'Every device',
    HANDSET_APP: 'Housekeeping handset',
    ANDROID: 'Android handset',
    IPHONE: 'iPhone',
    IPAD: 'iPad',
    EDGE: 'Edge on Windows',
    FIREFOX: 'Firefox',
    CHROME: 'Chrome',
    SAFARI: 'Safari',
    DENIED: 'Not allowed',
    ALLOWED: 'Allowed',
    ALLOWED_WITH_REASON: 'Allowed, with a reason recorded',
    DIRTY: 'Dirty',
    IN_PROGRESS: 'In progress',
    CLEAN: 'Clean',
    INSPECTED: 'Inspected',
    INSPECTION_REQUIRED: 'Rooms wait to be inspected',
    INSPECTION_NOT_REQUIRED: 'Rooms are ready when the attendant says so',
    OPEN: 'Open',
    ACKNOWLEDGED: 'Acknowledged',
    RESOLVED: 'Resolved',
    CANCELLED: 'Cancelled',
    RECEIPT: 'Receipt',
    TRANSFER: 'Transfer',
    ISSUE: 'Issue',
    RETURN: 'Return',
    LOSS: 'Loss',
    ADJUSTMENT: 'Adjustment',
    PROPOSED: 'Waiting for a decision',
    APPROVED: 'Approved',
    DECLINED: 'Turned down',
    ON: 'On',
    OFF: 'Off',
    ROOM_ATTENDANT: 'Room attendant',
    FLOOR_SUPERVISOR: 'Floor supervisor',
    HOUSEKEEPING_MANAGER: 'Housekeeping manager',
    STORE_KEEPER: 'Store keeper',
    FRONT_DESK: 'Front desk',
    ADMINISTRATOR: 'Administrator',
  },
}

const ar: Dict = {
  boardTitle: 'لوحة الغرف',
  boardSubtitle: 'حالة الغرف مباشرة',
  legendTitle: 'المفتاح',
  refresh: 'تحديث',
  searchLabel: 'رقم الغرفة',
  searchPlaceholder: 'ابحث برقم الغرفة',
  allFloors: 'كل الطوابق',
  building: 'المبنى',
  teamsTitle: 'الفِرَق',
  teamsSubtitle: 'من يعمل مع من، وفي أي طابق',
  teamsPairs: 'الثنائيات',
  teamsFloors: 'طوابق اليوم',
  teamsUnpaired: 'بلا فريق',
  teamsNoPairs: 'لا يوجد ثنائي بعد',
  teamsNoPairsBody: 'اختر رجلين من القائمة أدناه واجمعهما. يُسند الطابق إلى الثنائي، ولأيٍّ منهما أن يعمل في أي غرفة فيه.',
  teamsMakePair: 'كوِّن ثنائيًا',
  teamsPairThem: 'اجمعهما',
  teamsDisband: 'فكّ الثنائي',
  teamsCover: 'إنابة',
  teamsCoverFor: 'من لم يحضر؟',
  teamsCovering: 'ينوب عن',
  teamsClearCover: 'لقد حضر',
  teamsNobody: 'لا أحد',
  teamsGiveFloor: 'أسنِد هذا الطابق إلى',
  teamsRoomsMoved: 'انتقلت {count} غرفة إلى هذا الثنائي',
  teamsHolding: 'يحمل {count}',
  teamsOffToday: 'ليس في الدوام اليوم',
  teamsOnShift: 'في الدوام',
  teamsNoFloor: 'بلا طابق بعد',
  teamsChoose: 'اختر',
  allBuildings: 'كل المباني',
  floor: 'الطابق',
  emptyTitle: 'لا توجد غرف على هذه اللوحة بعد',
  emptyBody: 'تظهر الغرف هنا بعد إعداد بيانات الفندق.',
  noMatchTitle: 'لا توجد غرف مطابقة',
  noMatchBody: 'جرّب عامل تصفية آخر أو امسح البحث.',
  retry: 'أعد المحاولة',
  updatedAt: 'آخر تحديث',
  rooms: 'غرفة',
  filters: {
    ALL: 'الكل',
    READY: 'جاهزة',
    DIRTY: 'متسخة',
    IN_PROGRESS: 'قيد التنظيف',
    AWAITING_INSPECTION: 'بانتظار التفتيش',
    NOT_IN_SERVICE: 'خارج الخدمة',
    OCCUPIED: 'مشغولة',
    DEPARTING: 'مغادرة اليوم',
  },
  tones: {
    dirty: 'متسخة',
    prog: 'قيد التنظيف',
    clean: 'نظيفة',
    ready: 'مفتَّشة',
    oos: 'موقوفة عن الخدمة',
    ooo: 'خارج الخدمة (عطل)',
  },
  legendOccupancy: 'الرمز في الزاوية = الإشغال',
  legendNotSellable: 'الحد المتقطع = غير قابلة للبيع',

  ovrTitle: 'اعتبار الغرفة {room} نظيفة',
  ovrIntro:
    'يسجّل هذا الغرفة {room} كنظيفة دون أي عمل تنظيف مقابلها. ويُحفظ في سجل النشاط '
    + 'باسمك وبسببك، ويمكن للمدير الاطّلاع عليه.',
  ovrCurrent: 'حالة الغرفة الآن: {state}.',
  ovrReasonLabel: 'السبب',
  ovrReasonHint: 'من سيقرأ هذا بعد ستة أسابيع لم يكن حاضرًا في موقعك.',
  ovrConfirm: 'اعتبارها نظيفة',
  ovrDone: 'الغرفة {room} أصبحت نظيفة.',
  ovrOnlyDirty:
    'لا يمكن اعتبار الغرفة نظيفة بهذه الطريقة إلا إذا كانت متسخة. أما العمل الجاري '
    + 'فيُلغى من لوحة الإسناد.',

  svcOffTitle: 'سحب الغرفة {room} من البيع',
  svcOnTitle: 'إعادة الغرفة {room} للبيع',
  svcOffIntro:
    'لن يتمكّن الاستقبال من بيع الغرفة {room} حتى يعيدها أحد. ويستمرّ التنظيف كالمعتاد — '
    + 'فالأمر يخصّ بيع الغرفة لا حالتها.',
  svcOnIntro:
    'يمكن بيع الغرفة {room} مجدّدًا فور تنفيذ ذلك، تبعًا لحالتها ولمن فيها.',
  svcOffReasonLabel: 'سبب سحبها من البيع',
  svcOnReasonLabel: 'سبب إعادتها للبيع',
  svcOffReasonHint: 'غرفة مسحوبة من البيع بلا سبب مدوَّن لا يجرؤ أحد على إعادتها.',
  svcOnReasonHint: 'اذكر ما الذي انتهى. تعود الغرفة للبيع فورًا.',
  svcTakeOff: 'سحبها من البيع',
  svcPutBack: 'إعادتها للبيع',
  svcOffNow: 'هذه الغرفة مسحوبة من البيع.',
  svcOutOfOrderNote:
    'هذه الغرفة خارج الخدمة بسبب عطل مُبلَّغ عنه. عالِج العطل لإعادتها للبيع.',

  histTitle: 'ماذا حدث',
  histEmpty: 'لم يحدث شيء لهذه الغرفة خلال آخر {days} أيام.',
  histLoading: 'جارٍ قراءة سجل الغرفة\u2026',
  histBy: 'بواسطة {name}',
  histByNobody: 'من نظام الفندق',
  histTruncated: 'تُعرض آخر {count} فقط.',
  histCapturedEarlier: 'تم في {time}، وسُجّل في {recorded}',
  roomEvents: {
    STARTED: 'بدأ التنظيف',
    SUBMITTED: 'أُرسلت للتفتيش',
    ACCEPTED: 'اجتازت التفتيش',
    REJECTED: 'رُفضت في التفتيش',
    SOILED: 'عادت متسخة',
    SOILED_BEFORE_INSPECTION: 'عادت متسخة قبل التفتيش',
    CANCELLED: 'أُلغي العمل',
    OVERRIDDEN: 'اعتُبرت نظيفة دون تنظيف',
    TOUCH_UP: 'بدأ التنظيف السريع',
  },
  issuesTitle: 'الأعطال المبلَّغ عنها',

  escalationsTitle: 'يحتاج إليك',
  escalationsSubtitle: 'ما رصده النظام ولا يستطيع معالجته بنفسه',
  needsYou: 'يحتاج إليك',
  nothingNeedsYou: 'لا شيء يحتاج إليك',
  nothingNeedsYouBody: 'لا توجد غرفة متوقّفة، ولا وعد مكسور، والمخزون كافٍ. '
    + 'تمتلئ هذه الشاشة من تلقاء نفسها عندما يتغيّر شيء.',
  thingsNeedYou: {
    one: 'أمر واحد · لا شيء آخر يحرّكه',
    two: 'أمران · لا شيء آخر يحرّكهما',
    few: '{count} أمور · لا شيء آخر يحرّكها',
    many: '{count} أمرًا · لا شيء آخر يحرّكها',
    other: '{count} أمر · لا شيء آخر يحرّكها',
  },
  minutesOver: 'تأخّر {minutes}',
  minutesAgo: 'قبل {minutes}',
  now: 'الآن',
  today: 'اليوم',
  roomAndArrival: '{room} · الوصول {time}',
  roomItemAndPerson: '{room} {item} · {name}',
  ofPar: '{quantity} من {par} الحدّ',
  neverAccepted: {
    one: 'واحد منها لم يقبله أحد',
    two: 'اثنان منها لم يقبلهما أحد',
    few: '{count} منها لم يقبلها أحد',
    many: '{count} منها لم يقبلها أحد',
    other: '{count} منها لم يقبلها أحد',
  },
  failuresThisWeek: {
    one: 'إخفاق واحد هذا الأسبوع',
    two: 'إخفاقان هذا الأسبوع',
    few: '{count} إخفاقات هذا الأسبوع',
    many: '{count} إخفاقًا هذا الأسبوع',
    other: '{count} إخفاق هذا الأسبوع',
  },
  daysOfCover: {
    one: 'يكفي نحو يوم واحد بالمعدّل الأخير',
    two: 'يكفي نحو يومين بالمعدّل الأخير',
    few: 'يكفي نحو {count} أيام بالمعدّل الأخير',
    many: 'يكفي نحو {count} يومًا بالمعدّل الأخير',
    other: 'يكفي نحو {count} يوم بالمعدّل الأخير',
  },
  laundryTooSlow: 'المغسلة تستغرق نحو ٢٤ ساعة — غسل ما لديك لن يغطّي هذا.',

  /* ── W-75 · ما قد يكون معطَّلًا في الغرفة (Sprint 63) ────────────────────── */
  fcTitle: 'ما قد يكون معطَّلًا في الغرفة',
  fcIntro:
    'هذه الخيارات التي يراها عامل الغرف عند الإبلاغ عن عطل، وهي المجموعات التي تُحتسب بها '
    + 'تقارير الأعطال. نحن كتبناها؛ صحّحوها، وأضيفوا ما لدى الفندق، وأطفئوا ما ليس لديه.',
  fcColRaised: 'البلاغات',
  fcNeverRaised: 'لا شيء',
  fcRaised: {
    one: 'بلاغ واحد',
    two: 'بلاغان',
    few: '{count} بلاغات',
    many: '{count} بلاغًا',
    other: '{count} بلاغ',
  },
  fcStillOpen: {
    one: 'واحد ما زال مفتوحًا',
    two: 'اثنان ما زالا مفتوحين',
    few: '{count} ما زالت مفتوحة',
    many: '{count} ما زالت مفتوحة',
    other: '{count} ما زال مفتوحًا',
  },
  fcKeepHint:
    'يُحتفظ بأي تصنيف أُبلغ تحته عن شيء، لأن تلك البلاغات جزء من تاريخ هذا الفندق — '
    + 'وإطفاؤه يمنع عرضه ويترك البلاغات كما هي. ولا يُحذف إلا صف لم يُبلَّغ تحته شيء قط.',
  fcAdd: 'إضافة نوع عطل',
  fcAddTitle: 'نوع عطل جديد',
  fcAddIntro:
    'الرمز دائم: به تُحتسب كل تقارير الأعطال، ولا سبيل لتغييره لاحقًا يُبقي تلك التقارير '
    + 'متّسقة. أما الأسماء فليست كذلك — صحّحوها متى شئتم.',
  fcAddSave: 'أضِفه',

  /* ── W-22 · قائمة الغرف (Sprint 63) ────────────────────────────────────── */
  rlView: 'طريقة عرض الغرف',
  rlAsGrid: 'لوحة',
  rlAsList: 'قائمة',
  rlSortBy: 'الترتيب حسب هذا',
  rlRoom: 'الغرفة',
  rlType: 'النوع',
  rlFloor: 'الطابق',
  rlCondition: 'الحالة',
  rlOccupancy: 'النزيل',
  rlService: 'للبيع',
  rlAssigned: 'مع',
  rlDeparture: 'تُغادر',
  rlOcc: {
    VACANT: 'شاغرة',
    OCCUPIED: 'نزيل فيها',
    ARRIVING: 'وصول',
    DEPARTING: 'مغادرة اليوم',
  },
  rlSvc: {
    IN_SERVICE: 'للبيع',
    OUT_OF_SERVICE: 'مسحوبة من البيع',
    OUT_OF_ORDER: 'معطّلة',
  },

  /* ── W-21 · الغرفة (Sprint 62) ─────────────────────────────────────────── */
  rdBack: 'العودة إلى اللوحة',
  rdFloor: 'الطابق {code}',
  rdVip: 'ضيف مميّز',
  rdGuest: 'النزيل',
  rdGuestInResidence: 'النزيل مقيم',
  rdGuestLeaving: 'النزيل يغادر اليوم',
  rdNoArrivalToday: 'لا وصول اليوم',
  rdArrivalUnknown: 'هناك وصول — ولم يُحدَّد وقته في نظام الفندق',
  rdArrivingAt: 'الوصول الساعة {time}',
  rdAdults: {
    one: 'بالغ واحد',
    two: 'بالغان',
    few: '{count} بالغين',
    many: '{count} بالغًا',
    other: '{count} بالغ',
  },
  rdPeopleIn: {
    one: 'شخص واحد في الغرفة',
    two: 'شخصان في الغرفة',
    few: '{count} أشخاص في الغرفة',
    many: '{count} شخصًا في الغرفة',
    other: '{count} شخص في الغرفة',
  },
  rdSleeps: 'وتتّسع الغرفة لـ {count}',
  rdOverCapacity: '{count} أشخاص في غرفة أَسِرّتها أقل من ذلك.',
  rdLeavingAt: 'المغادرة الساعة {time}',
  rdWork: 'العمل المعلّق',
  rdNothingDue: 'لا عمل مستحق على هذه الغرفة',
  rdNothingDueBody: 'لا توجد مهمة تنظيف مفتوحة عليها اليوم.',
  rdCredits: '{credits} نقطة',
  rdEstimated: 'نحو {minutes}',
  rdStartedAt: 'بدأ الساعة {time}',
  rdStates: {
    CREATED: 'مُنشأة، بلا إسناد',
    ASSIGNED: 'مُسندة',
    STARTED: 'قيد التنظيف',
    SUBMITTED: 'سُلِّمت، بانتظار الفحص',
    REWORK: 'أُعيدت',
    COMPLETED: 'منتهية',
    CANCELLED: 'ملغاة',
  },
  rdWillFollow: 'ستُنظَّف وفق {name}، النسخة {version} — {items} بنود',
  rdWouldFollow: 'ستتبع {name}، النسخة {version} — {items} بنود',
  rdNeeds: 'ما تحتاجه هذه الغرفة',
  rdAboutRoom: 'عن هذه الغرفة',
  rdLastTime: 'المرة الماضية',
  rdCleanedOn: 'نُظِّفت في {date}',
  rdByPerson: 'على يد {name}',
  rdPassedFirstTime: 'اجتازت الفحص من أول مرة.',
  rdWasRejected: 'أُعيدت مرة واحدة على الأقل.',
  rdNotInspected: 'لم تُفحَص.',
  rdActs: 'ما يمكنك فعله',

  fdBack: 'العودة إلى اللوحة',
  fdTitle: 'العطل',
  fdReportedBy: 'أبلغ عنه {name}، {when}',
  fdCapturedEarlier: 'سُجّل على الهاتف الساعة {time} ووصلنا الساعة {recorded}.',
  fdOpenFor: 'مفتوح منذ {duration}',
  fdWasOpenFor: 'بقي مفتوحًا {duration}',
  fdAcknowledgedBy: 'استلمه {name}، {when}',
  fdClosedBy: 'أغلقه {name}، {when}',
  fdNobodyYet: 'لم يستلمه أحد بعد.',
  fdStates: {
    OPEN: 'مفتوح', ACKNOWLEDGED: 'مستلَم', RESOLVED: 'مُغلق', CANCELLED: 'ملغى',
  },
  fdPlace: 'المكان',
  fdRoomOnFloor: 'غرفة {number} · الطابق {floor}',
  fdPublicArea: 'منطقة عامة',
  fdTookPlaceOut: 'هذا هو العطل الذي أخرجها من الخدمة.',
  fdPlaceStillShut: 'ما زالت خارج الخدمة.',
  fdPlaceBackInUse: 'عادت إلى الخدمة.',
  fdShutBySomethingElse: 'هي خارج الخدمة لسبب آخر.',
  fdOtherFaults: {
    one: 'يوجد عطل آخر مفتوح هنا.',
    two: 'يوجد عطلان آخران مفتوحان هنا.',
    few: 'توجد {count} أعطال أخرى مفتوحة هنا.',
    many: 'يوجد {count} عطلًا آخر مفتوحًا هنا.',
    other: 'يوجد {count} عطل آخر مفتوح هنا.',
  },
  fdOtherBlocking: {
    one: 'أحدها معطِّل، لذا إغلاق هذا لن يعيدها إلى الخدمة.',
    two: 'اثنان منها معطِّلان، لذا إغلاق هذا لن يعيدها إلى الخدمة.',
    few: '{count} منها معطِّلة، لذا إغلاق هذا لن يعيدها إلى الخدمة.',
    many: '{count} منها معطِّلة، لذا إغلاق هذا لن يعيدها إلى الخدمة.',
    other: '{count} منها معطِّل، لذا إغلاق هذا لن يعيدها إلى الخدمة.',
  },
  fdNothingElseWrong: 'لا توجد أعطال أخرى مبلَّغ عنها هنا.',
  fdWhatWasSaid: 'نص البلاغ',
  fdNoDescription: 'لم يُكتب وصف.',
  fdHistory: 'كل خطوة',
  fdNoHistory: 'لم يُسجَّل شيء على هذا العطل بعد.',
  fdRaised: 'تم الإبلاغ',
  fdByNobody: 'لا اسم مسجَّل',
  fdActs: 'ما يمكنك فعله',
  fdAcknowledge: 'استلام',
  fdCancel: 'إلغاء البلاغ',
  fdCancelReasonBody: 'الإلغاء يعني أنّ هذا لم يكن عطلًا حقيقيًا، ولذلك يُسجَّل باسمك.',
  fdAlreadyClosed: 'هذا العطل مُغلق. لا يمكن عمل شيء آخر له هنا.',

  iqTitle: 'بانتظار الفحص',
  iqNav: 'للفحص',
  iqSubtitle: 'الأقدم أولًا. افتح واحدة لاعتمادها أو إعادتها.',
  iqBack: 'العودة إلى القائمة',
  iqEmpty: 'لا شيء بالانتظار.',
  iqAllClear: 'لا توجد غرف بانتظار الفحص.',
  iqHeadline: {
    one: 'غرفة واحدة بالانتظار منذ {oldest}.',
    two: 'غرفتان بالانتظار. أقدمها انتظرت {oldest}.',
    few: '{count} غرف بالانتظار. أقدمها انتظرت {oldest}.',
    many: '{count} غرفة بالانتظار. أقدمها انتظرت {oldest}.',
    other: '{count} غرفة بالانتظار. أقدمها انتظرت {oldest}.',
  },
  iqBlockedForYou: {
    one: 'واحدة منها من عملك أنت، فيتعيّن أن يوقّع عليها غيرك.',
    two: 'اثنتان منها من عملك أنت، فيتعيّن أن يوقّع عليهما غيرك.',
    few: '{count} منها من عملك أنت، فيتعيّن أن يوقّع عليها غيرك.',
    many: '{count} منها من عملك أنت، فيتعيّن أن يوقّع عليها غيرك.',
    other: '{count} منها من عملك أنت، فيتعيّن أن يوقّع عليها غيرك.',
  },
  iqOnFloor: 'الطابق {floor}',
  iqExceptions: {
    one: 'أمر واحد لم يستطع إنجازه.',
    two: 'أمران لم يستطع إنجازهما.',
    few: '{count} أمور لم يستطع إنجازها.',
    many: '{count} أمرًا لم يستطع إنجازها.',
    other: '{count} أمر لم يستطع إنجازه.',
  },
  iqCannot: {
    NOT_HANDED_IN: 'لم تُسلَّم هذه الغرفة بعد.',
    NOT_ASSIGNED: 'لم يُسنَد أحد إلى هذه الغرفة، فلا عمل يُوقَّع عليه.',
    OWN_WORK: 'هذا عملك أنت. يجب أن يفحصه شخص آخر.',
  },
  iqHandedInBy: 'سلّمها {name} الساعة {time}',
  iqFigures: 'الأرقام',
  iqWaiting: 'الانتظار',
  iqTaken: 'استغرقت',
  iqNotRecorded: 'غير مسجَّل',
  iqAgainst: 'مقابل {minutes}',
  iqTheList: 'ما طُلب منه',
  iqAnsweredOf: '{answered} من {total}',
  iqNoChecklist: 'لم يكن لهذه الغرفة قائمة تحقّق.',
  iqHeDidIt: 'أشّر أنه أنجزه.',
  iqHeCouldNot: 'لم يستطع إنجازه.',
  iqNotAnswered: 'لم يجب على هذا.',
  iqFail: 'إخفاق',
  iqPriorFailures: {
    one: 'أخفق في هذا مرة واحدة هذا الأسبوع.',
    two: 'أخفق في هذا مرتين هذا الأسبوع.',
    few: 'أخفق في هذا {count} مرات هذا الأسبوع.',
    many: 'أخفق في هذا {count} مرة هذا الأسبوع.',
    other: 'أخفق في هذا {count} مرة هذا الأسبوع.',
  },
  iqVerdict: 'قرارك',
  iqMarked: {
    one: 'سطر واحد مؤشَّر.',
    two: 'سطران مؤشَّران.',
    few: '{count} أسطر مؤشَّرة.',
    many: '{count} سطرًا مؤشَّرًا.',
    other: '{count} سطر مؤشَّر.',
  },
  iqWhyBack: 'لماذا تعود؟',
  iqSendBack: 'أعدها',
  iqPass: 'اعتماد الغرفة',
  iqPassed: 'تم الاعتماد. الغرفة جاهزة.',
  iqSentBack: 'أُعيدت. عادت إلى قائمته.',

  rpTitle: 'للإرسال إلى الطوابق',
  rpSubtitle: 'ما ينقص كل مخزن طابق، وهل يغطّيه المخزن الرئيسي.',
  rpNothingShort: 'كل مخازن الطوابق عند مستواها المطلوب.',
  rpOnFloor: 'الطابق {floor}',
  rpHas: 'المتوفّر',
  rpInStore: 'في المخزن',
  rpSendCol: 'أرسل',
  rpSend: 'أرسلها',
  rpStoreShort: 'المخزن لا يغطّي هذا',
  rpNeedsOtherRole: 'تحريك المخزون يحتاج دورًا آخر.',
  rpLines: {
    one: 'سطر واحد للإرسال.',
    two: 'سطران للإرسال.',
    few: '{count} أسطر للإرسال.',
    many: '{count} سطرًا للإرسال.',
    other: '{count} سطر للإرسال.',
  },
  rpSent: {
    one: 'أُرسل سطر واحد.',
    two: 'أُرسل سطران.',
    few: 'أُرسلت {count} أسطر.',
    many: 'أُرسل {count} سطرًا.',
    other: 'أُرسل {count} سطر.',
  },
  rpShortTitle: 'المخزن لا يكفي لتعبئة مخازن الطوابق',
  rpShortBody: 'هذا طلب شراء، لا عربة تُحمَّل.',
  rpShortLine: 'المطلوب {needed} لكل مخازن الطوابق، والمتوفّر في المخزن {held}',

  lnTitle: 'في المغسلة',
  lnSubtitle: 'ما الموجود هناك، ومنذ متى.',
  lnNoLaundry: 'لم تُحدَّد مغسلة لهذا الفندق.',
  lnNothingThere: 'لا يوجد شيء في المغسلة.',
  lnThere: 'هناك',
  lnOut: 'أقدم ما بالخارج',
  lnSent: 'أُرسل هذا الأسبوع',
  lnBack: 'عاد هذا الأسبوع',
  lnHours: '{hours} س',
  lnLate: 'متأخّر',
  lnAllOnTime: 'لا شيء بقي في المغسلة أكثر من {hours} ساعة.',
  lnOverdue: {
    one: 'صنف واحد بقي في المغسلة أكثر من {hours} ساعة.',
    two: 'صنفان بقيا في المغسلة أكثر من {hours} ساعة.',
    few: '{count} أصناف بقيت في المغسلة أكثر من {hours} ساعة.',
    many: '{count} صنفًا بقي في المغسلة أكثر من {hours} ساعة.',
    other: '{count} صنف بقي في المغسلة أكثر من {hours} ساعة.',
  },
  lnNote: 'يُفترض أنّ ما نزل أولًا يعود أولًا — فالمفروشات غير معلَّمة، والعمر هنا عمر '
    + 'الرصيد لا عمر قطعة بعينها. وعمودا «أُرسل» و«عاد» يغطّيان آخر سبعة أيام.',

  plTitle: 'الأماكن',
  plSubtitle: 'أين تُحفظ المفروشات، وما اسم كل مكان.',
  plAdd: 'أضف مكانًا',
  plName: 'الاسم',
  plNameAr: 'الاسم بالعربية',
  plKind: 'النوع',
  plKinds: {
    STORE: 'مخزن', PANTRY: 'مخزن طابق', LAUNDRY: 'مغسلة', TROLLEY: 'عربة',
  },
  plHolding: 'أصناف موجودة',
  plPars: 'حدود محدَّدة',
  plOnFloor: 'الطابق {floor}',
  plRetired: 'موقوف',
  plRetire: 'أوقفه',
  plRestore: 'أعده',
  plRemove: 'احذفه',
  plInLedger: 'في {count} حركة',
  plCodeWillBe: 'سيكون رمزه {code}. يُكتب مرة واحدة ولا يتغيّر.',
  plNote: 'المكان الذي ذكره السجل مرة واحدة يمكن إيقافه لا حذفه — فكل حركة مسجَّلة تشير '
    + 'إليه. والعربات معروضة هنا لكنها تخصّ ثنائيًا وتُنشأ مع الفريق الذي يحملها.',

  itTitle: 'الأصناف',
  itSubtitle: 'الأشياء التي يجردها هذا الفندق، واسم كل صنف.',
  itAdd: 'أضف صنفًا',
  itName: 'الاسم',
  itNameAr: 'الاسم بالعربية',
  itKind: 'النوع',
  itKinds: {
    CIRCULATING: 'يعود', CONSUMABLE: 'يذهب مع النزيل',
  },
  itKindSettled: 'حسمه السجل',
  itKindWarning: 'يمكن اختيار ما إذا كان الصنف يعود الآن، لا بعد أن تتحرك منه قطعة '
    + 'واحدة. فهو يحدّد ما إذا كان الفندق لا يزال يملك القطعة التي ذهبت إلى غرفة.',
  itUnit: 'يُعدّ بـ',
  itUnits: { piece: 'قطع', pair: 'أزواج', set: 'أطقم' },
  itOwned: 'المملوك',
  itInStandards: 'في {count} معيارًا',
  itRetired: 'موقوف',
  itRetire: 'أوقفه',
  itRestore: 'أعده',
  itRemove: 'احذفه',
  itInLedger: 'في {count} حركة',
  itCodeWillBe: 'سيكون رمزه {code}. يُكتب مرة واحدة ولا يتغيّر.',
  itNote: 'الصنف الذي ذكره السجل مرة واحدة يمكن إيقافه لا حذفه — فكل حركة مسجَّلة تشير '
    + 'إليه. وكونه يعود أو لا يعود تحسمه أول حركة، لأن تغييره بعدها يعيد تفسير كل حركة '
    + 'سابقة.',

  lgTitle: 'الحركات',
  lgSubtitle: 'كيف صار كل رقم في هذا الفندق على ما هو عليه.',
  lgItem: 'الصنف',
  lgAllItems: 'كل الأصناف',
  lgPlace: 'المكان',
  lgAnywhere: 'أي مكان',
  lgEitherDirection: 'كل ما مرّ بهذا المكان، دخولًا أو خروجًا.',
  lgWhen: 'متى',
  lgWhat: 'ماذا جرى',
  lgHowMany: 'الكمية',
  lgWhereFrom: 'من',
  lgWhereTo: 'إلى',
  lgWho: 'من قام بها',
  lgToRoom: 'غرفة {room}',
  lgFromACount: 'ضمن جرد',
  lgKinds: {
    RECEIPT: 'استلام', TRANSFER: 'نقل', ISSUE: 'صرف إلى غرفة',
    RETURN: 'إعادة', LOSS: 'شطب', ADJUSTMENT: 'تصحيح',
  },
  lgShowing: 'تعرض من {from} إلى {to} من {total}',
  lgNewer: 'الأحدث',
  lgOlder: 'الأقدم',
  lgNothing: 'لم تتحرك أي كمية',
  lgNothingBody: 'لا توجد حركة تطابق ما اخترته. وكل رصيد في هذا الفندق محسوب من هذه '
    + 'القائمة، فخلوّها يعني أن الأرقام كلها أصفار.',
  lgNote: 'لا يمكن تعديل شيء هنا ولا حذفه، وهذا ما يجعله جديرًا بالقراءة. فالخطأ يُصحَّح '
    + 'بتسجيل حركة في الاتجاه المعاكس، فيظهر التصحيح بجوار ما صحّحه.',

  shTitle: 'أوقات عمل هذا الفندق',
  shIntro: 'هذه الأوقات ليست مجرّد تسمية. فطول الدوام هو حدّ النقاط لكل مسار على لوحة '
    + 'الإسناد، وتقصيره ينقص العمل عن كل من هو مُدرج عليه — من لحظة الحفظ.',
  shName: 'الدوام',
  shNameAr: 'الاسم بالعربية',
  shStarts: 'يبدأ',
  shEnds: 'ينتهي',
  shBudget: 'نقاط المسار',
  shWas: 'كان {credits}',
  shAdd: 'أضف دوامًا',
  shRetire: 'أوقفه',
  shRestore: 'أعده',
  shRetired: 'موقوف',
  shRostered: 'عُمل به في {count} يومًا',
  shClockShape: 'اكتب الوقت هكذا ٠٨:٠٠.',

  close: 'إغلاق',
  rdOpenTask: 'كل ما جرى على هذا العمل',
  tdTitle: 'العمل على {where}',
  tdWhat: 'ما هو',
  tdKind: 'نوع التنظيف',
  tdState: 'إلى أين وصل',
  tdStates: {
    CREATED: 'مُنشأ، ولم يُسند لأحد',
    ASSIGNED: 'مُسنَد',
    STARTED: 'قيد التنظيف',
    SUBMITTED: 'سُلِّم، بانتظار التفتيش',
    REWORK: 'أُعيد',
    COMPLETED: 'منتهٍ',
    CANCELLED: 'أُلغي',
  },
  tdWorth: 'كم كان يساوي',
  tdCredits: '{credits} نقطة',
  tdEstimate: 'المدة المتوقعة',
  tdRaisedBy: 'من أنشأه',
  tdBySweep: 'مسح الصباح',
  tdByAPerson: 'طلبه أحدهم',
  tdWhen: 'متى',
  tdStarted: 'أول بداية',
  tdSubmitted: 'سُلِّم',
  tdCompleted: 'انتهى',
  tdNotYet: 'ليس بعد',
  tdFirstStartNote: 'أول بداية هي الأولى فعلًا، والغرفة التي أُعيدت ونُظفت ثانية تحتفظ '
    + 'بها. والفارق بين هذين الوقتين ليس المدة التي قضاها أحد في الغرفة.',
  tdWhoHeld: 'من تولّاه',
  tdNobodyHeld: 'لم يُسند هذا لأحد بعد.',
  tdStillHolding: 'ما زال لديه',
  tdGivenBy: 'أسنده {who}',
  tdChecklist: 'قائمة التحقق كما أُجيبت',
  tdAnswers: { YES: 'تم', NO: 'لم يتم' },
  tdUnanswered: 'بلا إجابة',
  tdLinen: 'المفروشات',
  tdItem: 'الصنف',
  tdStandard: 'المعيار',
  tdUsed: 'المستخدم',

  sysTitle: 'النظام',
  sysSubtitle: 'ما يستطيعه هذا الخادم، وما يوشك السجل أن ينساه',
  sysAllWell: 'كل ما يعتمد عليه هذا الخادم يعمل.',
  sysNotReady: '{count} من الأمور التي يعتمد عليها هذا الخادم غير جاهزة للاستخدام الفعلي. '
    + 'وهي مذكورة أدناه مع ما يعمل عليه كلٌّ منها.',
  sysChecksTitle: 'على أي شيء يعمل هذا الخادم',
  sysChecks: {
    identityProvider: 'من يتحقّق من الدخول',
    fileStorage: 'أين تُحفظ الصور',
    /* **NOT "PDF".** W-110 already says «تنزيل ملف للطباعة» in Arabic, and a supervisor
       who reads both screens should meet the same words for the same thing. */
    pdfRenderer: 'إخراج تقرير كملف للطباعة',
    database: 'قاعدة البيانات',
  },
  sysStates: {
    OK: 'يعمل',
    PREVIEW: 'غير جاهز للاستخدام الفعلي',
    MISSING: 'غير متوفّر',
  },
  sysNotes: {
    configuredProvider: 'يتحقَّق عبر مزوّد الفندق نفسه.',
    previewProvider: 'بديل مؤقّت يمنح الدخول دون التحقّق منه — فكل من يصل إلى هذا العنوان '
      + 'يستطيع الدخول باسم أي شخص. لا يُستخدم مع موظّفين حقيقيّين.',
    storageCovered: 'موضع أكّد الفندق أنه يبقى بعد استبدال الخادم.',
    storageLocalDisk: 'قرص هذا الخادم نفسه. لم يؤكّد أحد أنه منسوخ احتياطيًا، والصورة '
      + 'المحفوظة عليه تذهب مع الجهاز — دون أي خطأ يظهر عند ذلك.',
    rendererAvailable: 'متوفّرة.',
    rendererMissing: 'لا يوجد متصفّح على هذا الخادم، فالتقارير تُقرأ على الشاشة ولا تُخرَج '
      + 'كملف للطباعة. وزرّ الطباعة يقول ذلك بدل أن يفشل.',
    databaseAnswered: 'أجابت عن استعلام حقيقي، لا مجرّد وصول الطلب إلى هنا.',
    databaseUnreachable: 'لم تُجب. وهذا ما قالته:',
  },
  sysBlockedTitle: 'في انتظار الفندق',
  sysBlockedBody: 'هذه ليست عيوبًا في المنتج، بل قرارات وتفاصيل ما زالت مطلوبة من جهة '
    + 'الفندق قبل أن يُستخدم هذا الخادم لعمل حقيقي.',
  sysRetentionTitle: 'مدة حفظ السجل',
  sysRetentionBody: 'كل تجاوز وكل تغيير في الصلاحيات وكل تصحيح يُحفظ {months} شهرًا ثم '
    + 'يُحذف.',
  sysOldest: 'أقدم مدخل ما زال محفوظًا',
  sysLogEmpty: 'لم يُكتب شيء بعد',
  sysHeld: 'المدخلات المحفوظة',
  sysExpiring: 'مدخلات تُحذف في الثلاثين يومًا القادمة',
  sysRetentionNote: 'هذه المدة ثابتة، وعدم إمكان تغييرها من الشاشة مقصود — فتقصيرها وسيلة '
    + 'لحذف أشهر من الأدلّة برقم واحد، ومن كان سيحتاج تلك المدخلات هو من لن يُخبَر بذلك.',
  sysAboutTitle: 'عن هذا الخادم',
  sysEnvironment: 'يعمل بوصفه',
  sysVersion: 'الإصدار',
  sysServerTime: 'ساعة الخادم',
  sysHotelZone: 'المنطقة الزمنية للفندق',
  sysHotelDate: 'تاريخ الفندق الآن',

  dvTitle: 'الأجهزة',
  dvSubtitle: 'ما هو مسجَّل الدخول، ومن أي نوع من الأجهزة',
  dvWhatThisIs: 'اقرأ هذا قبل التصرّف بناءً عليه',
  dvNoDeviceIdentity:
    'هذه ليست قائمة أجهزة. لا شيء هنا يمنح هاتفًا أو حاسوبًا هويةً — فكل المعروف هو ما قاله '
    + 'كل تسجيل دخول عن نفسه، وقد يكون خاطئًا، وهو نفسه في جهازين متطابقين. والسطر أدناه هو '
    + 'شخص واحد ونوع واحد من الأجهزة، لا جهاز بعينه.',
  dvWhatItIsFor:
    'وما تصلح له: العثور على شيء ما زال مسجَّل الدخول ولا ينبغي أن يكون — بعد مغادرة موظّف، '
    + 'أو ضياع جهاز، أو في نهاية موسم.',
  dvHeadlineSessions: {
    one: 'تسجيل دخول واحد مفتوح.',
    two: 'تسجيلا دخول مفتوحان.',
    few: '{count} تسجيلات دخول مفتوحة.',
    many: '{count} تسجيل دخول مفتوحًا.',
    other: '{count} تسجيل دخول مفتوح.',
  },
  dvHeadlineAcross: {
    one: 'وهو لدى شخص واحد.',
    two: 'وهما لدى شخصين.',
    few: 'وهي لدى {count} أشخاص.',
    many: 'وهي لدى {count} شخصًا.',
    other: 'وهي لدى {count} شخص.',
  },
  dvStale: {
    one: 'واحد منها لم يُستخدم منذ أكثر من يوم.',
    two: 'اثنان منها لم يُستخدما منذ أكثر من يوم.',
    few: '{count} منها لم تُستخدم منذ أكثر من يوم.',
    many: '{count} منها لم يُستخدم منذ أكثر من يوم.',
    other: '{count} منها لم يُستخدم منذ أكثر من يوم.',
  },
  dvGroupings: {
    one: 'سطر واحد أدناه يحمل أكثر من تسجيل دخول. وقد يكون جهازًا واحدًا سجّل الدخول أكثر '
      + 'من مرة أو أجهزة عدة — ولا سبيل هنا للتمييز بينها.',
    two: 'سطران أدناه يحملان أكثر من تسجيل دخول. وقد يكون كلٌّ منهما جهازًا واحدًا سجّل '
      + 'الدخول أكثر من مرة أو أجهزة عدة — ولا سبيل هنا للتمييز بينها.',
    few: '{count} سطور أدناه تحمل أكثر من تسجيل دخول. وقد يكون كلٌّ منها جهازًا واحدًا سجّل '
      + 'الدخول أكثر من مرة أو أجهزة عدة — ولا سبيل هنا للتمييز بينها.',
    many: '{count} سطرًا أدناه تحمل أكثر من تسجيل دخول. وقد يكون كلٌّ منها جهازًا واحدًا سجّل '
      + 'الدخول أكثر من مرة أو أجهزة عدة — ولا سبيل هنا للتمييز بينها.',
    other: '{count} سطر أدناه يحمل أكثر من تسجيل دخول. وقد يكون كلٌّ منها جهازًا واحدًا سجّل '
      + 'الدخول أكثر من مرة أو أجهزة عدة — ولا سبيل هنا للتمييز بينها.',
  },
  dvSignedIn: 'تسجيلات الدخول المفتوحة',
  dvManySessions: {
    one: 'تسجيل دخول واحد على هذا السطر',
    two: 'تسجيلا دخول على هذا السطر',
    few: '{count} تسجيلات دخول على هذا السطر',
    many: '{count} تسجيل دخول على هذا السطر',
    other: '{count} تسجيل دخول على هذا السطر',
  },
  dvSince: 'أول دخول {when}',
  dvDaysAgo: {
    one: 'قبل يوم',
    two: 'قبل يومين',
    few: 'قبل {count} أيام',
    many: 'قبل {count} يومًا',
    other: 'قبل {count} يوم',
  },
  dvUnknownNote: {
    one: 'تسجيل دخول واحد لم يقل عن نفسه شيئًا معروفًا. وهو مذكور بوصفه غير معروف بدل أن '
      + 'يُخمَّن ما هو.',
    two: 'تسجيلا دخول لم يقولا عن نفسيهما شيئًا معروفًا. وهما مذكوران بوصفهما غير معروفين '
      + 'بدل أن يُخمَّن ما هما.',
    few: '{count} تسجيلات دخول لم تقل عن نفسها شيئًا معروفًا. وهي مذكورة بوصفها غير معروفة '
      + 'بدل أن يُخمَّن ما هي.',
    many: '{count} تسجيل دخول لم تقل عن نفسها شيئًا معروفًا. وهي مذكورة بوصفها غير معروفة '
      + 'بدل أن يُخمَّن ما هي.',
    other: '{count} تسجيل دخول لم يقل عن نفسه شيئًا معروفًا. وهو مذكور بوصفه غير معروف بدل '
      + 'أن يُخمَّن ما هو.',
  },
  dvEnd: 'أنهِه',
  dvEndAll: 'أنهِ الكل',
  dvNobody: 'لا أحد مسجَّل الدخول',
  dvNobodyBody: 'لا يوجد تسجيل دخول مفتوح في هذا الفندق الآن.',

  propButton: 'اقترح من يأخذ ماذا',
  propHeadline: 'المقترح: {count} غرف.',
  propNothing: 'لا يوجد ما يُقترح.',
  propNothingMoved: 'لم يتغيّر شيء بعد — هذا اقتراح.',
  propRow: '{room} ← {name}',
  propOffFloor: '(ليس في طابقه)',
  propUnplaced: '{count} لم يمكن إسنادها إلى أحد: {rooms}.',
  propUnplacedWhy: {
    NOT_ASSIGNABLE: 'لم يقل أحد بعد كيف تُنظَّف هذه الغرف.',
    NOBODY_FREE: 'كل من في الدوام بلغ ساعاته.',
    NOBODY_FREE_ON_FLOOR: 'من يعملون في تلك الطوابق بلغوا ساعاتهم. وهناك من لديه وقت، '
      + 'لكن الغرف لا تُقترح إلا على من يعمل في الطابق — ضع أحدهم في الطابق، أو انقل '
      + 'البطاقة بنفسك.',
    NOBODY_ON_SHIFT: 'لا أحد في الدوام ليأخذها.',
  },
  propIdle: 'سيترك هذا {names} بلا عمل. فالغرف لا تُقترح إلا على من يعمل في ذلك الطابق، '
    + 'ومن ليس في طابق لا يأخذ شيئًا. ضعه في طابق، أو انقل بطاقة بنفسك.',
  propApprove: 'أسندها',
  propApplying: 'يُسند {done} من {total}',
  propDiscard: 'تجاهله',

  ntfTitle: 'ما الذي حدث',
  ntfSubtitle: 'ما فعله الآخرون بعملك',
  ntfSentence: {
    ROOM_CAME_BACK: 'أعاد {who} إليك الغرفة {room}.',
    WORK_TAKEN_OFF_YOU: 'سحب {who} منك الغرفة {room}.',
    WORK_GIVEN_TO_YOU: 'أسند إليك {who} الغرفة {room}.',
    YOUR_FAULT_WAS_CLOSED: 'أغلق {who} العطل الذي بلّغت عنه في الغرفة {room}.',
    ADJUSTMENT_NEEDS_YOU: 'ينتظر {who} أن تبتّ في تصحيح: {room}.',
    YOUR_ADJUSTMENT_WAS_DECIDED: 'بتّ {who} في تصحيحك: {room}.',
  },
  ntfSomebody: 'أحدهم',
  ntfARoom: 'غرفة',
  ntfUnread: '{count} منذ آخر مرة نظرت فيها.',
  ntfAllSeen: 'لا جديد منذ آخر مرة نظرت فيها.',
  ntfMarkSeen: 'علّمها مقروءة',
  ntfNothingTitle: 'لم يحدث شيء لعملك',
  ntfNothingBody: 'لم ينقل أحد غرفك، ولم يُعِد إليك غرفة، ولم يُغلق عطلًا بلّغت عنه خلال '
    + 'الأسبوعين الماضيين.',
  ntfScope: 'يغطّي هذا الأسبوعين الماضيين: ما فعله الآخرون بعملك، وما ينتظر قرارًا منك. أمّا ما هو قائم '
    + 'الآن فمكانه اللوحات لا هنا.',

  /* ── W-10 · لوحة اليوم (Sprint 61) ───────────────────────────────────────── */
  opsTitle: 'اليوم',
  opsSubtitle: 'أين يقف الفندق الآن',
  opsQuestion: 'هل نغطّي اليوم؟',
  opsArrivals: {
    one: 'وصول واحد',
    two: 'وصولان',
    few: '{count} وصولات',
    many: '{count} وصولًا',
    other: '{count} وصول',
  },
  opsReady: {
    one: 'غرفة واحدة جاهزة',
    two: 'غرفتان جاهزتان',
    few: '{count} غرف جاهزة',
    many: '{count} غرفة جاهزة',
    other: '{count} غرفة جاهزة',
  },
  opsNoArrivals: 'لا وصول اليوم',
  opsNoArrivalsBody:
    'اليوم مغادرات وإقامات مستمرّة. ومع ذلك تحتاج الغرف أدناه إلى التجهيز للغد.',
  opsCovered: 'اليوم مغطّى',
  opsCoveredBody: 'لكل وصول غرفة جاهزة. أبقِ الطوابق تتحرّك.',
  opsShort: {
    one: 'يجب أن تجهز غرفة واحدة إضافية',
    two: 'يجب أن تجهز غرفتان إضافيتان',
    few: 'يجب أن تجهز {count} غرف إضافية',
    many: 'يجب أن تجهز {count} غرفة إضافية',
    other: 'يجب أن تجهز {count} غرفة إضافية',
  },
  opsShortBy: 'أول وصول بلا غرفة جاهزة الساعة {time}.',
  opsShortNoTime: 'لا يحمل أي وصول اليوم وقتًا، فالرقم هو كم لا متى.',
  opsCannot: {
    one: 'تنقص غرفة واحدة، ولا يمكن تدبيرها من الطوابق',
    two: 'تنقص غرفتان، ولا يمكن تدبيرهما من الطوابق',
    few: 'تنقص {count} غرف، ولا يمكن تدبيرها من الطوابق',
    many: 'تنقص {count} غرفة، ولا يمكن تدبيرها من الطوابق',
    other: 'تنقص {count} غرفة، ولا يمكن تدبيرها من الطوابق',
  },
  opsCannotBody:
    'حتى بتنظيف كل ما يمكن تنظيفه يبقى الفندق ناقصًا. هذا يُحلّ مع الاستقبال لا من هذه الشاشة.',
  opsRecoverable: {
    one: 'يمكن أن تجهز غرفة واحدة اليوم',
    two: 'يمكن أن تجهز غرفتان اليوم',
    few: 'يمكن أن تجهز {count} غرف اليوم',
    many: 'يمكن أن تجهز {count} غرفة اليوم',
    other: 'يمكن أن تجهز {count} غرفة اليوم',
  },
  opsRooms: 'الغرف',
  opsDepartures: 'المغادرات',
  opsDeparturesNote: 'من {expected} متوقّعة',
  opsOpenTasks: 'عمل مفتوح',
  opsRunning: 'قيد التنظيف',
  opsToInspect: 'للفحص',
  opsWaitingOldest: 'أقدمها {minutes}',
  opsWaitingNone: 'لا شيء بالانتظار',
  opsFaults: 'أعطال مفتوحة',
  opsBlocking: 'معطِّلة',
  opsNotReady: 'غير جاهزة، ولماذا',
  opsAllReady: 'كل غرف الفندق جاهزة.',
  opsReason: {
    AWAITING_CLEAN: 'بانتظار التنظيف',
    BEING_CLEANED: 'قيد التنظيف',
    AWAITING_INSPECTION: 'بانتظار الفحص',
    OUT_OF_SERVICE: 'خارج الخدمة',
    OUT_OF_ORDER: 'معطّلة',
    STILL_OCCUPIED: 'النزيل ما زال فيها',
  },
  opsFloors: 'من على الطوابق',
  opsNobodyHolding: 'لا أحد يحمل غرفًا',
  opsNobodyHoldingBody: 'لم يُوزَّع عمل اليوم بعد.',
  opsLaneDone: 'منجزة',
  opsLaneIn: 'في {room}',
  opsLaneRework: '{count} معادة',
  opsLaneLoad: '{credits} من {budget}',
  opsLaneNoBudget: 'ليس على جدول اليوم',
  opsDecisions: 'يحتاج قرارًا',
  opsAsAt: 'حتى الساعة {time}',
  cfgTitle: 'الإعدادات',
  cfgSubtitle: 'كلمات هذا الفندق، وما يمكن للنزيل طلبه',
  cfgIntro:
    'النصوص العربية في كل الشاشات كتبناها نحن، والغرض منها أن تُصحَّح. أي تغيير هنا يظهر '
    + 'في كل مكان — القائمة الجانبية والتقارير والأجهزة.',
  cfgHotel: 'هذا الفندق',
  cfgName: 'الاسم',
  cfgNameAr: 'الاسم بالعربية',
  cfgNameArHint: 'اتركه فارغًا إذا كان الاسم الإنجليزي هو الاسم في اللغتين.',
  cfgCode: 'الرمز المختصر',
  cfgCodeHint: 'حُدِّد عند إضافة الفندق. اتصل بنا إذا لزم تغييره.',
  cfgTimeZone: 'المنطقة الزمنية',
  cfgTimeZoneHint: 'كل ما يعتبره الفندق «اليوم» يأتي من هنا — اللوحة والجدول وكل تقرير.',
  cfgLanguage: 'تُفتح الشاشات بـ',
  cfgLanguageHint: 'ما يراه الشخص قبل أن يختار لنفسه.',
  cfgInspection: 'هل تنتظر الغرفة النظيفة من يفحصها؟',
  cfgInspectionHint: 'هذا يحدّد متى تصبح الغرفة قابلة للبيع، لا من يفحصها فقط.',
  cfgInspectionYes: 'نعم — يفحصها المشرف أولًا',
  cfgInspectionNo: 'لا — تصبح جاهزة بقول عامل التنظيف',
  cfgInspectionOffWarning:
    'بإيقاف هذا تصبح الغرفة معروضة للبيع بقول شخص واحد، ويُسجَّل العمل منتهيًا لحظة تسليمه. '
    + 'ولا يفحصها أحد، وستبقى شاشات الفحص وتقاريره فارغة لأنه لن يكون فيها ما يُعرض.',
  cfgLanguageEn: 'الإنجليزية',
  cfgLanguageAr: 'العربية',
  cfgSaveHotel: 'حفظ بيانات الفندق',
  cfgHotelSaved: 'تمّ الحفظ.',
  cfgRoles: 'أسماء الأدوار',
  cfgRolesIntro:
    'الأسماء فقط. أما صلاحيات كل دور فهي في شاشة الأدوار والصلاحيات، ولا يغيّرها شيء هنا.',
  cfgColRole: 'الدور',
  cfgColName: 'الاسم',
  cfgColNameAr: 'الاسم بالعربية',
  cfgColHeld: 'يحمله',
  cfgHeld: {
    one: 'شخص واحد',
    two: 'شخصان',
    few: '{count} أشخاص',
    many: '{count} شخصًا',
    other: '{count} شخص',
  },
  cfgNobodyHolds: 'لا أحد هنا',
  cfgRoleSaved: 'تمّ الحفظ.',
  cfgSave: 'حفظ',
  cfgSaving: 'جارٍ الحفظ…',
  rcTitle: 'ما يمكن للنزيل طلبه',
  rcIntro:
    'هذه أنواع الطلبات التي يعرضها الاستقبال ورمز الاستجابة في كل غرفة، والمدة الموعودة '
    + 'لكل نوع. بدأنا معك بستة أنواع، وهي اجتهاد منّا، ومن هنا تجعلها أنواعك أنت.',
  rcColCode: 'الرمز المختصر',
  rcColName: 'الاسم',
  rcColNameAr: 'الاسم بالعربية',
  rcColPromise: 'الوعد خلال',
  rcColCountable: 'كم العدد؟',
  rcColStock: 'يُخصم من المخزون',
  rcColAsked: 'طُلب',
  rcMinutes: {
    one: 'دقيقة واحدة',
    two: 'دقيقتان',
    few: '{count} دقائق',
    many: '{count} دقيقة',
    other: '{count} دقيقة',
  },
  rcNeverAsked: 'لم يُطلب',
  rcAsked: {
    one: 'مرة واحدة',
    two: 'مرتان',
    few: '{count} مرات',
    many: '{count} مرة',
    other: '{count} مرة',
  },
  rcNoStock: 'لا شيء',
  rcCountableYes: 'نعم — يُسأل عن العدد',
  rcCountableNo: 'لا',
  rcCountableLabel: 'اسأل عن العدد',
  rcRetired: 'موقوف',
  rcRetire: 'إيقاف',
  rcRestore: 'إعادة التشغيل',
  rcDelete: 'حذف',
  rcKeepHint:
    'نوع الطلب الذي طلبه النزلاء من قبل يبقى في تقاريرك ولا يمكن حذفه — أوقفه بدلًا من '
    + 'ذلك فيتوقف عرضه من حينها. أما ما لم يُطلب قط فيمكن حذفه نهائيًا.',
  rcAdd: 'إضافة نوع طلب',
  rcAddTitle: 'نوع طلب جديد',
  rcAddIntro:
    'الرمز المختصر هو ما تُحتسب به كل التقارير، ولا يمكن تغييره بعد ذلك. أما ما عداه فيمكن.',
  rcCode: 'الرمز المختصر',
  rcCodeHint: 'حروف إنجليزية كبيرة وأرقام وشرطات سفلية، ويبدأ بحرف. دائم لا يتغيّر.',
  rcCodeSuggested: 'مقترح من الاسم. غيّره الآن إن أردت غير ذلك.',
  rcCodeBad: 'حروف إنجليزية كبيرة وأرقام وشرطات سفلية، ويبدأ بحرف.',
  rcPromise: 'الوعد خلال',
  rcPromiseHint: 'بالدقائق. أنواعك الأخرى بين ٣٠ و٦٠.',
  rcAddSave: 'أضِفه',
  rcCancel: 'إلغاء',
  rcConfirmDelete: 'هل تحذف {name}؟ لم يطلبه أحد، فلن يضيع شيء.',
  trTitle: 'الأسماء العربية',
  trSubtitle: 'كل ما سمّاه هذا النظام، وما تسمّونه أنتم',
  trIntro:
    'كل اسم عربي في الأسفل كتبناه نحن. وهو اجتهادنا في تسمية كل شيء كما نظن أن الفندق '
    + 'يسمّيه، وليس أيٌّ منها قرارًا منكم. غيّر أيًّا منها فيتغيّر في كل مكان — الشاشات '
    + 'والأجهزة والتقارير. أما الاسم الإنجليزي فهو ما يسمّيه النظام ويبقى كما هو.',
  trRemaining: {
    one: 'بقي واحد',
    two: 'بقي اثنان',
    few: 'بقي {count}',
    many: 'بقي {count}',
    other: 'بقي {count}',
  },
  trAllDone: 'لكل اسم اسمٌ عربي.',
  trNothingToDo: 'لم يُحمَّل شيء بعد، فلا شيء هنا لتسميته.',
  trColCode: 'الرمز المختصر',
  trColName: 'ما يسمّيه النظام',
  trColNameAr: 'ما تسمّونه',
  trGroupEmpty: 'لا شيء من هذا بعد.',
  trSave: 'حفظ',
  trSaving: 'جارٍ الحفظ…',
  trElsewhere:
    'اسم الفندق وأسماء الأدوار وأنواع طلبات النزلاء في شاشة الإعدادات، لأن الاسم الإنجليزي '
    + 'قابل للتغيير هناك أيضًا. أما سؤال قائمة التحقق فيُعاد صوغه بنشر نسخة جديدة من قالبه.',
  trKind: {
    BUILDING: 'المباني',
    ZONE: 'المناطق',
    ROOM_TYPE: 'أنواع الغرف',
    AREA: 'المناطق العامة',
    STOCK_ITEM: 'أصناف المخزون',
    STOCK_LOCATION: 'مواقع المخزون',
    TASK_TYPE: 'أنواع العمل',
    SHIFT: 'الورديات',
    TEMPLATE: 'قوالب قوائم التحقق',
  },
  trInUseFloors: {
    one: 'طابق واحد', two: 'طابقان', few: '{count} طوابق',
    many: '{count} طابقًا', other: '{count} طابق',
  },
  trInUseAreas: {
    one: 'منطقة واحدة', two: 'منطقتان', few: '{count} مناطق',
    many: '{count} منطقة', other: '{count} منطقة',
  },
  trInUseRooms: {
    one: 'غرفة واحدة', two: 'غرفتان', few: '{count} غرف',
    many: '{count} غرفة', other: '{count} غرفة',
  },
  glTitle: 'بيانات التشغيل',
  glSubtitle: 'ما يجب إدخاله قبل أول وردية',
  glIntro:
    'بالترتيب الذي يجب اتّباعه. الأعداد الظاهرة هي أعداد هذا الفندق الآن — اذكر كم ينبغي '
    + 'أن يكون العدد ليخبرك الصف متى اكتمل.',
  glProgress: 'اكتمل {done} من {total}',
  glColWhat: 'البند',
  glColSource: 'طريقة الإدخال',
  glColExpected: 'المتوقّع',
  glColLoaded: 'المُدخَل',
  glColDepends: 'يحتاج أولًا',
  glColState: 'الحالة',
  glStep: {
    PROPERTIES_AND_FLOORS: 'المباني والطوابق',
    ROOM_TYPES: 'أنواع الغرف',
    ROOMS: 'الغرف',
    PUBLIC_AREAS: 'المناطق العامة',
    CHECKLIST_TEMPLATES: 'قوائم التحقق',
    CLEANING_RULES_PER_ROOM: 'قواعد التنظيف لكل غرفة',
    STOCK_ITEMS: 'المفروشات والمستهلكات',
    STOCK_LOCATIONS: 'المخازن والمقالب',
    OPENING_STOCK_COUNT: 'الجرد الافتتاحي',
    PEOPLE_AND_ROLES: 'الموظفون والأدوار',
    FIRST_ROSTER: 'أول جدول دوام',
  },
  glSource: {
    BY_HAND: 'إدخال يدوي',
    SPREADSHEET: 'ملف جدول',
    IN_BULK: 'تطبيق جماعي',
    PHYSICAL_COUNT: 'جرد ميداني',
  },
  glState: {
    DONE: 'مكتمل',
    SHORT: 'ناقص',
    LOADED: 'مُدخَل',
    NOT_STARTED: 'لم يبدأ',
    WAITING: 'بانتظار غيره',
    BLOCKED: 'متوقّف',
  },
  glMissing: {
    one: 'ينقص واحد',
    two: 'ينقص اثنان',
    few: 'تنقص {count}',
    many: 'ينقص {count}',
    other: 'ينقص {count}',
  },
  glSignInNote:
    'الموظفون والأدوار ينتظران طريقة التحقّق من تسجيل الدخول، وهي سؤال ما زال مفتوحًا '
    + 'لدينا. يمكن منح أي شخص في القائمة دورًا اليوم، لكن لا يمكن التحقّق من أحد عند دخوله.',
  glNoTargetNote:
    'الصفوف التي تقول «مُدخَل» فيها بيانات بلا رقم يُقاس عليه. اذكر كم ينبغي أن يكون '
    + 'العدد لتتمكّن من معرفة اكتمالها.',
  glWorkedOut: 'يُحتسب تلقائيًا',
  glTargetLabel: 'كم ينبغي أن يكون عدد {what}؟',
  glNothingYet: 'لا شيء بعد',
  glDays: {
    one: 'يوم واحد',
    two: 'يومان',
    few: '{count} أيام',
    many: '{count} يومًا',
    other: '{count} يوم',
  },

  alert: {
    soldRoomOutOfOrder: 'غرفة محجوزة وخارج الخدمة',
    requestsLate: {
      one: 'طلب واحد متأخّر',
      two: 'طلبان متأخّران',
      few: '{count} طلبات متأخّرة',
      many: '{count} طلبًا متأخّرًا',
      other: '{count} طلب متأخّر',
    },
    repeatFailure: 'تكرّر الإخفاق نفسه مرّتين',
    stockBelowPar: '{item} تحت الحدّ',
  },
  issuesEmpty: 'لا توجد بلاغات.',
  requestsTitle: 'طلبات النزلاء',
  requestsEmpty: 'لا توجد طلبات قيد الانتظار.',
  severities: { MINOR: 'بسيط', MAJOR: 'كبير', BLOCKING: 'معطِّل' },
  issuePhotos: 'الصور',
  issuePhotoCount: '{n}',
  issuePhotoTaken: 'التُقطت {when}',
  issuePhotoBy: 'بواسطة {name}',
  issuePhotoFailed: 'الصورة مسجّلة لكن الملف مفقود من وحدة التخزين. '
    + 'أبلغ عنها مع المرجع {correlationId}.',
  issuePhotoClose: 'إغلاق',
  requestCategories: {
    EXTRA_TOWELS: 'مناشف إضافية', EXTRA_PILLOW: 'وسادة إضافية',
    TURNDOWN: 'خدمة تجهيز المساء', AMENITIES: 'مستلزمات', OTHER: 'أخرى',
  },
  issueTookRoomOut: 'هذه الغرفة خارج الخدمة بسبب هذا العطل.',
  issueNeedsOtherRole: 'رفع العطل المعطِّل يحتاج دورًا آخر.',
  resolve: 'إغلاق البلاغ',
  fulfil: 'تم',
  late: 'متأخر',
  reasonTitle: 'اذكر السبب',
  reasonBody: 'هذا يعيد الغرفة إلى البيع، وسيُسجَّل باسمك.',
  save: 'حفظ',
  stockOwnedTitle: 'ما يملكه الفندق',
  stockOwnedSubtitle: 'المفروشات تبقى ملكنا عند إرسالها للغرف. المستهلكات لا.',
  stockWhereTitle: 'أين توجد',
  stockItem: 'الصنف',
  stockLocation: 'المكان',
  stockQuantity: 'هنا',
  stockPar: 'الحد الأدنى',
  stockOwned: 'المملوك',
  stockInLocations: 'في المخازن',
  stockInRooms: 'في الغرف',
  stockCirculating: 'مفروشات',
  stockConsumable: 'مستهلك',
  stockAllLocations: 'كل الأماكن',
  stockBelowPar: '{count} دون الحد الأدنى.',
  stockEmpty: 'لا يوجد شيء مسجَّل هنا بعد.',
  stockTitle: 'المفروشات والمخزون',

  rosterTitle: 'جدول الدوام',
  rosterSubtitle: 'من يعمل، ومتى — من السبت إلى الجمعة.',
  rosterEmpty: 'لا يوجد بعد من يعمل ورديات التدبير الفندقي في هذا الفندق.',
  rosterToday: 'اليوم',
  rosterThisWeek: 'هذا الأسبوع',
  rosterPrevWeek: 'الأسبوع السابق',
  rosterNextWeek: 'الأسبوع التالي',
  rosterPerson: 'الموظف',
  rosterUnset: '—',
  rosterUnsetHint: 'غير محدد',
  /* NOT "بعهدته". The pronoun is masculine, and most of this workforce is women — a
     string that misgenders two thirds of the roster on every row. Naming the thing
     instead of its holder avoids the agreement entirely and reads shorter. */
  rosterHolding: 'مفتوحة: {count}',
  rosterHoldingWarning:
    'بعهدة {name} {count} من المهام المفتوحة — يبقى هذا العمل في مساره ولا بد من نقله يدويًا.',
  rosterPickShift: 'أي وردية؟',
  rosterCoverage: 'التغطية (اختياري)',
  rosterCoveragePlaceholder: 'الطوابق ٣–٥',
  rosterSave: 'حفظ',
  rosterCancel: 'إلغاء',
  rosterSaved: 'تم الحفظ.',
  rosterUnwritten: 'لم يُحدَّد بعد {count} من هذا الأسبوع.',
  reportsTitle: 'التقارير',
  reportsSubtitle: 'اقرأه على الشاشة، ثم اطبع المستند نفسه.',
  reportsPick: 'أي تقرير؟',
  reportsNone: 'لا توجد تقارير متاحة لك.',
  reportsNoneBody:
    'تُمنح التقارير حسب الدور. إن كنت تتوقع تقريراً هنا فراجع من أنشأ حسابك — لا يوجد خلل.',
  reportsFrom: 'من',
  reportsTo: 'إلى',
  /* "مكتوب بـ" and not "اللغة": the control chooses the language of the DOCUMENT, and
     a box labelled "language" beside a screen that already has a language toggle reads
     as a second copy of that toggle. */
  reportsLanguage: 'مكتوب بـ',
  reportsRun: 'عرض',
  /* WITHOUT THE ACRONYM, for the same reason the error catalogue drops it: "PDF" is
     Latin, and a Latin fragment in an Arabic line breaks the run — the bidi algorithm
     reorders around it and the label reads as though somebody forgot to translate half
     of it. "A file for printing" is what the button does anyway. */
  reportsDownload: 'تنزيل ملف للطباعة',
  reportsPreview: 'التقرير',
  loadTitle: 'تحميل الغرف',
  loadSubtitle: 'أدخل طوابق الفندق وأنواع غرفه وغرفه من ملف جدول بيانات.',
  loadIntro:
    'ارفع قائمة غرف الفندق وستعرض هذه الشاشة ما سيحدث تمامًا عند تحميلها. '
    + 'لا يُكتب شيء حتى توافق.',
  loadColumns:
    'يحتاج الملف إلى صف بأسماء الأعمدة، وثلاثة منها ضرورية: رقم الغرفة والطابق والنوع. '
    + 'وأضف عمود «المبنى» إن كان للفندق أكثر من مبنى — فبدونه لا سبيل للتمييز بين '
    + 'الطابق الثالث في مبنى والطابق الثالث في آخر. '
    + 'أما اسم النوع والاسم بالعربية ودقائق التنظيف فتُستخدم إن وُجدت. '
    + 'يُفضّل إرسال ملف اكسل — فملف النص المفصول بفواصل يجب حفظه بترميز يونيكود ليحتفظ بالعربية.',
  loadChoose: 'اختر ملفًا',
  loadChooseAnother: 'اختر ملفًا آخر',
  loadReading: 'جارٍ قراءته…',
  loadWouldHappen: 'ماذا سيحدث',
  loadNewRooms: 'غرف مضافة',
  loadMovedRooms: 'غرف مُنقولة',
  loadUnchangedRooms: 'غرف دون تغيير',
  loadNotInFile: 'في الفندق وليست في هذا الملف',
  loadNotInFileNote: 'تُترك كما هي تمامًا. لا شيء هنا يُغلق غرفة.',
  loadFloorsAndTypes: '{floors} طوابق جديدة · {types} أنواع غرف جديدة · {changed} أُعيدت تسميتها',
  loadBuildingsNew: '{count} مبانٍ جديدة',
  loadLeftOutOfUse: '{count} خارج الخدمة، وتُركت كذلك',
  loadConverted: '{count} صفوف بأرقام عربية، قُرئت بالأرقام اللاتينية',
  loadCommit: 'حمّلها',
  loadLoading: 'جارٍ التحميل…',
  loadFixFirst: 'صحّح الأسطر أدناه ثم ارفع الملف من جديد — التحميل كامل أو لا شيء.',
  loadDone: 'تم التحميل. أُضيفت {rooms} غرفة، ونُقلت {moved}.',
  loadProblems: '{count} بحاجة إلى تصحيح',
  loadLine: 'السطر {line}',
  loadRooms: '{count} غرفة في الملف',
  loadColRoom: 'الغرفة',
  loadColBuilding: 'المبنى',
  loadTypes: '{count} أنواع غرف في الملف',
  loadColTypeName: 'الاسم',
  loadColSleeps: 'تتّسع لـ',
  loadColMinutes: 'الدقائق',
  loadColRooms: 'الغرف',
  loadColFloor: 'الطابق',
  loadColType: 'النوع',
  loadColChange: '',
  loadChanges: {
    NEW: 'جديدة',
    CHANGED: 'تغيّرت',
    UNCHANGED: 'دون تغيير',
    LEFT_OUT_OF_USE: 'خارج الفندق',
  },

  /* ── غرفة واحدة في كل مرة ───────────────────────────────────────────── */
  setupTitle: 'الغرف والطوابق',
  setupSubtitle: 'شكل الفندق: ما غرفه، وفي أي طابق، ومن أي نوع.',
  editTab: 'غرفة واحدة',
  loadTab: 'من ملف جدول',
  editTitle: 'غرف الفندق',
  editListTitle: '{count} غرفة',
  editCardHint: 'في هذه الغرفة بطاقة. تغيير الرقم يعني طباعة بطاقة جديدة.',
  editIntro:
    'اليوم الذي يتغيّر فيه رقم الغرفة 305، أو تُهدم غرفة فتزول. إخراج غرفة من الفندق '
    + 'ليس كإيقافها عن الخدمة: الغرفة الموقوفة عن الخدمة تبقى من غرف الفندق وتبقى على اللوحة.',
  editSearch: 'ابحث عن غرفة',
  editAdd: 'أضف غرفة',
  editShowRemoved: 'أظهر الغرف الخارجة من الفندق',
  editNoMatch: 'لا توجد غرفة مطابقة.',
  editEmpty: 'لا غرف في هذا الفندق بعد. حمّلها من ملف جدول أولًا.',
  editColStatus: '',
  cleanColDeparture: 'تنظيف بعد المغادرة',
  cleanColStayover: 'تنظيف إقامة مستمرة',
  cleanColMinutes: 'الدقائق',
  cleanMinutesHint: 'مدة التنظيف بعد المغادرة. وتُحسب بقية الأعمال منها.',
  divisorCurrent: 'الوحدة = {minutes} دقيقة',
  divisorTitle: 'قيمة وحدة العمل',
  divisorIntro:
    'تُحسب كل حصة في لوحة التوزيع بوحدات العمل، وهذا عدد دقائق العمل التي تعادل وحدة واحدة. '
    + 'تغييره يعيد تسعير كل غرف الفندق في الوقت نفسه — لا تطول مدة أي غرفة، لكن كل رقم '
    + 'يوازن به المشرف دورته سيتغيّر.',
  divisorLabel: 'الدقائق لكل وحدة',
  divisorHint: 'بين ٥ و١٢٠.',
  divisorPreview:
    'الغرفة {room} تستغرق {minutes} دقيقة، فتنتقل من {was} وحدة إلى {now}.',
  divisorSaved: 'أصبحت وحدة العمل تعادل {minutes} دقيقة من العمل.',
  cleanColCredits: 'الوحدات',
  cleanColDeepClean: 'التنظيف العميق',
  cleanNotSet: 'غير محدَّد',
  cleanNeverPublished: 'لم تُنشر أي نسخة بعد',
  cleanUnknownTemplate: 'قائمة تحقق لم تعد مستخدمة',
  cleanEdit: 'طريقة التنظيف',
  /* THE NOUN CHANGES, NOT A SUFFIX — which is why one template with a number in it
     cannot be right in Arabic however carefully it is worded. */
  cleanUnconfigured: {
    one: 'هناك غرفة واحدة لا يمكن إسنادها إلى أحد حتى يُحدَّد كيف تُنظَّف.',
    two: 'هناك غرفتان لا يمكن إسنادهما إلى أحد حتى يُحدَّد كيف تُنظَّفان.',
    few: 'هناك {count} غرف لا يمكن إسنادها إلى أحد حتى يُحدَّد كيف تُنظَّف.',
    many: 'هناك {count} غرفة لا يمكن إسنادها إلى أحد حتى يُحدَّد كيف تُنظَّف.',
    other: 'هناك {count} غرفة لا يمكن إسنادها إلى أحد حتى يُحدَّد كيف تُنظَّف.',
  },
  cleanFixThem: 'حدِّدها الآن',
  cleanTitleOne: 'طريقة تنظيف الغرفة {room}',
  cleanTitleMany: 'طريقة تنظيف {count} غرفة',
  cleanIntro:
    'قائمة التحقق التي تُعطى للموظف، وقيمة الغرفة ضمن الجولة، وعدد مرات التنظيف العميق. '
    + 'الغرفة التي بلا قائمة تحقق وبلا دقائق لا يمكن إسنادها إطلاقًا.',
  cleanIntroMany:
    'كل خانة تتركها فارغة تبقى كما هي في كل غرفة. وملء أي خانة يضبطها على جميع الغرف في هذه القائمة.',
  cleanApplyDefaults: 'طبّق الإعدادات الافتراضية لـ{type}',
  cleanNoDefaults: 'لا توجد إعدادات افتراضية لهذا النوع من الغرف بعد.',
  cleanChoose: 'اختر قائمة تحقق',
  cleanLeaveAlone: 'اتركها كما هي',
  cleanNotes: 'أي ملاحظة خاصة بتنظيف هذه الغرفة',
  cleanSaved: 'تم الحفظ.',
  cleanNowAssignable: 'يمكن الآن إسناد {count} غرفة إلى أحد.',
  deepClean: {
    NONE: 'غير مجدول',
    MONTHLY: 'شهريًا',
    QUARTERLY: 'كل ثلاثة أشهر',
    TWICE_YEARLY: 'مرتين سنويًا',
    YEARLY: 'سنويًا',
  },
  editColCard: 'البطاقة',
  editInHotel: 'داخل الفندق',
  editOutOfHotel: 'خارج الفندق',
  editHasCard: 'لها بطاقة',
  editEdit: 'تعديل',
  editRemove: 'أخرِجها من الفندق',
  editRestore: 'أعِدها إلى الفندق',
  editSave: 'حفظ',
  editSaving: 'يُحفظ…',
  editCancel: 'إلغاء',
  editNewRoom: 'غرفة جديدة',
  editRoomNumber: 'رقم الغرفة',
  editRoomNumberHint: 'كما هو مكتوب على الباب.',
  editFloor: 'الطابق',
  editType: 'نوع الغرفة',
  editAddFloor: 'أضف طابقًا',
  editFloorCode: 'الطابق',
  editAddBuilding: 'أضف مبنًى',
  typesTitle: 'أنواع الغرف',
  typesName: 'الاسم',
  typesSleeps: 'تتّسع لـ',
  typesSleepsNote:
    'عدد الأشخاص الذين تتّسع لهم الغرفة. وهو ما يُكتب عليه معيار المفروشات لهذا النوع، '
    + 'فتغييره يغيّر ما يدخل من كل باب من أبواب هذا النوع.',
  typesMinutes: 'الدقائق',
  typesMinutesNote:
    'نقطة البداية لأي غرفة جديدة من هذا النوع. أما الغرف الموجودة فتبقى على الزمن '
    + 'المحدّد لها.',
  typesRooms: 'الغرف',
  typesEditTitle: 'نوع الغرفة',

  linenTitle: 'ما تحتاجه التنظيفة',
  linenSubtitle: 'ما يحتاجه كل نوع من الغرف لكل نوع من التنظيف.',
  linenIntro:
    'هذه هي القائمة التي تُعرض على الموظف قبل فتح الباب، وهي الرقم الذي تُقارن به العربة '
    + 'في نهاية الوردية. اختر نوع التنظيف، ثم حدّد كم يحتاج كل نوع من الغرف من كل صنف.',
  linenOurs:
    'الأرقام التي بدأت بها من عندنا لا من عندكم — مأخوذة من حزمة التصميم المعتمدة ومضروبة '
    + 'بعدد الأشخاص الذين تتّسع لهم الغرفة. ولا يعاملها النظام على أنها مرجع. صحّحوها هنا.',
  linenForWork: 'لهذا النوع من التنظيف',
  linenGrid: 'الأصناف حسب نوع الغرفة',
  linenItem: 'الصنف',
  linenSleeps: 'تتّسع لـ {count}',
  linenNone: 'لا شيء',
  linenNote:
    'الخانة الفارغة تعني ألّا يُحمل شيء من هذا الصنف — وهي ليست صفرًا، ولا يظهر الصنف في '
    + 'قائمة الموظف. وتسري التعديلات على التنظيفة التالية، ولا يُعاد احتساب ما أُنجز. '
    + 'ولا يُرفض على أحد استخدام غير ذلك: المعيار هو ما يجعل الفرق ظاهرًا.',
  editBuildingCode: 'الرمز',
  editBuildingName: 'الاسم',
  editReasonRemove: 'لماذا تخرج هذه الغرفة من الفندق؟',
  editReasonRestore: 'لماذا تعود هذه الغرفة؟',
  editReasonHint: 'سيسأل أحدهم عن السبب بعد ستة أسابيع.',
  editRemovedBecause: 'خارج الفندق منذ {date} — {reason}',
  editNothingChanged: 'لم يتغيّر شيء.',
  editCreated: 'أُضيفت الغرفة {roomNumber}.',
  editRenumbered: 'أصبح رقمها {roomNumber}.',
  editMoved: 'نُقلت الغرفة {roomNumber}.',
  editRemoved: 'الغرفة {roomNumber} خارج الفندق الآن، وأُلغيت بطاقتها.',
  editRestored: 'عادت الغرفة {roomNumber} إلى الفندق، وتحتاج إلى تنظيف قبل بيعها.',
  editReprint:
    'بطاقة الغرفة {roomNumber} ما زالت تعمل، لكن الرقم المطبوع عليها صار خطأ. '
    + 'اطبع بطاقة جديدة من شاشة رموز الاستجابة السريعة.',
  editConverted: 'حُفظت باسم {roomNumber} — تُكتب أرقام الغرف بالأرقام اللاتينية.',
  editRestoreWarning: 'تعود بحاجة إلى تنظيف، مهما كانت حالتها حين خرجت.',

  /* ── رموز الغرف ─────────────────────────────────────────────────────── */
  /* SHORT ENOUGH FOR THE SIDEBAR. The formal Arabic for a QR code is
     "رمز الاستجابة السريعة", which the navigation truncated to an ellipsis — a label
     nobody can read is not a label. The subtitle carries the full sense. */
  qrTitle: 'رموز الغرف',
  qrSubtitle: 'إصدار رمز كل باب وإلغاؤه وإعادة طباعته.',
  qrIntro:
    'يمسح النزيل البطاقة بجانب سريره ويطلب مناشف دون حساب. الرمز خلف كل سطر قيمة '
    + 'عشوائية وليس رقم الغرفة أبدًا — فلو كان الرقم لاستطاع كل من يعرف العدّ أن يرفع '
    + 'طلبًا على أي غرفة في الفندق دون أن يزورها.',
  qrWhereTheyStand: 'حال البطاقات',
  qrRooms: 'الغرف',
  qrLive: 'بطاقات فعّالة',
  qrNever: 'بلا بطاقة',
  qrRevoked: 'ملغاة',
  qrRequests: 'طلبات عبر البطاقات',
  qrIssueMissing: 'أصدر البطاقات الناقصة ({count})',
  qrIssuing: 'يجري الإصدار…',
  qrIssuedCount: 'صدرت {count} بطاقة. اطبع الورقة وضع البطاقات الجديدة.',
  qrNothingMissing: 'لكل غرفة بطاقة.',
  qrPrint: 'اطبع البطاقات',
  qrPrinting: 'يجري تجهيز الورقة…',
  qrPrintHint: 'ست بطاقات في الصفحة مع خطوط القص. تُطبع البطاقات الفعّالة فقط.',

  /* ── ممّ يتكوّن التنظيف ──────────────────────────────────────────────── */
  clTitle: 'قوائم التنظيف',
  clHowVersionsWork: 'كيف تعمل النسخ',
  clListTitle: '{count} قائمة',
  clSubtitle: 'ممّ يتكوّن التنظيف · نسخ محفوظة، ولا تُعدّل في مكانها.',
  clIntro:
    'لا تُعدّل القائمة المعمول بها — أي تغيير ينشئ نسخة جديدة. كل تنظيف منجز يسجّل النسخة '
    + 'التي اتّبعها، فتبقى غرفة نُظّفت في تموز مقروءة بقواعد تموز. ولولا ذلك لأعاد تعديل '
    + 'القائمة كتابة تاريخ كل تفتيش استعملها.',
  clLive: 'معمول بها',
  clDrafts: 'مسوّدة',
  clRetired: 'متقاعدة',
  clNew: 'قائمة جديدة',
  clColTemplate: 'القائمة',
  clColAppliesTo: 'تنطبق على',
  clColVersion: 'النسخة',
  clColItems: 'البنود',
  clColTime: 'الزمن المعياري',
  clColInUse: 'معمول بها منذ',
  clColState: '',
  clStateLive: 'معمول بها',
  clStateDraft: 'مسوّدة',
  clStateNever: 'غير مستعملة',
  clEdit: 'تعديل',
  clEditDraft: 'أكمل المسوّدة',
  clNeverPublished: 'لم تُنشر بعد',
  clEmpty: 'لا قوائم تنظيف في هذا الفندق بعد.',
  clMinutes: '{count} دقيقة',
  clBack: 'كل القوائم',
  clDraftOf: 'مسوّدة · مبنية على النسخة {live} · غير مستعملة بعد',
  clDraftFirst: 'مسوّدة · أول نسخة من هذه القائمة · غير مستعملة بعد',
  clItemsTitle: 'البنود بالترتيب',
  clItemsHint: 'الترتيب هو ما يراه العامل على الجهاز.',
  clAddItem: 'أضف بندًا',
  clItemCode: 'الرمز',
  clItemEn: 'الإنجليزية',
  clItemAr: 'العربية',
  clRequired: 'إلزامي',
  clOptional: 'اختياري',
  clPhoto: 'صورة',
  clRemove: 'احذف',
  clUp: 'إلى أعلى',
  clDown: 'إلى أسفل',
  clChangesTitle: 'ما الذي يتغيّر عن النسخة {live}',
  clChangesNone: 'لا شيء بعد. هذه المسوّدة مطابقة للنسخة المعمول بها.',
  clChanges: {
    ADDED: '{code} — أُضيف',
    REMOVED: '{code} — حُذف',
    RELABELLED: '{code} — تغيّرت صياغته',
    BECAME_REQUIRED: '{code} — صار إلزاميًا',
    BECAME_OPTIONAL: '{code} — صار اختياريًا',
    PHOTO_NOW_REQUIRED: '{code} — صارت الصورة مطلوبة',
    PHOTO_NO_LONGER_REQUIRED: '{code} — لم تعد الصورة مطلوبة',
    MOVED: '{code} — تغيّر موضعه',
  },
  clPhotoCost:
    'نحو {count} صورة إضافية في اليوم، على أساس {cleans} تنظيفًا يوميًا. وهذه مسألة تخزين '
    + 'ورفع لا مسألة تصميم — يُستحسن حسمها الآن.',
  clPhotoSaving: 'نحو {count} صورة أقل في اليوم، على أساس {cleans} تنظيفًا يوميًا.',
  clPhotoUnknown:
    'لم يسجّل هذا الفندق تنظيفات منجزة كافية بعد لتقدير كلفة الصور اليومية.',
  clNotReEvaluated:
    'الغرف التي نُظّفت وفق النسخة {live} تحتفظ بسجلّها. ولا يُعاد تقييم شيء منجز.',
  clPublish: 'انشرها كنسخة {version}',
  clPublishing: 'يجري النشر…',
  clPublished: 'نُشرت كنسخة {version}. كل تنظيف يبدأ من الآن يتّبعها.',
  clSave: 'احفظ المسوّدة',
  clSaving: 'يُحفظ…',
  clSaved: 'حُفظت المسوّدة. ولا يُعمل بها حتى تُنشر.',
  clNameEn: 'اسم هذه القائمة',
  clNameAr: 'اسمها بالعربية',
  clSaveName: 'احفظ الاسم',
  clNameNoVersion: 'تغيير الاسم لا يُنشئ نسخة جديدة.',
  clNameSaved: 'تم حفظ الاسم.',
  clDiscard: 'احذف المسوّدة',
  clDiscarded: 'حُذفت المسوّدة. ولم تُمسّ النسخة المعمول بها.',
  clNewTemplate: 'قائمة جديدة',
  clTaskType: 'لأي نوع من المهام؟',
  clCreate: 'أنشئ',
  qrShowRevoked: 'أظهر البطاقات الملغاة',
  qrSearch: 'ابحث عن غرفة',
  qrFloor: 'الطابق {floor}',
  qrColRoom: 'الغرفة',
  qrColIssued: 'تاريخ الإصدار',
  qrColRequests: 'الطلبات',
  qrColState: '',
  qrStateLive: 'فعّالة',
  qrStateNever: 'بلا بطاقة',
  qrStateRevoked: 'ملغاة',
  qrIssue: 'أصدر',
  qrRevoke: 'ألغِ',
  qrReprint: 'أعد الطباعة',
  qrRevokeReason: 'لماذا تُلغى هذه البطاقة؟',
  qrRevokeHint:
    'يكلّف ذلك بطاقة جديدة على الباب. وبعد ستة أسابيع لن يوضّح غير هذا السبب هل '
    + 'أُسيء استعمال الرمز أم أُعيد ترقيم الطابق.',
  qrRevokeConfirm: 'ألغِ البطاقة',
  qrRevoked1: 'أُلغيت البطاقة. أصدر بطاقة جديدة واطبعها قبل بيع الغرفة من جديد.',
  qrIssued1: 'صدرت بطاقة للغرفة {roomNumber}، ولم توضع على الباب بعد.',
  qrSuperseded: 'حلّت محلها بطاقة أخرى — {reason}',
  qrNoMatch: 'لا توجد غرفة مطابقة.',
  qrEmpty: 'لا غرف في هذا الفندق بعد. حمّلها من «الغرف والمناطق» أولًا.',
  rosterStates: {
    ON_SHIFT: 'على وردية',
    REST_DAY: 'يوم راحة',
    LEAVE: 'إجازة',
    ABSENT: 'غياب',
  },

  areasTitle: 'المناطق العامة',
  areasSubtitle: 'مجمّعة حسب المنطقة، لا حسب الطابق',
  areasNeedAttention: '{count} بحاجة إلى متابعة',
  areasAllOnSchedule: 'كل شيء ضمن الجدول.',
  areasEmpty: 'لم تُضف مناطق لهذا الفندق بعد.',
  areasEvery: 'كل {interval}',
  areasLateBy: 'متأخرة {duration}',
  areasDueIn: 'تستحق بعد {duration}',
  areasNeverCleaned: 'لم تُنظّف قط',
  areasLastDone: 'آخر تنظيف {when}',
  areasMarkClean: 'تسجيل التنظيف',
  areaSvcClose: 'إغلاقها',
  areaSvcReopen: 'إعادة فتحها',
  areaSvcCloseTitle: 'إغلاق {area}',
  areaSvcReopenTitle: 'إعادة فتح {area}',
  areaSvcCloseIntro:
    'ما دامت مغلقة فلن تظهر مستحقة ولا متأخرة، ولا يمكن تسجيل تنظيف لها. وما رُفع عليها '
    + 'من عمل يبقى — لا يُصرف أحد.',
  areaSvcReopenIntro:
    'تعود إلى الجدول مباشرة، ويُحتسب تأخرها من آخر تنظيف لا من هذه اللحظة.',
  areaSvcReasonLabel: 'السبب',
  areaSvcCloseReasonHint:
    'جملة واحدة. هي الشيء الوحيد الذي سيفسّر، بعد أسبوعين، لماذا اختفت هذه المنطقة من '
    + 'قوائم الجميع.',
  areaSvcReopenReasonHint: 'جملة واحدة — ما الذي تغيّر.',
  areaSvcClosedNow: 'مغلقة حاليًا.',
  areaSvcOutOfOrderNote:
    'عُطل يُبقي هذه المنطقة خارج الخدمة، فليست فتحها أو إغلاقها من هنا بيدك. تعود عند '
    + 'إصلاح العُطل.',
  areaSvcCancel: 'إلغاء',
  areaSvcClosed: 'أُغلقت {name}.',
  areaSvcReopened: 'أُعيد فتح {name}.',
  areasMarked: 'تم تسجيل {name}.',
  areasUngrouped: 'بلا منطقة',
  areaStandings: {
    ON_SCHEDULE: 'ضمن الجدول',
    DUE_SOON: 'تستحق قريبًا',
    OVERDUE: 'متأخرة',
    MISSED: 'فائتة',
    BEING_CLEANED: 'قيد التنظيف',
    CLOSED: 'مغلقة',
    RESTING: 'خارج ساعات العمل',
  },

  assignTitle: 'لوحة الإسناد',
  assignSubtitle: 'أعمال اليوم حسب الموظف',
  unassigned: 'غير مُسنَد',
  credits: 'وحدة عمل',
  creditsShort: 'وحدة',
  workload: 'حجم العمل',
  assignHere: 'أسنِد هنا',
  assignTo: 'إسناد إلى',
  cancelSelection: 'إلغاء',
  selectedCard: 'محدَّدة',
  moving: 'جارٍ النقل',
  noWorkTitle: 'لا توجد أعمال على اللوحة اليوم',
  noWorkBody: 'أنشئ تنظيفات اليوم من غرف الفندق، أو أضف عملًا يدويًا.',
  genButton: 'أنشئ أعمال اليوم',
  genWorking: 'جارٍ مسح الفندق…',
  genRaised: 'أُنشئ {count} لليوم.',
  genNothingNew: 'لا جديد — اللوحة محدَّثة بالفعل.',
  genSwitchedOff: '{count} تحتاج «{what}»، وهذا الفندق لا يُنشئه',
  genBreakdown:
    '{departures} مغادرة · {stayovers} إقامة مستمرة · {deep} تنظيف عميق · '
    + '{refresh} لمسة أخيرة · {inHand} قيد العمل · '
    + '{nothing} لا تحتاج تنظيفًا · {shut} خارج الخدمة · {swept} غرفة إجمالًا',
  genUnassignable:
    'هذه الغرف عليها عمل اليوم ولا يمكن إسناده إلى أحد، لأن أحدًا لم يحدّد طريقة '
    + 'تنظيفها: {rooms}. حدّدها من شاشة الغرف والطوابق.',
  genDismiss: 'إخفاء',
  schedRunsAt: 'تعمل تلقائيًا في {time}',
  schedManual: 'تُنشأ يدويًا',
  schedChange: 'تغيير',
  schedTitle: 'موعد إنشاء أعمال اليوم',
  schedIntro:
    'يمكن إنشاء تنظيفات الفندق تلقائيًا كل صباح بتوقيت الفندق المحلي. ويبقى الزر في '
    + 'الحالتين — اضغطه متى أبلغ نظام الفندق عن مغادرات جديدة.',
  schedLabel: 'الإنشاء في',
  schedHint: 'بتوقيت الفندق المحلي. اتركه فارغًا للإنشاء يدويًا.',
  schedOff: 'إيقاف الجدولة',
  schedSavedOn: 'ستُنشأ أعمال اليوم في {time} من الآن فصاعدًا.',
  schedSavedOff: 'الجدولة متوقفة. اضغط الزر لإنشاء أعمال اليوم.',
  schedOverdue:
    'كان ينبغي إنشاء أعمال اليوم تلقائيًا في {time} ولم يحدث ذلك. اضغط الزر، وأبلغ '
    + 'المسؤول عن الخادم.',
  cardNotConfigured: 'غير مهيّأة',
  cardNotConfiguredWhy:
    'لم يحدّد أحد طريقة تنظيف الغرفة {room}، فلا يمكن إسناد هذا العمل إلى أحد. '
    + 'حدّدها من شاشة الغرف والطوابق.',
  laneEmpty: 'لا توجد مهام مُسنَدة',
  dropHere: 'أفلِت المهمة هنا للإسناد',
  notWorking: 'ليس على المناوبة. الإسناد متوقف.',
  overBudget: 'تجاوز {name} حجم العمل بمقدار {by} وحدة.',
  hasCapacity: 'لدى {name} {free} وحدة متاحة.',
  roster: {
    ON_SHIFT: 'على المناوبة',
    REST_DAY: 'يوم راحة',
    LEAVE: 'إجازة',
    ABSENT: 'غياب',
  },

  checklistTitle: 'قائمة التحقق',
  room: 'غرفة',
  doneOf: 'منجز',
  optional: 'اختياري',
  mandatory: 'إلزامي',
  cannotDo: 'تعذّر التنفيذ',
  whatStoppedYou: 'ما الذي منعك؟',
  saveException: 'حفظ',
  trgTitle: 'ما الذي يُنشئ العمل',
  trgSubtitle: 'خمسة أمور قد تضع غرفة على اللوحة. ولك أن توقف أيًّا منها.',
  trgNames: {
    GUEST_CHECKOUT: 'مغادرة نزيل',
    STAYOVER_SERVICE: 'بقاء النزيل ليلة أخرى',
    SCHEDULED_DEEP_CLEAN: 'حلول موعد التنظيف العميق',
    VACANT_ROOM_REFRESH: 'غرفة فارغة طال وقوفها',
    ON_REQUEST: 'طلب من أحدهم',
  },
  trgDoes: {
    GUEST_CHECKOUT: 'ينشئ المسح الصباحي تنظيفًا بعد المغادرة لكل غرفة غادرها نزيل أو '
      + 'يغادرها اليوم.',
    STAYOVER_SERVICE: 'ينشئ المسح تنظيف إقامة مستمرة لكل غرفة ينام فيها أحد الليلة.',
    SCHEDULED_DEEP_CLEAN: 'ينشئ المسح تنظيفًا عميقًا حين تبلغ الغرفة الوتيرة المحدَّدة لها '
      + '— شهريًا أو ربع سنوي أو مرتين في السنة أو سنويًا. أما الغرف المضبوطة على '
      + '«بلا» فلا يُجدوَل لها شيء.',
    VACANT_ROOM_REFRESH: 'ينشئ المسح لمسة أخيرة لغرفة فارغة مرتَّبة لم يدخلها أحد طوال '
      + 'عدد الأيام الذي تحدّده.',
    ON_REQUEST: 'ينشئ المشرف مهمة بيده لما لم ينشئه المسح. أوقف هذا فلا يستطيع أحد ذلك.',
  },
  trgEvery: 'بعد',
  trgDays: 'يومًا من الوقوف',
  trgCountsFrom: 'الغرف التي لم يُسجَّل لها تنظيف عميق بعد تُحتسب من {date}.',

  adjTitle: 'تصحيحات بانتظار البتّ',
  adjSubtitle: 'تصحيح رصيد الرفّ يبتّ فيه شخص آخر.',
  adjRaise: 'صحّح رصيدًا',
  adjNothingMoves: 'لم يتغيّر شيء بعد. يبتّ في هذا شخص آخر لديه صلاحية المخزون، ولا '
    + 'يتغيّر رصيد الرفّ إلا حين يفعل.',
  adjWhat: 'أيّ صنف، وأين',
  adjPickShelf: 'اختر صنفًا وموقعًا',
  adjBy: 'غيّر الرصيد بمقدار',
  adjByHint: '٤-',
  adjWhy: 'السبب',
  adjWhyHint: 'أربع ممزّقة في المغسلة',
  adjSend: 'أرسله للاعتماد',
  adjNoneWaiting: 'لا شيء بانتظار البتّ.',
  adjMovesBy: '{by} — من {from} إلى {to}',
  adjRaisedBy: '{who} · {when}',
  adjApprove: 'اعتمده',
  adjDecline: 'ارفضه',
  adjWhyNot: 'سبب الرفض',
  adjWhyNotHint: 'المغسلة ما زالت تحتفظ بها. اسأل يوم الجمعة.',
  adjYoursToWaitFor: 'أنت من طلب هذا التصحيح، لذا يبتّ فيه شخص آخر.',
  adjRecent: 'بُتّ فيها مؤخرًا',
  adjDecidedLine: '{what} {by} — {state} بواسطة {who}',
  adjStates: { PROPOSED: 'بانتظار البتّ', APPROVED: 'معتمد', DECLINED: 'مرفوض' },

  cancel: 'إلغاء',
  exceptionRecorded: 'تعذّر تنفيذه',
  submitRoom: 'تسليم الغرفة',
  submitting: 'جارٍ التسليم',
  submittedTitle: 'تم تسليم الغرفة',
  submittedBody: 'سيقوم مشرفك بتفتيشها.',
  stillNeeded: 'ما زال مطلوبًا {count}',
  waitingToSend: '{count} بانتظار الإرسال',
  submitAfterSending: 'يمكنك التسليم بعد إرسال عملك.',
  staleSince: 'تعرض معلومات من {time}. قد تكون تغيّرت.',
  workingOffline: 'العمل دون اتصال',
  loadFailed: 'تعذّر تحميل قائمة التحقق.',
  handsetNotice:
    'هذه معاينة وليست تطبيق الهاتف. القواعد وطابور العمل دون اتصال والنصوص هي النهائية، '
    + 'لكن منصة الهاتف لم تُعتمد بعد، لذا تعمل هذه الشاشة في المتصفح بعرض الجهاز.',

  /* ── W-105 و W-106 ──────────────────────────────────────────────────────── */
  cntTitle: 'جرد المخزون',
  cntSubtitle: 'اعدّ المخزن، ثم قرّر ما الذي يعنيه الفرق.',
  cntIndexTitle: 'أين نبدأ الجرد',
  cntIndexIntro:
    'أثناء فتح الجرد لا يمكن إدخال أو إخراج أي مخزون من ذلك الموقع، وهذا ما يمنع تغيّر '
    + 'الأرقام أثناء عدّ الرفوف. اعتماد الجرد أو إلغاؤه يرفع هذا التعليق.',
  cntStartAt: 'جرد {location}',
  cntStarting: 'جارٍ البدء…',
  cntNoLocations: 'لم تُعرَّف أي مواقع مخزون بعد.',
  cntRecent: 'عمليات الجرد',
  cntNoCounts: 'لم يُجرَ أي جرد بعد.',
  cntColReference: 'الجرد',
  cntColLocation: 'الموقع',
  cntColStarted: 'بدأ',
  cntColProgress: 'المعدود',
  cntColState: 'الحالة',
  cntOpen: 'قيد العدّ',
  cntSubmitted: 'بانتظار الاعتماد',
  cntApproved: 'معتمد',
  cntAbandoned: 'ملغى',
  cntOpenIt: 'فتح',
  cntBack: 'كل عمليات الجرد',
  cntSheetTitle: 'كشف الجرد · {reference}',
  cntBlindTitle: 'هذا جرد أعمى',
  cntBlindWhy:
    'الكمية المتوقعة غير معروضة، ولا تُرسَل إلى هذه الشاشة أصلًا. إظهارها يحوّل الجرد إلى '
    + 'تأكيد: يرى الشخص ٤١٢ فيعدّ ٤١٢ تقريبًا ويمضي. يظهر الفرق بعد تسليم الكشف.',
  cntProgress: 'عُدَّ {counted} من {total} صنفًا',
  cntColItem: 'الصنف',
  cntColCounted: 'المعدود',
  cntColCountedBy: 'عدّه',
  cntColTime: 'الوقت',
  cntColExpected: 'المتوقع',
  cntHidden: 'مخفي',
  cntUnreadable:
    'أحد الأرقام غير قابل للقراءة كعدد. لم يُحفظ شيء — راجع الحقول قبل الحفظ مرة أخرى '
    + 'حتى لا يُحسب إدخال خاطئ على أنه عدّ.',
  cntNotCounted: 'لم يُعدّ',
  cntSave: 'حفظ وإيقاف مؤقت',
  cntSaving: 'جارٍ الحفظ…',
  cntSaved: 'تم الحفظ. يمكنك إغلاق الكشف والعودة إليه لاحقًا.',
  cntHandOver: 'تسليم للاعتماد',
  cntHandingOver: 'جارٍ التسليم…',
  cntHeldNotice: 'الحركات من وإلى {location} معلّقة ما دام هذا الجرد مفتوحًا.',
  cntAbandon: 'إلغاء هذا الجرد',
  cntAbandonReason: 'ما سبب الإلغاء؟',
  cntAbandonConfirm: 'ألغِ الجرد وارفع التعليق',
  cntCancel: 'إلغاء',
  cntVarianceTitle: 'الفروقات · {reference}',
  cntVarianceIntro:
    'الاعتماد يكتب قيد تسوية واحدًا في السجل لكل صنف تقرّر تسويته، ولا يمحو شيئًا: يبقى '
    + 'المتوقع والمعدود والفرق كما هي. أما الصنف المحدَّد لإعادة العدّ فلا يُكتب له شيء.',
  cntFigItems: 'الأصناف المعدودة',
  cntFigMatched: 'مطابق تمامًا',
  cntFigVariances: 'الفروقات',
  cntFigNet: 'صافي القطع',
  cntFigNotCounted: 'لم تُعدّ',
  cntShortOver: 'نقص {short}، زيادة {over}',
  cntNoAdjustment: 'لا حاجة لتسوية',
  cntPercentOfHolding: '{percent} مما عُدَّ',
  cntColDifference: 'الفرق',
  cntColReason: 'السبب المرجّح',
  cntColDecision: 'التسوية',
  cntReasonMatched: 'مطابق',
  cntReasonReceipt: 'استلام سابق غير مسجَّل',
  cntReasonLaundry: 'ربما في المغسلة',
  cntReasonCirculation: 'ربما ما زال في التداول',
  cntReasonIssued: 'صُرف دون تسجيل',
  cntReasonLoss: 'فقد في التداول',
  cntAdjNone: 'لا شيء يُكتب',
  cntAdjUp: 'تسوية بالزيادة',
  cntAdjWriteOff: 'شطب',
  cntAdjRecount: 'إعادة العدّ',
  cntWorkingLaundry: '{quantity} في المغسلة',
  cntWorkingCirculation: '{quantity} خارج المخزن',
  cntApprove: 'اعتماد التسويات',
  cntApproving: 'جارٍ الاعتماد…',
  cntSendBack: 'إعادة للعدّ',
  cntSendBackReason: 'ما الذي ينبغي التحقق منه مجددًا؟',
  cntSendingBack: 'جارٍ الإرجاع…',
  cntYouCounted:
    'أنت من قام بالعدّ، لذا يعتمده شخص آخر. عدّ النقص واعتماد الشطب قراران منفصلان عن قصد.',
  cntSubmittedBy: 'عدّه {name}، {time}',
  cntApprovedBy: 'اعتمده {name}، {time}',
  cntAbandonedBy: 'ألغاه {name}، {time}',
  cntReturnedNote: 'أُعيد للعدّ: {reason}',
  cntRound: 'الجولة {round}',

  /* ── W-120 و W-121 ──────────────────────────────────────────────────────── */
  usrTitle: 'المستخدمون',
  usrSubtitle: 'كل من يمكنه تسجيل الدخول، ومن لا يستطيع بعد.',
  usrWhoCanSignIn: 'من يمكنه تسجيل الدخول',
  usrIntro:
    'الدور هو ما يتيح لأي شخص تسجيل الدخول ويحدّد ما يمكنه فعله. من لا دور له لا يصل إلى '
    + 'أي شيء — ولهذا له تبويب خاص به بدلًا من صفّ واحد وسط ثلاثين.',
  usrNoPms:
    'لا يوجد نظام فندقي متصل في هذه المرحلة، لذا يُضاف الأشخاص هنا يدويًا. وعند ربط نظام، '
    + 'سيصل الأشخاص منه وتصبح أسماؤهم للقراءة فقط.',
  usrActive: 'نشط',
  usrNoRole: 'بلا دور',
  usrDisabled: 'معطَّل',
  usrAdd: 'إضافة شخص',
  usrAdding: 'جارٍ الإضافة…',
  usrAddTitle: 'إضافة شخص',
  usrEmployeeNo: 'الرقم الوظيفي',
  usrName: 'الاسم',
  usrNameAr: 'الاسم بالعربية',
  usrLanguage: 'اللغة',
  usrEnglish: 'الإنجليزية',
  usrArabic: 'العربية',
  usrCreate: 'إضافة',
  usrCancel: 'إلغاء',
  usrColPerson: 'الشخص',
  usrColEmployeeNo: 'الرقم الوظيفي',
  usrColRole: 'الدور',
  usrColProperty: 'الفندق',
  usrColLastSeen: 'آخر ظهور',
  usrColSource: 'المصدر',
  usrColState: 'الحالة',
  usrEmail: 'البريد الإلكتروني',
  usrSourceSynced: 'مُزامَن',
  usrSourceEnteredHere: 'أُضيف هنا',
  usrNeverSignedIn: 'لم يسجّل الدخول قط',
  usrNoneHere: 'لا أحد في هذه الحالة.',
  usrOpen: 'فتح',
  usrBack: 'كل المستخدمين',
  usrFromPms: 'من النظام الفندقي',
  usrFromPmsWhy:
    'للقراءة فقط عادةً — تُصحَّح من المصدر. لا يوجد نظام فندقي في هذه المرحلة، لذا أُدخلت '
    + 'هذه البيانات هنا ويمكن تصحيحها هنا.',
  usrOurs: 'ما يمكنه فعله هنا',
  usrOursWhy: 'هذا من إعدادنا. لا شيء يخصّ الصلاحيات يأتي من النظام الفندقي.',
  usrRole: 'الدور',
  usrProperty: 'الفندق',
  usrSave: 'حفظ',
  usrSaving: 'جارٍ الحفظ…',
  usrSaved: 'تم الحفظ.',
  usrRemoveRole: 'إزالة هذا الدور',
  usrSessions: 'الجلسات',
  usrSessionsWhy:
    'إنهاء جلسة يُخرج ذلك الجهاز عند أول طلب يرسله — وليس عند انتهاء صلاحية الجلسة.',
  usrNoSessions: 'لم يسجّل هذا الشخص الدخول قط.',
  usrColDevice: 'الجهاز',
  usrColSignedIn: 'سجّل الدخول',
  usrColExpires: 'تنتهي',
  usrColActivity: 'آخر نشاط',
  usrUnknownDevice: 'جهاز غير معروف',
  usrDeviceHandsetApp: 'جهاز التنظيف',
  usrDeviceAndroid: 'جهاز أندرويد',
  usrDeviceIphone: 'آيفون',
  usrDeviceIpad: 'آيباد',
  usrDeviceEdge: 'إيدج على ويندوز',
  usrDeviceFirefox: 'فايرفوكس',
  usrDeviceChrome: 'كروم',
  usrDeviceSafari: 'سفاري',
  usrThisDevice: 'هذا الجهاز',
  usrEnd: 'إنهاء',
  usrEndAll: 'إنهاء كل الجلسات',
  usrEnded: 'منتهية',
  usrEndedByAdmin: 'أنهاها مسؤول',
  usrEndedSignedOut: 'تسجيل خروج',
  usrEndedDisabled: 'انتهت عند تعطيل الحساب',
  usrEndedAll: 'انتهت مع البقية',
  usrActivity: 'النشاط',
  usrActivityWindow: 'آخر ٣٠ يومًا',
  usrTasksCompleted: 'الغرف المنجزة',
  usrInspectionsFailed: 'أُعيدت',
  usrRequestsFulfilled: 'الطلبات المنجزة',
  usrNoRate:
    'أعداد لا نِسَب. ما ينبغي أن تُقاس عليه نسبة مثل نسبة الإعادة ما زال سؤالًا لدى الفندق.',
  usrDisable: 'تعطيل هذا الشخص',
  usrEnable: 'تفعيل هذا الشخص',
  usrDisableReason: 'ما السبب؟',
  usrDisableConfirm: 'عطّله وأخرجه من الأجهزة',
  usrDisableWarning:
    'سيُخرَج من كل الأجهزة عند أول طلب يرسله، ولن يستطيع تسجيل الدخول حتى يفعّله أحد.',
  usrDisabledOn: 'عُطِّل في {when} — {reason}',
  usrCannotSignIn: 'لا يمكنه تسجيل الدخول حتى يُسنَد إليه دور.',
  /* ── W-122 · الأدوار والصلاحيات ─────────────────────────────────────────── */
  rolTitle: 'الأدوار والصلاحيات',
  rolSubtitle: 'كل خانة قرار',
  rolWhy:
    'الأدوار من عندنا لا من النظام الفندقي. لا شيء يخصّ الصلاحيات يعبر تلك الواجهة — '
    + 'النظام الفندقي يخبرنا من هو الشخص، وهذا الجدول يقرّر ما يحقّ له فعله. هذا الفصل هو '
    + 'ما يتيح للمسؤول أن يحلّ مشكلة صلاحية في الثامنة صباحًا دون انتظار أحد.',
  rolHalfMeans: 'المربّع نصف الممتلئ يعني «مسموح، مع تسجيل السبب».',
  rolAll: 'كل الصلاحيات',
  rolChangedOnly: 'ما غُيّر عن الإعداد',
  rolNothingChanged: 'لم يُغيَّر شيء عمّا أُعدّ عليه النظام.',
  rolColArea: 'المجال',
  rolColCapability: 'الصلاحية',
  rolSave: 'حفظ',
  rolSaving: 'جارٍ الحفظ…',
  rolSaved: 'تم الحفظ.',
  rolDiscard: 'تجاهل التغييرات',
  rolUnsaved: '{count} لم تُحفظ بعد',
  rolChangedFromDefault: '{count} تختلف عمّا أُعدّ عليه النظام',
  rolDefaultIs: 'الإعداد الأصلي: {grant}',
  rolDenied: 'غير مسموح',
  rolAllowed: 'مسموح',
  rolAllowedWithReason: 'مسموح، مع تسجيل السبب',
  rolEnforcedBy: 'يُتحقَّق منها في',
  rolNotEnforcedYet: 'لا شيء يتحقّق من هذه بعد.',
  rolAreaRooms: 'الغرف',
  rolAreaWork: 'العمل',
  rolAreaRequests: 'الطلبات',
  rolAreaFaults: 'الأعطال',
  rolAreaAreas: 'المناطق العامة',
  rolAreaInventory: 'المخزون',
  rolAreaReports: 'التقارير',
  rolAreaFiles: 'الصور',
  rolAreaAdministration: 'الإدارة',
  rolCapRoomViewReadiness: 'عرض لوحة الغرف وتفاصيلها',
  rolCapRoomSetOutOfService: 'إخراج غرفة من الخدمة للإصلاح',
  rolCapRoomOverrideToClean: 'اعتماد غرفة نظيفة دون تفتيش',
  rolCapRoomSyncFromPms: 'تحريك غرفة عند مغادرة النزيل',
  rolCapTaskStart: 'بدء تنظيف غرفة',
  rolCapTaskSubmit: 'إنهاء التنظيف وتصويره',
  rolCapTaskAssign: 'إسناد العمل وإعادة إسناده',
  rolCapTaskRaise: 'إضافة عمل وإنشاء تنظيفات اليوم',
  rolCapTaskInspect: 'التفتيش وإعادة العمل',
  rolCapTaskCancel: 'إلغاء عمل جارٍ',
  rolCapRosterEdit: 'تعديل جدول المناوبات',
  rolCapRequestRaise: 'تسجيل طلب نزيل',
  rolCapRequestFulfil: 'استلام طلب نزيل أو تسليمه أو رفضه',
  rolCapIssueRaise: 'الإبلاغ عن عطل وتصويره',
  rolCapFaultDowngradeBlocking: 'تخفيض عطل مانع',
  rolCapAreaClean: 'تسجيل تنظيف منطقة عامة',
  rolCapAreaSetOutOfService: 'إغلاق منطقة عامة',
  rolCapStockMove: 'تسجيل حركة مخزون وإجراء جرد',
  rolCapStockAdjust: 'تعديل المخزون واعتماد الجرد',
  rolCapAdminStockPars: 'تحديد ما يجب أن يحتفظ به كل مكان',
  rolCapLinenAdjust: 'تصحيح رقم المفروشات الذي سجّله غيرك',
  rolCapAdminGoLive: 'الاطّلاع على قائمة بيانات التشغيل',
  rolCapAdminConfiguration: 'تغيير اسم الفندق وأسماء الأدوار',
  rolCapAdminRequestCategories: 'تغيير ما يمكن للنزيل طلبه والمدة الموعودة لكل نوع',
  rolCapAdminFaultCategories: 'تغيير قائمة ما قد يكون معطَّلًا في الغرفة',
  rolCapReportAttendantProductivity: 'فتح تقرير إنتاجية العمّال',
  rolCapReportRoomsPerDay: 'فتح تقرير الغرف المنظّفة يوميًا',
  rolCapReportFaultsDowntime: 'فتح تقرير الأعطال وتوقّف الغرف',
  rolCapReportLoadThroughDay: 'فتح تقرير توزّع العمل على الساعات',
  rolCapReportConsumables: 'فتح تقرير المستهلكات',
  rolCapReportLinenLoss: 'فتح تقرير المفروشات المستخدمة والتالفة',
  rolCapReportRequestLateness: 'فتح تقرير طلبات النزلاء والتأخير',
  rolCapReportInspectionFailures: 'فتح تقرير إخفاقات التفتيش',
  rolCapFileRemove: 'إزالة صورة',
  rolCapAdminRooms: 'إعداد الغرف والمناطق',
  rolCapAdminLinenStandards: 'تحديد ما تحتاجه التنظيفة',
  rolCapAdminQrCodes: 'إصدار بطاقات الغرف وإلغاؤها',
  rolCapAdminChecklists: 'تعديل قوائم المهام',
  rolCapAdminUsers: 'إسناد الأدوار للأشخاص',
  rolCapAdminRoles: 'تغيير الأدوار والصلاحيات',
  rolCapAdminTranslations: 'تصحيح الأسماء العربية لكل ما سمّاه هذا النظام',
  rolCapAdminActivity: 'قراءة سجل النشاط',

  // ── W-124، سجل النشاط ───────────────────────────────────────────────────
  actTitle: 'سجل النشاط',
  actSubtitle: 'من فعل ماذا ومتى · يُحفظ {months} شهرًا',
  actTabAll: 'كل النشاط',
  actTabPermissions: 'تغييرات الصلاحيات',
  actTabOverrides: 'التجاوزات',
  actTabSignIns: 'محاولات دخول مرفوضة',
  actColWhen: 'الوقت',
  actColWho: 'الفاعل',
  actColWhat: 'الحدث',
  actColSubject: 'الموضوع',
  actColBefore: 'قبل',
  actColAfter: 'بعد',
  actColWhere: 'المصدر',
  actExport: 'تصدير',
  actExporting: 'جارٍ تحضير الملف…',
  actFrom: 'من',
  actTo: 'إلى',
  actRangeAll: 'كل المحفوظ',
  actRange7: 'آخر ٧ أيام',
  actRange30: 'آخر ٣٠ يومًا',
  actRange90: 'آخر ٩٠ يومًا',
  actOlder: 'عرض الأقدم',
  actLoading: 'جارٍ قراءة السجل…',
  actEmptyTitle: 'لا يوجد شيء مُسجَّل بعد',
  actEmptyBody:
    'يمتلئ هذا السجل عندما يتجاوز أحدهم حالة غرفة أو يغيّر صلاحية أو يفشل في الدخول. '
    + 'خلوّه في تركيب جديد أمر متوقع.',
  actEmptyFilteredBody:
    'لا شيء يطابق هذه التصفية وهذا المدى الزمني. جرّب تبويبًا آخر أو وسّع التواريخ.',
  actErrorTitle: 'تعذّرت قراءة السجل',
  actNoPermissionTitle: 'لا يمكنك الاطلاع على سجل النشاط',
  actNoPermissionBody:
    'يسجّل ما فعله الجميع، ولذلك يُمنح لعدد أقل من الأشخاص من الشاشات التي يرصدها. '
    + 'يمكن لمسؤول النظام منحه من شاشة الأدوار.',
  actRetentionNote:
    'لا تُعدَّل القيود أبدًا. تُحذف كاملةً بعد {months} شهرًا من وقوعها.',
  // ARABIC NAMES ONE AND TWO IN THE NOUN, so those two forms carry no {count} — the
  // placeholder guard is right to insist the English match.
  actEntries: {
    one: 'قيد واحد',
    two: 'قيدان',
    few: '{count} قيود',
    many: '{count} قيدًا',
    other: '{count} قيد',
  },
  actNothing: 'لا شيء',
  actReasonLabel: 'السبب',

  actActor: {
    SYSTEM: 'النظام',
    UNKNOWN: 'غير معروف',
  },

  actVerb: {
    PERMISSION_CHANGED: 'صلاحية',
    ROLE_ASSIGNED: 'مُنح دورًا',
    ROLE_REMOVED: 'سُحب الدور',
    SIGN_IN_REJECTED: 'رُفض الدخول',
    SIGN_IN_NO_ACCOUNT: 'دخول — لا حساب هنا',
    ROOM_OVERRIDDEN: 'تجاوز',
    TASK_CANCELLED: 'أُلغي العمل',
    FAULT_DOWNGRADED: 'أُغلق خلل مانع',
    STOCK_ADJUSTED: 'تسوية مخزون',
    PHOTOGRAPH_REMOVED: 'حُذفت صورة',
    USER_DISABLED: 'أُوقف الحساب',
    USER_ENABLED: 'أُعيد تشغيل الحساب',
    PERSON_ADDED: 'أُضيف شخص',
    SESSION_ENDED: 'أُنهيت الجلسة',
    ROOM_REMOVED_FROM_HOTEL: 'أُخرجت الغرفة من الفندق',
    ROOM_RESTORED: 'أُعيدت الغرفة',
    CLEANING_RULES_SET: 'قواعد التنظيف',
    CHECKLIST_PUBLISHED: 'نُشرت قائمة تحقق',
    LINEN_STANDARD_SET: 'تغيّر معيار المفروشات',
    ROOM_TAKEN_OUT_OF_SERVICE: 'سُحبت غرفة من البيع',
    LINEN_FIGURE_CORRECTED: 'صُحّح رقم المفروشات',
    HOTEL_DETAILS_SET: 'تغيّرت بيانات الفندق',
    INSPECTION_POLICY_SET: 'تغيّرت سياسة الفحص',
    ROLE_RENAMED: 'أُعيدت تسمية دور',
    FAULT_CATEGORY_ADDED: 'أُضيف نوع عطل',
    FAULT_CATEGORY_CHANGED: 'أُعيدت تسمية نوع عطل',
    FAULT_CATEGORY_RETIRED: 'أُطفئ نوع عطل',
    FAULT_CATEGORY_RESTORED: 'أُعيد تشغيل نوع عطل',
    FAULT_CATEGORY_REMOVED: 'حُذف نوع عطل',
    STOCK_PLACE_ADDED: 'أُضيف مكان للمفروشات',
    STOCK_PLACE_CHANGED: 'أُعيدت تسمية مكان للمفروشات',
    STOCK_PLACE_RETIRED: 'أُوقف مكان للمفروشات',
    STOCK_PLACE_RESTORED: 'أُعيد تشغيل مكان للمفروشات',
    STOCK_PLACE_REMOVED: 'حُذف مكان للمفروشات',
    STOCK_ITEM_ADDED: 'أُضيف صنف',
    STOCK_ITEM_CHANGED: 'تغيّر صنف',
    STOCK_ITEM_RETIRED: 'أُوقف صنف',
    STOCK_ITEM_RESTORED: 'أُعيد تشغيل صنف',
    STOCK_ITEM_REMOVED: 'حُذف صنف',
    SHIFT_ADDED: 'أُضيف دوام',
    SHIFT_CHANGED: 'تغيّرت أوقات دوام أو اسمه',
    SHIFT_RETIRED: 'أُوقف دوام',
    SHIFT_RESTORED: 'أُعيد تشغيل دوام',
    WORK_TRIGGER_CHANGED: 'تغيّر ما يُنشئ العمل',
    STOCK_ADJUSTMENT_PROPOSED: 'أُرسل تصحيح للاعتماد',
    STOCK_ADJUSTMENT_APPROVED: 'اعتُمد التصحيح',
    STOCK_ADJUSTMENT_DECLINED: 'رُفض التصحيح',
    REQUEST_CATEGORY_ADDED: 'أُضيف نوع طلب',
    REQUEST_CATEGORY_CHANGED: 'تغيّر نوع طلب',
    REQUEST_CATEGORY_RETIRED: 'أُوقف نوع طلب',
    REQUEST_CATEGORY_RESTORED: 'أُعيد تشغيل نوع طلب',
    REQUEST_CATEGORY_REMOVED: 'حُذف نوع طلب',
    AREA_TAKEN_OUT_OF_SERVICE: 'أُغلقت منطقة عامة أو أُعيد فتحها',
    TRANSLATION_CHANGED: 'صُحّح اسم عربي',
  },

  actSubject: {
    ROOM: 'غرفة',
    USER: 'شخص',
    ROLE: 'دور',
    SESSION: 'جلسة',
    SIGN_IN: 'تسجيل دخول',
    ISSUE: 'خلل',
    STOCK_ITEM: 'مادة مخزون',
    TEMPLATE: 'قائمة تحقق',
    TASK: 'مهمة',
    CHECKLIST_ANSWER: 'بند قائمة',
    FAULT_CATEGORY: 'نوع عطل',
    STOCK_PLACE: 'مكان للمفروشات',
    REQUEST_CATEGORY: 'نوع طلب',
    AREA: 'منطقة عامة',
    ROOM_TYPE: 'نوع غرفة',
    STOCK_LOCATION: 'موقع مخزون',
    TASK_TYPE: 'نوع عمل',
    SHIFT: 'وردية',
    WORK_TRIGGER: 'ما يُنشئ العمل',
    ZONE: 'منطقة',
    BUILDING: 'مبنى',
    PROPERTY: 'الفندق',
  },

  actSource: {
    WEB: 'الويب',
    MOBILE: 'الجهاز المحمول',
    RULE: 'قاعدة',
  },

  actValue: {
    ACTIVE: 'نشط',
    RETIRED: 'موقوف',
    DISABLED: 'موقوف',
    LIVE: 'قائمة',
    ENDED_BY_ADMIN: 'أنهاها مسؤول النظام',
    ALL_ENDED: 'أُنهيت كل الجلسات',
    USER_DISABLED: 'أُنهيت — أُوقف الحساب',
    IN_HOTEL: 'داخل الفندق',
    REMOVED: 'مُخرجة',
    IN_SERVICE: 'معروضة للبيع',
    OUT_OF_SERVICE: 'مسحوبة من البيع',
    OUT_OF_ORDER: 'خارج الخدمة',
    PRESENT: 'موجودة',
    REJECTED: 'مرفوض',
    NO_ACCOUNT: 'لا حساب هنا',
    EMPTY: 'لم يُكتب شيء',
    CLEARED_TEMPLATES: 'أُفرغت قوائم التحقق',
    UNKNOWN_DEVICE: 'جهاز غير معروف',
    ALL_DEVICES: 'كل الأجهزة',
    HANDSET_APP: 'جهاز التدبير المحمول',
    ANDROID: 'جهاز أندرويد',
    IPHONE: 'آيفون',
    IPAD: 'آيباد',
    EDGE: 'إيدج على ويندوز',
    FIREFOX: 'فَيَرفُكس',
    CHROME: 'كروم',
    SAFARI: 'سفاري',
    DENIED: 'غير مسموح',
    ALLOWED: 'مسموح',
    ALLOWED_WITH_REASON: 'مسموح، مع تسجيل سبب',
    DIRTY: 'متسخة',
    IN_PROGRESS: 'قيد التنظيف',
    CLEAN: 'نظيفة',
    INSPECTED: 'مفحوصة',
    INSPECTION_REQUIRED: 'الغرف تنتظر الفحص',
    INSPECTION_NOT_REQUIRED: 'الغرف جاهزة بقول عامل التنظيف',
    OPEN: 'مفتوح',
    ACKNOWLEDGED: 'مُستلم',
    RESOLVED: 'مُعالج',
    CANCELLED: 'مُلغى',
    RECEIPT: 'استلام',
    TRANSFER: 'نقل',
    ISSUE: 'صرف',
    RETURN: 'إرجاع',
    LOSS: 'فقد',
    ADJUSTMENT: 'تسوية',
    PROPOSED: 'بانتظار البتّ',
    APPROVED: 'معتمد',
    DECLINED: 'مرفوض',
    ON: 'مُشغَّل',
    OFF: 'موقوف',
    ROOM_ATTENDANT: 'عامل غرف',
    FLOOR_SUPERVISOR: 'مشرف طابق',
    HOUSEKEEPING_MANAGER: 'مدير التدبير الفندقي',
    STORE_KEEPER: 'أمين المخزن',
    FRONT_DESK: 'الاستقبال',
    ADMINISTRATOR: 'مسؤول النظام',
  },
}

export const STRINGS: Record<AppLocale, Dict> = { en, ar }
